import { ResponseContext } from '../http/http.js';
import { retry } from '../util.js';
async function requestWithRetries(requestContext, configuration) {
    if (configuration.retryNonIdempotent || requestContext.isIdempotent()) {
        return retry(() => requestWithMiddleware(requestContext, configuration), configuration.retries, [], configuration.retryMaxDelay);
    }
    return requestWithMiddleware(requestContext, configuration);
}
async function requestWithMiddleware(requestContext, configuration) {
    for (let middleware of configuration.middleware) {
        requestContext = await middleware.pre(requestContext);
    }
    let signal = requestContext.signal;
    if (configuration.signal && 'any' in AbortSignal) {
        signal = AbortSignal.any([configuration.signal, signal]);
    }
    const resp = await fetch(requestContext.getUrl(), {
        method: requestContext.getHttpMethod(),
        body: requestContext.getBody(),
        headers: requestContext.getHeaders(),
        credentials: "same-origin",
        signal: signal
    });
    let headers = {};
    resp.headers.forEach((value, key) => {
        headers[key] = value;
    });
    let bufferedBlob;
    let bufferedText;
    let bufferedJson;
    const readText = () => (bufferedText ??= bufferedBlob ? bufferedBlob.then((b) => b.text()) : resp.text());
    const readBlob = () => (bufferedBlob ??= bufferedText ? bufferedText.then((t) => new Blob([t])) : resp.blob());
    const readJson = () => (bufferedJson ??= readText().then((t) => JSON.parse(t)));
    let responseContext = new ResponseContext(resp.status, headers, {
        binary: () => readBlob(),
        text: () => readText(),
        json: () => readJson(),
    });
    for (let middleware of configuration.middleware) {
        responseContext = await middleware.post(responseContext, requestContext);
    }
    return responseContext;
}
import { AcceptMsaApiRequestFactory, AcceptMsaApiResponseProcessor } from "../apis/AcceptMsaApi.js";
export class PromiseAcceptMsaApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AcceptMsaApiRequestFactory(configuration), responseProcessor = new AcceptMsaApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async acceptMsaWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.acceptMsa(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.acceptMsaWithHttpInfo(responseContext);
    }
    async acceptMsa(_options) {
        const responseWithInfo = await this.acceptMsaWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { AccountsApiRequestFactory, AccountsApiResponseProcessor } from "../apis/AccountsApi.js";
export class PromiseAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AccountsApiRequestFactory(configuration), responseProcessor = new AccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async activateAtlasOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateAtlasOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateAtlasOrganizationWithHttpInfo(responseContext);
    }
    async activateAtlasOrganization(projectId, id, _options) {
        const responseWithInfo = await this.activateAtlasOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async activateAzureManagementGroupWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateAzureManagementGroup(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateAzureManagementGroupWithHttpInfo(responseContext);
    }
    async activateAzureManagementGroup(projectId, id, _options) {
        const responseWithInfo = await this.activateAzureManagementGroupWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async activateGcpFolderWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateGcpFolder(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateGcpFolderWithHttpInfo(responseContext);
    }
    async activateGcpFolder(projectId, id, _options) {
        const responseWithInfo = await this.activateGcpFolderWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async activateGcpOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateGcpOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateGcpOrganizationWithHttpInfo(responseContext);
    }
    async activateGcpOrganization(projectId, id, _options) {
        const responseWithInfo = await this.activateGcpOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async activateGcpRestoreFolderWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateGcpRestoreFolder(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateGcpRestoreFolderWithHttpInfo(responseContext);
    }
    async activateGcpRestoreFolder(projectId, id, _options) {
        const responseWithInfo = await this.activateGcpRestoreFolderWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async activateGcpRestoreOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.activateGcpRestoreOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.activateGcpRestoreOrganizationWithHttpInfo(responseContext);
    }
    async activateGcpRestoreOrganization(projectId, id, _options) {
        const responseWithInfo = await this.activateGcpRestoreOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async connectAtlasOrganizationWithHttpInfo(projectId, connectAtlasOrganizationRequest, _options) {
        let requestContextValue = await this.requestFactory.connectAtlasOrganization(projectId, connectAtlasOrganizationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAtlasOrganizationWithHttpInfo(responseContext);
    }
    async connectAtlasOrganization(projectId, connectAtlasOrganizationRequest, _options) {
        const responseWithInfo = await this.connectAtlasOrganizationWithHttpInfo(projectId, connectAtlasOrganizationRequest, _options);
        return responseWithInfo.data;
    }
    async connectCustomerVaultAccountWithHttpInfo(connectCustomerVaultAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.connectCustomerVaultAccount(connectCustomerVaultAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectCustomerVaultAccountWithHttpInfo(responseContext);
    }
    async connectCustomerVaultAccount(connectCustomerVaultAccountRequest, _options) {
        const responseWithInfo = await this.connectCustomerVaultAccountWithHttpInfo(connectCustomerVaultAccountRequest, _options);
        return responseWithInfo.data;
    }
    async connectGcpFolderWithHttpInfo(projectId, connectGcpFolderRequest, _options) {
        let requestContextValue = await this.requestFactory.connectGcpFolder(projectId, connectGcpFolderRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectGcpFolderWithHttpInfo(responseContext);
    }
    async connectGcpFolder(projectId, connectGcpFolderRequest, _options) {
        const responseWithInfo = await this.connectGcpFolderWithHttpInfo(projectId, connectGcpFolderRequest, _options);
        return responseWithInfo.data;
    }
    async connectGcpOrganizationWithHttpInfo(projectId, connectGcpOrganizationRequest, _options) {
        let requestContextValue = await this.requestFactory.connectGcpOrganization(projectId, connectGcpOrganizationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectGcpOrganizationWithHttpInfo(responseContext);
    }
    async connectGcpOrganization(projectId, connectGcpOrganizationRequest, _options) {
        const responseWithInfo = await this.connectGcpOrganizationWithHttpInfo(projectId, connectGcpOrganizationRequest, _options);
        return responseWithInfo.data;
    }
    async connectGcpRestoreFolderWithHttpInfo(projectId, connectGcpFolderRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.connectGcpRestoreFolder(projectId, connectGcpFolderRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectGcpRestoreFolderWithHttpInfo(responseContext);
    }
    async connectGcpRestoreFolder(projectId, connectGcpFolderRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.connectGcpRestoreFolderWithHttpInfo(projectId, connectGcpFolderRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async connectGcpRestoreOrganizationWithHttpInfo(projectId, connectGcpOrganizationRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.connectGcpRestoreOrganization(projectId, connectGcpOrganizationRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectGcpRestoreOrganizationWithHttpInfo(responseContext);
    }
    async connectGcpRestoreOrganization(projectId, connectGcpOrganizationRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.connectGcpRestoreOrganizationWithHttpInfo(projectId, connectGcpOrganizationRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async connectRestoreAccountWithHttpInfo(projectId, connectRestoreAccountRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.connectRestoreAccount(projectId, connectRestoreAccountRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectRestoreAccountWithHttpInfo(responseContext);
    }
    async connectRestoreAccount(projectId, connectRestoreAccountRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.connectRestoreAccountWithHttpInfo(projectId, connectRestoreAccountRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async connectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, connectRestoreAwsOrganizationalUnitRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.connectRestoreAwsOrganizationalUnit(projectId, connectRestoreAwsOrganizationalUnitRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectRestoreAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async connectRestoreAwsOrganizationalUnit(projectId, connectRestoreAwsOrganizationalUnitRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.connectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, connectRestoreAwsOrganizationalUnitRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async connectSourceAccountWithHttpInfo(projectId, connectSourceAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.connectSourceAccount(projectId, connectSourceAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectSourceAccountWithHttpInfo(responseContext);
    }
    async connectSourceAccount(projectId, connectSourceAccountRequest, _options) {
        const responseWithInfo = await this.connectSourceAccountWithHttpInfo(projectId, connectSourceAccountRequest, _options);
        return responseWithInfo.data;
    }
    async connectSourceAwsOrganizationalUnitWithHttpInfo(projectId, connectSourceAwsOrganizationalUnitRequest, _options) {
        let requestContextValue = await this.requestFactory.connectSourceAwsOrganizationalUnit(projectId, connectSourceAwsOrganizationalUnitRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectSourceAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async connectSourceAwsOrganizationalUnit(projectId, connectSourceAwsOrganizationalUnitRequest, _options) {
        const responseWithInfo = await this.connectSourceAwsOrganizationalUnitWithHttpInfo(projectId, connectSourceAwsOrganizationalUnitRequest, _options);
        return responseWithInfo.data;
    }
    async deactivateAtlasOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateAtlasOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateAtlasOrganizationWithHttpInfo(responseContext);
    }
    async deactivateAtlasOrganization(projectId, id, _options) {
        const responseWithInfo = await this.deactivateAtlasOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deactivateAzureManagementGroupWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateAzureManagementGroup(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateAzureManagementGroupWithHttpInfo(responseContext);
    }
    async deactivateAzureManagementGroup(projectId, id, _options) {
        const responseWithInfo = await this.deactivateAzureManagementGroupWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deactivateGcpFolderWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateGcpFolder(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateGcpFolderWithHttpInfo(responseContext);
    }
    async deactivateGcpFolder(projectId, id, _options) {
        const responseWithInfo = await this.deactivateGcpFolderWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deactivateGcpOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateGcpOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateGcpOrganizationWithHttpInfo(responseContext);
    }
    async deactivateGcpOrganization(projectId, id, _options) {
        const responseWithInfo = await this.deactivateGcpOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deactivateGcpRestoreFolderWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateGcpRestoreFolder(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateGcpRestoreFolderWithHttpInfo(responseContext);
    }
    async deactivateGcpRestoreFolder(projectId, id, _options) {
        const responseWithInfo = await this.deactivateGcpRestoreFolderWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deactivateGcpRestoreOrganizationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deactivateGcpRestoreOrganization(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateGcpRestoreOrganizationWithHttpInfo(responseContext);
    }
    async deactivateGcpRestoreOrganization(projectId, id, _options) {
        const responseWithInfo = await this.deactivateGcpRestoreOrganizationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async deleteRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.deleteRestoreAccountConnectivityConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteRestoreAccountConnectivityConfigWithHttpInfo(responseContext);
    }
    async deleteRestoreAccountConnectivityConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.deleteRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async deleteRestoreAccountV1WithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.deleteRestoreAccountV1(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteRestoreAccountV1WithHttpInfo(responseContext);
    }
    async deleteRestoreAccountV1(projectId, accountId, _options) {
        const responseWithInfo = await this.deleteRestoreAccountV1WithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async deleteSourceAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.deleteSourceAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteSourceAccountWithHttpInfo(responseContext);
    }
    async deleteSourceAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.deleteSourceAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async disableRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.disableRestoreAccountMetricsConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disableRestoreAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async disableRestoreAccountMetricsConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.disableRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async disableSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.disableSourceAccountMetricsConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disableSourceAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async disableSourceAccountMetricsConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.disableSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async disconnectCustomerVaultAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.disconnectCustomerVaultAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disconnectCustomerVaultAccountWithHttpInfo(responseContext);
    }
    async disconnectCustomerVaultAccount(accountId, _options) {
        const responseWithInfo = await this.disconnectCustomerVaultAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
    async disconnectRestoreAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.disconnectRestoreAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disconnectRestoreAccountWithHttpInfo(responseContext);
    }
    async disconnectRestoreAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.disconnectRestoreAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async disconnectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options) {
        let requestContextValue = await this.requestFactory.disconnectRestoreAwsOrganizationalUnit(projectId, organizationalUnitId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disconnectRestoreAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async disconnectRestoreAwsOrganizationalUnit(projectId, organizationalUnitId, _options) {
        const responseWithInfo = await this.disconnectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options);
        return responseWithInfo.data;
    }
    async disconnectSourceAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.disconnectSourceAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disconnectSourceAccountWithHttpInfo(responseContext);
    }
    async disconnectSourceAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.disconnectSourceAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async disconnectSourceAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options) {
        let requestContextValue = await this.requestFactory.disconnectSourceAwsOrganizationalUnit(projectId, organizationalUnitId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disconnectSourceAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async disconnectSourceAwsOrganizationalUnit(projectId, organizationalUnitId, _options) {
        const responseWithInfo = await this.disconnectSourceAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options);
        return responseWithInfo.data;
    }
    async enableRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, enableRestoreAccountMetricsConfigRequest, _options) {
        let requestContextValue = await this.requestFactory.enableRestoreAccountMetricsConfig(projectId, accountId, enableRestoreAccountMetricsConfigRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.enableRestoreAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async enableRestoreAccountMetricsConfig(projectId, accountId, enableRestoreAccountMetricsConfigRequest, _options) {
        const responseWithInfo = await this.enableRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, enableRestoreAccountMetricsConfigRequest, _options);
        return responseWithInfo.data;
    }
    async enableSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, enableSourceAccountMetricsConfigRequest, _options) {
        let requestContextValue = await this.requestFactory.enableSourceAccountMetricsConfig(projectId, accountId, enableSourceAccountMetricsConfigRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.enableSourceAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async enableSourceAccountMetricsConfig(projectId, accountId, enableSourceAccountMetricsConfigRequest, _options) {
        const responseWithInfo = await this.enableSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, enableSourceAccountMetricsConfigRequest, _options);
        return responseWithInfo.data;
    }
    async getAzureOnboardedTenantsWithHttpInfo(projectId, requireInTenantDataAccessApp, _options) {
        let requestContextValue = await this.requestFactory.getAzureOnboardedTenants(projectId, requireInTenantDataAccessApp, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureOnboardedTenantsWithHttpInfo(responseContext);
    }
    async getAzureOnboardedTenants(projectId, requireInTenantDataAccessApp, _options) {
        const responseWithInfo = await this.getAzureOnboardedTenantsWithHttpInfo(projectId, requireInTenantDataAccessApp, _options);
        return responseWithInfo.data;
    }
    async getGcsServiceAgentWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.getGcsServiceAgent(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGcsServiceAgentWithHttpInfo(responseContext);
    }
    async getGcsServiceAgent(accountId, _options) {
        const responseWithInfo = await this.getGcsServiceAgentWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
    async getGoogleWorkspaceConnectionInfoWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getGoogleWorkspaceConnectionInfo(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGoogleWorkspaceConnectionInfoWithHttpInfo(responseContext);
    }
    async getGoogleWorkspaceConnectionInfo(projectId, _options) {
        const responseWithInfo = await this.getGoogleWorkspaceConnectionInfoWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async getGoogleWorkspaceRestoreConnectionInfoWithHttpInfo(projectId, gcpProjectId, _options) {
        let requestContextValue = await this.requestFactory.getGoogleWorkspaceRestoreConnectionInfo(projectId, gcpProjectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGoogleWorkspaceRestoreConnectionInfoWithHttpInfo(responseContext);
    }
    async getGoogleWorkspaceRestoreConnectionInfo(projectId, gcpProjectId, _options) {
        const responseWithInfo = await this.getGoogleWorkspaceRestoreConnectionInfoWithHttpInfo(projectId, gcpProjectId, _options);
        return responseWithInfo.data;
    }
    async getMicrosoft365ConnectionInfoWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getMicrosoft365ConnectionInfo(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getMicrosoft365ConnectionInfoWithHttpInfo(responseContext);
    }
    async getMicrosoft365ConnectionInfo(projectId, _options) {
        const responseWithInfo = await this.getMicrosoft365ConnectionInfoWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async getMicrosoft365RestoreConnectionInfoWithHttpInfo(projectId, tenantId, _options) {
        let requestContextValue = await this.requestFactory.getMicrosoft365RestoreConnectionInfo(projectId, tenantId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getMicrosoft365RestoreConnectionInfoWithHttpInfo(responseContext);
    }
    async getMicrosoft365RestoreConnectionInfo(projectId, tenantId, _options) {
        const responseWithInfo = await this.getMicrosoft365RestoreConnectionInfoWithHttpInfo(projectId, tenantId, _options);
        return responseWithInfo.data;
    }
    async getRestoreAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreAccountWithHttpInfo(responseContext);
    }
    async getRestoreAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.getRestoreAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getRestoreAccountCanDeleteWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreAccountCanDelete(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreAccountCanDeleteWithHttpInfo(responseContext);
    }
    async getRestoreAccountCanDelete(projectId, accountId, _options) {
        const responseWithInfo = await this.getRestoreAccountCanDeleteWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreAccountConnectivityConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreAccountConnectivityConfigWithHttpInfo(responseContext);
    }
    async getRestoreAccountConnectivityConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.getRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreAccountMetricsConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async getRestoreAccountMetricsConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.getRestoreAccountMetricsConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getSourceAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getSourceAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSourceAccountWithHttpInfo(responseContext);
    }
    async getSourceAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.getSourceAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getSourceAccountCanDeleteWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getSourceAccountCanDelete(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSourceAccountCanDeleteWithHttpInfo(responseContext);
    }
    async getSourceAccountCanDelete(projectId, accountId, _options) {
        const responseWithInfo = await this.getSourceAccountCanDeleteWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async getSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getSourceAccountMetricsConfig(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSourceAccountMetricsConfigWithHttpInfo(responseContext);
    }
    async getSourceAccountMetricsConfig(projectId, accountId, _options) {
        const responseWithInfo = await this.getSourceAccountMetricsConfigWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async listAtlasOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listAtlasOrganizations(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAtlasOrganizationsWithHttpInfo(responseContext);
    }
    async listAtlasOrganizations(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listAtlasOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listAzureManagementGroupsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listAzureManagementGroups(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureManagementGroupsWithHttpInfo(responseContext);
    }
    async listAzureManagementGroups(projectId, _options) {
        const responseWithInfo = await this.listAzureManagementGroupsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async listEksClustersWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listEksClusters(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listEksClustersWithHttpInfo(responseContext);
    }
    async listEksClusters(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listEksClustersWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGcpFoldersWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGcpFolders(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpFoldersWithHttpInfo(responseContext);
    }
    async listGcpFolders(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGcpFoldersWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGcpOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGcpOrganizations(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpOrganizationsWithHttpInfo(responseContext);
    }
    async listGcpOrganizations(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGcpOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGcpRestoreFoldersWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGcpRestoreFolders(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpRestoreFoldersWithHttpInfo(responseContext);
    }
    async listGcpRestoreFolders(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGcpRestoreFoldersWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGcpRestoreOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGcpRestoreOrganizations(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpRestoreOrganizationsWithHttpInfo(responseContext);
    }
    async listGcpRestoreOrganizations(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGcpRestoreOrganizationsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listRestoreAccountsWithHttpInfo(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options) {
        let requestContextValue = await this.requestFactory.listRestoreAccounts(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreAccountsWithHttpInfo(responseContext);
    }
    async listRestoreAccounts(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options) {
        const responseWithInfo = await this.listRestoreAccountsWithHttpInfo(projectId, pageToken, pageSize, listRestoreAccountsRequest, _options);
        return responseWithInfo.data;
    }
    async listRestoreAwsOrganizationalUnitsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listRestoreAwsOrganizationalUnits(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreAwsOrganizationalUnitsWithHttpInfo(responseContext);
    }
    async listRestoreAwsOrganizationalUnits(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listRestoreAwsOrganizationalUnitsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listSourceAccountsWithHttpInfo(projectId, pageToken, pageSize, listSourceAccountsRequest, _options) {
        let requestContextValue = await this.requestFactory.listSourceAccounts(projectId, pageToken, pageSize, listSourceAccountsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSourceAccountsWithHttpInfo(responseContext);
    }
    async listSourceAccounts(projectId, pageToken, pageSize, listSourceAccountsRequest, _options) {
        const responseWithInfo = await this.listSourceAccountsWithHttpInfo(projectId, pageToken, pageSize, listSourceAccountsRequest, _options);
        return responseWithInfo.data;
    }
    async listSourceAwsOrganizationalUnitsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listSourceAwsOrganizationalUnits(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSourceAwsOrganizationalUnitsWithHttpInfo(responseContext);
    }
    async listSourceAwsOrganizationalUnits(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listSourceAwsOrganizationalUnitsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async reconnectCustomerVaultAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.reconnectCustomerVaultAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reconnectCustomerVaultAccountWithHttpInfo(responseContext);
    }
    async reconnectCustomerVaultAccount(accountId, _options) {
        const responseWithInfo = await this.reconnectCustomerVaultAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
    async reconnectRestoreAccountWithHttpInfo(projectId, accountId, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.reconnectRestoreAccount(projectId, accountId, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reconnectRestoreAccountWithHttpInfo(responseContext);
    }
    async reconnectRestoreAccount(projectId, accountId, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.reconnectRestoreAccountWithHttpInfo(projectId, accountId, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async reconnectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options) {
        let requestContextValue = await this.requestFactory.reconnectRestoreAwsOrganizationalUnit(projectId, organizationalUnitId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reconnectRestoreAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async reconnectRestoreAwsOrganizationalUnit(projectId, organizationalUnitId, _options) {
        const responseWithInfo = await this.reconnectRestoreAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options);
        return responseWithInfo.data;
    }
    async reconnectSourceAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.reconnectSourceAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reconnectSourceAccountWithHttpInfo(responseContext);
    }
    async reconnectSourceAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.reconnectSourceAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async reconnectSourceAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options) {
        let requestContextValue = await this.requestFactory.reconnectSourceAwsOrganizationalUnit(projectId, organizationalUnitId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reconnectSourceAwsOrganizationalUnitWithHttpInfo(responseContext);
    }
    async reconnectSourceAwsOrganizationalUnit(projectId, organizationalUnitId, _options) {
        const responseWithInfo = await this.reconnectSourceAwsOrganizationalUnitWithHttpInfo(projectId, organizationalUnitId, _options);
        return responseWithInfo.data;
    }
    async syncRestoreAccountPermissionsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.syncRestoreAccountPermissions(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.syncRestoreAccountPermissionsWithHttpInfo(responseContext);
    }
    async syncRestoreAccountPermissions(projectId, accountId, _options) {
        const responseWithInfo = await this.syncRestoreAccountPermissionsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async syncSourceAccountPermissionsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.syncSourceAccountPermissions(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.syncSourceAccountPermissionsWithHttpInfo(responseContext);
    }
    async syncSourceAccountPermissions(projectId, accountId, _options) {
        const responseWithInfo = await this.syncSourceAccountPermissionsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
    async updateRestoreAccountWithHttpInfo(projectId, accountId, updateRestoreAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.updateRestoreAccount(projectId, accountId, updateRestoreAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRestoreAccountWithHttpInfo(responseContext);
    }
    async updateRestoreAccount(projectId, accountId, updateRestoreAccountRequest, _options) {
        const responseWithInfo = await this.updateRestoreAccountWithHttpInfo(projectId, accountId, updateRestoreAccountRequest, _options);
        return responseWithInfo.data;
    }
    async updateRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, updateRestoreAccountConnectivityConfigRequest, _options) {
        let requestContextValue = await this.requestFactory.updateRestoreAccountConnectivityConfig(projectId, accountId, updateRestoreAccountConnectivityConfigRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRestoreAccountConnectivityConfigWithHttpInfo(responseContext);
    }
    async updateRestoreAccountConnectivityConfig(projectId, accountId, updateRestoreAccountConnectivityConfigRequest, _options) {
        const responseWithInfo = await this.updateRestoreAccountConnectivityConfigWithHttpInfo(projectId, accountId, updateRestoreAccountConnectivityConfigRequest, _options);
        return responseWithInfo.data;
    }
    async updateSourceAccountWithHttpInfo(projectId, accountId, updateSourceAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.updateSourceAccount(projectId, accountId, updateSourceAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateSourceAccountWithHttpInfo(responseContext);
    }
    async updateSourceAccount(projectId, accountId, updateSourceAccountRequest, _options) {
        const responseWithInfo = await this.updateSourceAccountWithHttpInfo(projectId, accountId, updateSourceAccountRequest, _options);
        return responseWithInfo.data;
    }
}
import { ActionApprovalsApiRequestFactory, ActionApprovalsApiResponseProcessor } from "../apis/ActionApprovalsApi.js";
export class PromiseActionApprovalsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ActionApprovalsApiRequestFactory(configuration), responseProcessor = new ActionApprovalsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async cancelActionApprovalRequestWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.cancelActionApprovalRequest(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelActionApprovalRequestWithHttpInfo(responseContext);
    }
    async cancelActionApprovalRequest(projectId, requestId, _options) {
        const responseWithInfo = await this.cancelActionApprovalRequestWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
    async createActionApprovalPolicyWithHttpInfo(projectId, createMPAPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.createActionApprovalPolicy(projectId, createMPAPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createActionApprovalPolicyWithHttpInfo(responseContext);
    }
    async createActionApprovalPolicy(projectId, createMPAPolicyRequest, _options) {
        const responseWithInfo = await this.createActionApprovalPolicyWithHttpInfo(projectId, createMPAPolicyRequest, _options);
        return responseWithInfo.data;
    }
    async createActionApprovalRequestWithHttpInfo(projectId, requestId, submitMyMPARequestRequest, _options) {
        let requestContextValue = await this.requestFactory.createActionApprovalRequest(projectId, requestId, submitMyMPARequestRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createActionApprovalRequestWithHttpInfo(responseContext);
    }
    async createActionApprovalRequest(projectId, requestId, submitMyMPARequestRequest, _options) {
        const responseWithInfo = await this.createActionApprovalRequestWithHttpInfo(projectId, requestId, submitMyMPARequestRequest, _options);
        return responseWithInfo.data;
    }
    async createActionApprovalRequestReviewWithHttpInfo(projectId, requestId, createMPARequestReviewRequest, _options) {
        let requestContextValue = await this.requestFactory.createActionApprovalRequestReview(projectId, requestId, createMPARequestReviewRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createActionApprovalRequestReviewWithHttpInfo(responseContext);
    }
    async createActionApprovalRequestReview(projectId, requestId, createMPARequestReviewRequest, _options) {
        const responseWithInfo = await this.createActionApprovalRequestReviewWithHttpInfo(projectId, requestId, createMPARequestReviewRequest, _options);
        return responseWithInfo.data;
    }
    async deleteActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, _options) {
        let requestContextValue = await this.requestFactory.deleteActionApprovalPolicy(projectId, mpaPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteActionApprovalPolicyWithHttpInfo(responseContext);
    }
    async deleteActionApprovalPolicy(projectId, mpaPolicyId, _options) {
        const responseWithInfo = await this.deleteActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, _options);
        return responseWithInfo.data;
    }
    async getActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, _options) {
        let requestContextValue = await this.requestFactory.getActionApprovalPolicy(projectId, mpaPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getActionApprovalPolicyWithHttpInfo(responseContext);
    }
    async getActionApprovalPolicy(projectId, mpaPolicyId, _options) {
        const responseWithInfo = await this.getActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, _options);
        return responseWithInfo.data;
    }
    async getActionApprovalRequestWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.getActionApprovalRequest(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getActionApprovalRequestWithHttpInfo(responseContext);
    }
    async getActionApprovalRequest(projectId, requestId, _options) {
        const responseWithInfo = await this.getActionApprovalRequestWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
    async getActionApprovalSummaryWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getActionApprovalSummary(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getActionApprovalSummaryWithHttpInfo(responseContext);
    }
    async getActionApprovalSummary(projectId, _options) {
        const responseWithInfo = await this.getActionApprovalSummaryWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async getMyActionApprovalRequestWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.getMyActionApprovalRequest(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getMyActionApprovalRequestWithHttpInfo(responseContext);
    }
    async getMyActionApprovalRequest(projectId, requestId, _options) {
        const responseWithInfo = await this.getMyActionApprovalRequestWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
    async listActionApprovalOperationsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listActionApprovalOperations(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listActionApprovalOperationsWithHttpInfo(responseContext);
    }
    async listActionApprovalOperations(_options) {
        const responseWithInfo = await this.listActionApprovalOperationsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
    async listActionApprovalPoliciesWithHttpInfo(projectId, operation, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listActionApprovalPolicies(projectId, operation, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listActionApprovalPoliciesWithHttpInfo(responseContext);
    }
    async listActionApprovalPolicies(projectId, operation, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listActionApprovalPoliciesWithHttpInfo(projectId, operation, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listActionApprovalRequestsWithHttpInfo(projectId, pageToken, pageSize, listMPARequestsRequest, _options) {
        let requestContextValue = await this.requestFactory.listActionApprovalRequests(projectId, pageToken, pageSize, listMPARequestsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listActionApprovalRequestsWithHttpInfo(responseContext);
    }
    async listActionApprovalRequests(projectId, pageToken, pageSize, listMPARequestsRequest, _options) {
        const responseWithInfo = await this.listActionApprovalRequestsWithHttpInfo(projectId, pageToken, pageSize, listMPARequestsRequest, _options);
        return responseWithInfo.data;
    }
    async listMyActionApprovalRequestsWithHttpInfo(projectId, pageToken, pageSize, listMyMPARequestsRequest, _options) {
        let requestContextValue = await this.requestFactory.listMyActionApprovalRequests(projectId, pageToken, pageSize, listMyMPARequestsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMyActionApprovalRequestsWithHttpInfo(responseContext);
    }
    async listMyActionApprovalRequests(projectId, pageToken, pageSize, listMyMPARequestsRequest, _options) {
        const responseWithInfo = await this.listMyActionApprovalRequestsWithHttpInfo(projectId, pageToken, pageSize, listMyMPARequestsRequest, _options);
        return responseWithInfo.data;
    }
    async updateActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, updateMPAPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.updateActionApprovalPolicy(projectId, mpaPolicyId, updateMPAPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateActionApprovalPolicyWithHttpInfo(responseContext);
    }
    async updateActionApprovalPolicy(projectId, mpaPolicyId, updateMPAPolicyRequest, _options) {
        const responseWithInfo = await this.updateActionApprovalPolicyWithHttpInfo(projectId, mpaPolicyId, updateMPAPolicyRequest, _options);
        return responseWithInfo.data;
    }
}
import { AddRestoreAtlasPrivateLinkApiRequestFactory, AddRestoreAtlasPrivateLinkApiResponseProcessor } from "../apis/AddRestoreAtlasPrivateLinkApi.js";
export class PromiseAddRestoreAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AddRestoreAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new AddRestoreAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async addRestoreAtlasPrivateLinkWithHttpInfo(projectId, accountId, addRestoreAtlasPrivateLinkRequest, _options) {
        let requestContextValue = await this.requestFactory.addRestoreAtlasPrivateLink(projectId, accountId, addRestoreAtlasPrivateLinkRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.addRestoreAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async addRestoreAtlasPrivateLink(projectId, accountId, addRestoreAtlasPrivateLinkRequest, _options) {
        const responseWithInfo = await this.addRestoreAtlasPrivateLinkWithHttpInfo(projectId, accountId, addRestoreAtlasPrivateLinkRequest, _options);
        return responseWithInfo.data;
    }
}
import { AiAgentApiRequestFactory, AiAgentApiResponseProcessor } from "../apis/AiAgentApi.js";
export class PromiseAiAgentApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AiAgentApiRequestFactory(configuration), responseProcessor = new AiAgentApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createAIAgentSessionWithHttpInfo(projectId, createSessionRequest, _options) {
        let requestContextValue = await this.requestFactory.createAIAgentSession(projectId, createSessionRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createAIAgentSessionWithHttpInfo(responseContext);
    }
    async createAIAgentSession(projectId, createSessionRequest, _options) {
        const responseWithInfo = await this.createAIAgentSessionWithHttpInfo(projectId, createSessionRequest, _options);
        return responseWithInfo.data;
    }
    async deleteAIAgentSessionWithHttpInfo(projectId, sessionId, _options) {
        let requestContextValue = await this.requestFactory.deleteAIAgentSession(projectId, sessionId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteAIAgentSessionWithHttpInfo(responseContext);
    }
    async deleteAIAgentSession(projectId, sessionId, _options) {
        const responseWithInfo = await this.deleteAIAgentSessionWithHttpInfo(projectId, sessionId, _options);
        return responseWithInfo.data;
    }
    async getAIAgentCardWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getAIAgentCard(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAIAgentCardWithHttpInfo(responseContext);
    }
    async getAIAgentCard(_options) {
        const responseWithInfo = await this.getAIAgentCardWithHttpInfo(_options);
        return responseWithInfo.data;
    }
    async getAIAgentSessionWithHttpInfo(projectId, sessionId, _options) {
        let requestContextValue = await this.requestFactory.getAIAgentSession(projectId, sessionId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAIAgentSessionWithHttpInfo(responseContext);
    }
    async getAIAgentSession(projectId, sessionId, _options) {
        const responseWithInfo = await this.getAIAgentSessionWithHttpInfo(projectId, sessionId, _options);
        return responseWithInfo.data;
    }
    async listAIAgentSessionsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listAIAgentSessions(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAIAgentSessionsWithHttpInfo(responseContext);
    }
    async listAIAgentSessions(projectId, _options) {
        const responseWithInfo = await this.listAIAgentSessionsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async searchResourcesWithHttpInfo(projectId, searchResourcesRequest, _options) {
        let requestContextValue = await this.requestFactory.searchResources(projectId, searchResourcesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchResourcesWithHttpInfo(responseContext);
    }
    async searchResources(projectId, searchResourcesRequest, _options) {
        const responseWithInfo = await this.searchResourcesWithHttpInfo(projectId, searchResourcesRequest, _options);
        return responseWithInfo.data;
    }
    async sendA2AMessageWithHttpInfo(sendA2AMessageRequest, _options) {
        let requestContextValue = await this.requestFactory.sendA2AMessage(sendA2AMessageRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.sendA2AMessageWithHttpInfo(responseContext);
    }
    async sendA2AMessage(sendA2AMessageRequest, _options) {
        const responseWithInfo = await this.sendA2AMessageWithHttpInfo(sendA2AMessageRequest, _options);
        return responseWithInfo.data;
    }
    async sendMessageToAIWithHttpInfo(projectId, sendMessageToAIRequest, _options) {
        let requestContextValue = await this.requestFactory.sendMessageToAI(projectId, sendMessageToAIRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.sendMessageToAIWithHttpInfo(responseContext);
    }
    async sendMessageToAI(projectId, sendMessageToAIRequest, _options) {
        const responseWithInfo = await this.sendMessageToAIWithHttpInfo(projectId, sendMessageToAIRequest, _options);
        return responseWithInfo.data;
    }
    async updateAIAgentSessionWithHttpInfo(projectId, sessionId, updateSessionRequest, _options) {
        let requestContextValue = await this.requestFactory.updateAIAgentSession(projectId, sessionId, updateSessionRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateAIAgentSessionWithHttpInfo(responseContext);
    }
    async updateAIAgentSession(projectId, sessionId, updateSessionRequest, _options) {
        const responseWithInfo = await this.updateAIAgentSessionWithHttpInfo(projectId, sessionId, updateSessionRequest, _options);
        return responseWithInfo.data;
    }
}
import { AmazonRegisterApiRequestFactory, AmazonRegisterApiResponseProcessor } from "../apis/AmazonRegisterApi.js";
export class PromiseAmazonRegisterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AmazonRegisterApiRequestFactory(configuration), responseProcessor = new AmazonRegisterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async amazonRegisterWithHttpInfo(xAmznMarketplaceToken, _options) {
        let requestContextValue = await this.requestFactory.amazonRegister(xAmznMarketplaceToken, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.amazonRegisterWithHttpInfo(responseContext);
    }
    async amazonRegister(xAmznMarketplaceToken, _options) {
        const responseWithInfo = await this.amazonRegisterWithHttpInfo(xAmznMarketplaceToken, _options);
        return responseWithInfo.data;
    }
    async amazonRegisterGetWithHttpInfo(xAmznMarketplaceToken, _options) {
        let requestContextValue = await this.requestFactory.amazonRegisterGet(xAmznMarketplaceToken, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.amazonRegisterGetWithHttpInfo(responseContext);
    }
    async amazonRegisterGet(xAmznMarketplaceToken, _options) {
        const responseWithInfo = await this.amazonRegisterGetWithHttpInfo(xAmznMarketplaceToken, _options);
        return responseWithInfo.data;
    }
}
import { AuditLogsApiRequestFactory, AuditLogsApiResponseProcessor } from "../apis/AuditLogsApi.js";
export class PromiseAuditLogsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuditLogsApiRequestFactory(configuration), responseProcessor = new AuditLogsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async auditLogsDownloadWithHttpInfo(filter, columns, _options) {
        let requestContextValue = await this.requestFactory.auditLogsDownload(filter, columns, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.auditLogsDownloadWithHttpInfo(responseContext);
    }
    async auditLogsDownload(filter, columns, _options) {
        const responseWithInfo = await this.auditLogsDownloadWithHttpInfo(filter, columns, _options);
        return responseWithInfo.data;
    }
    async listAuditLogsWithHttpInfo(pageToken, pageSize, listAuditLogRequest, _options) {
        let requestContextValue = await this.requestFactory.listAuditLogs(pageToken, pageSize, listAuditLogRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAuditLogsWithHttpInfo(responseContext);
    }
    async listAuditLogs(pageToken, pageSize, listAuditLogRequest, _options) {
        const responseWithInfo = await this.listAuditLogsWithHttpInfo(pageToken, pageSize, listAuditLogRequest, _options);
        return responseWithInfo.data;
    }
}
import { AuditLogsPopulateApiRequestFactory, AuditLogsPopulateApiResponseProcessor } from "../apis/AuditLogsPopulateApi.js";
export class PromiseAuditLogsPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuditLogsPopulateApiRequestFactory(configuration), responseProcessor = new AuditLogsPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async auditLogsPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.auditLogsPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.auditLogsPopulateWithHttpInfo(responseContext);
    }
    async auditLogsPopulate(projectId, version, _options) {
        const responseWithInfo = await this.auditLogsPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { AuthApiRequestFactory, AuthApiResponseProcessor } from "../apis/AuthApi.js";
export class PromiseAuthApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthApiRequestFactory(configuration), responseProcessor = new AuthApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAccessTokenWithHttpInfo(apiCredentials, _options) {
        let requestContextValue = await this.requestFactory.getAccessToken(apiCredentials, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAccessTokenWithHttpInfo(responseContext);
    }
    async getAccessToken(apiCredentials, _options) {
        const responseWithInfo = await this.getAccessTokenWithHttpInfo(apiCredentials, _options);
        return responseWithInfo.data;
    }
    async getAccessTokenOAuth2WithHttpInfo(clientId, clientSecret, grantType, _options) {
        let requestContextValue = await this.requestFactory.getAccessTokenOAuth2(clientId, clientSecret, grantType, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAccessTokenOAuth2WithHttpInfo(responseContext);
    }
    async getAccessTokenOAuth2(clientId, clientSecret, grantType, _options) {
        const responseWithInfo = await this.getAccessTokenOAuth2WithHttpInfo(clientId, clientSecret, grantType, _options);
        return responseWithInfo.data;
    }
    async listIdentityProvidersNamesWithHttpInfo(domain, _options) {
        let requestContextValue = await this.requestFactory.listIdentityProvidersNames(domain, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listIdentityProvidersNamesWithHttpInfo(responseContext);
    }
    async listIdentityProvidersNames(domain, _options) {
        const responseWithInfo = await this.listIdentityProvidersNamesWithHttpInfo(domain, _options);
        return responseWithInfo.data;
    }
    async rotateApiClientSecretWithHttpInfo(clientId, _options) {
        let requestContextValue = await this.requestFactory.rotateApiClientSecret(clientId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.rotateApiClientSecretWithHttpInfo(responseContext);
    }
    async rotateApiClientSecret(clientId, _options) {
        const responseWithInfo = await this.rotateApiClientSecretWithHttpInfo(clientId, _options);
        return responseWithInfo.data;
    }
    async rotateCurrentApiClientSecretWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.rotateCurrentApiClientSecret(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.rotateCurrentApiClientSecretWithHttpInfo(responseContext);
    }
    async rotateCurrentApiClientSecret(_options) {
        const responseWithInfo = await this.rotateCurrentApiClientSecretWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { AuthV2AssociateSoftwareTokenApiRequestFactory, AuthV2AssociateSoftwareTokenApiResponseProcessor } from "../apis/AuthV2AssociateSoftwareTokenApi.js";
export class PromiseAuthV2AssociateSoftwareTokenApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2AssociateSoftwareTokenApiRequestFactory(configuration), responseProcessor = new AuthV2AssociateSoftwareTokenApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2AssociateSoftwareTokenWithHttpInfo(authV2AssociateSoftwareTokenRequest, _options) {
        let requestContextValue = await this.requestFactory.authV2AssociateSoftwareToken(authV2AssociateSoftwareTokenRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2AssociateSoftwareTokenWithHttpInfo(responseContext);
    }
    async authV2AssociateSoftwareToken(authV2AssociateSoftwareTokenRequest, _options) {
        const responseWithInfo = await this.authV2AssociateSoftwareTokenWithHttpInfo(authV2AssociateSoftwareTokenRequest, _options);
        return responseWithInfo.data;
    }
}
import { AuthV2ConfirmForgotPasswordApiRequestFactory, AuthV2ConfirmForgotPasswordApiResponseProcessor } from "../apis/AuthV2ConfirmForgotPasswordApi.js";
export class PromiseAuthV2ConfirmForgotPasswordApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2ConfirmForgotPasswordApiRequestFactory(configuration), responseProcessor = new AuthV2ConfirmForgotPasswordApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2ConfirmForgotPasswordWithHttpInfo(authV2ConfirmForgotPasswordRequest, _options) {
        let requestContextValue = await this.requestFactory.authV2ConfirmForgotPassword(authV2ConfirmForgotPasswordRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2ConfirmForgotPasswordWithHttpInfo(responseContext);
    }
    async authV2ConfirmForgotPassword(authV2ConfirmForgotPasswordRequest, _options) {
        const responseWithInfo = await this.authV2ConfirmForgotPasswordWithHttpInfo(authV2ConfirmForgotPasswordRequest, _options);
        return responseWithInfo.data;
    }
}
import { AuthV2ForgotPasswordApiRequestFactory, AuthV2ForgotPasswordApiResponseProcessor } from "../apis/AuthV2ForgotPasswordApi.js";
export class PromiseAuthV2ForgotPasswordApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2ForgotPasswordApiRequestFactory(configuration), responseProcessor = new AuthV2ForgotPasswordApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2ForgotPasswordWithHttpInfo(authV2ForgotPasswordRequest, _options) {
        let requestContextValue = await this.requestFactory.authV2ForgotPassword(authV2ForgotPasswordRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2ForgotPasswordWithHttpInfo(responseContext);
    }
    async authV2ForgotPassword(authV2ForgotPasswordRequest, _options) {
        const responseWithInfo = await this.authV2ForgotPasswordWithHttpInfo(authV2ForgotPasswordRequest, _options);
        return responseWithInfo.data;
    }
}
import { AuthV2LoginApiRequestFactory, AuthV2LoginApiResponseProcessor } from "../apis/AuthV2LoginApi.js";
export class PromiseAuthV2LoginApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2LoginApiRequestFactory(configuration), responseProcessor = new AuthV2LoginApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2LoginWithHttpInfo(authV2LoginRequest, domain, _options) {
        let requestContextValue = await this.requestFactory.authV2Login(authV2LoginRequest, domain, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2LoginWithHttpInfo(responseContext);
    }
    async authV2Login(authV2LoginRequest, domain, _options) {
        const responseWithInfo = await this.authV2LoginWithHttpInfo(authV2LoginRequest, domain, _options);
        return responseWithInfo.data;
    }
}
import { AuthV2RespondChallengeApiRequestFactory, AuthV2RespondChallengeApiResponseProcessor } from "../apis/AuthV2RespondChallengeApi.js";
export class PromiseAuthV2RespondChallengeApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2RespondChallengeApiRequestFactory(configuration), responseProcessor = new AuthV2RespondChallengeApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2RespondChallengeWithHttpInfo(authV2RespondChallengeRequest, domain, _options) {
        let requestContextValue = await this.requestFactory.authV2RespondChallenge(authV2RespondChallengeRequest, domain, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2RespondChallengeWithHttpInfo(responseContext);
    }
    async authV2RespondChallenge(authV2RespondChallengeRequest, domain, _options) {
        const responseWithInfo = await this.authV2RespondChallengeWithHttpInfo(authV2RespondChallengeRequest, domain, _options);
        return responseWithInfo.data;
    }
}
import { AuthV2VerifySoftwareTokenApiRequestFactory, AuthV2VerifySoftwareTokenApiResponseProcessor } from "../apis/AuthV2VerifySoftwareTokenApi.js";
export class PromiseAuthV2VerifySoftwareTokenApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AuthV2VerifySoftwareTokenApiRequestFactory(configuration), responseProcessor = new AuthV2VerifySoftwareTokenApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async authV2VerifySoftwareTokenWithHttpInfo(authV2VerifySoftwareTokenRequest, _options) {
        let requestContextValue = await this.requestFactory.authV2VerifySoftwareToken(authV2VerifySoftwareTokenRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.authV2VerifySoftwareTokenWithHttpInfo(responseContext);
    }
    async authV2VerifySoftwareToken(authV2VerifySoftwareTokenRequest, _options) {
        const responseWithInfo = await this.authV2VerifySoftwareTokenWithHttpInfo(authV2VerifySoftwareTokenRequest, _options);
        return responseWithInfo.data;
    }
}
import { AzureBYOAApiRequestFactory, AzureBYOAApiResponseProcessor } from "../apis/AzureBYOAApi.js";
export class PromiseAzureBYOAApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AzureBYOAApiRequestFactory(configuration), responseProcessor = new AzureBYOAApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectBYOAppsWithHttpInfo(projectId, connectBYOAppsRequest, _options) {
        let requestContextValue = await this.requestFactory.connectBYOApps(projectId, connectBYOAppsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectBYOAppsWithHttpInfo(responseContext);
    }
    async connectBYOApps(projectId, connectBYOAppsRequest, _options) {
        const responseWithInfo = await this.connectBYOAppsWithHttpInfo(projectId, connectBYOAppsRequest, _options);
        return responseWithInfo.data;
    }
}
import { AzureMarketplaceApiRequestFactory, AzureMarketplaceApiResponseProcessor } from "../apis/AzureMarketplaceApi.js";
export class PromiseAzureMarketplaceApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AzureMarketplaceApiRequestFactory(configuration), responseProcessor = new AzureMarketplaceApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async handleAzureMarketplaceWebhookWithHttpInfo(azureMarketplaceWebhookInput, _options) {
        let requestContextValue = await this.requestFactory.handleAzureMarketplaceWebhook(azureMarketplaceWebhookInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.handleAzureMarketplaceWebhookWithHttpInfo(responseContext);
    }
    async handleAzureMarketplaceWebhook(azureMarketplaceWebhookInput, _options) {
        const responseWithInfo = await this.handleAzureMarketplaceWebhookWithHttpInfo(azureMarketplaceWebhookInput, _options);
        return responseWithInfo.data;
    }
}
import { AzureOAuthCallbackApiRequestFactory, AzureOAuthCallbackApiResponseProcessor } from "../apis/AzureOAuthCallbackApi.js";
export class PromiseAzureOAuthCallbackApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AzureOAuthCallbackApiRequestFactory(configuration), responseProcessor = new AzureOAuthCallbackApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async azureOAuthCallbackWithHttpInfo(code, state, _options) {
        let requestContextValue = await this.requestFactory.azureOAuthCallback(code, state, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.azureOAuthCallbackWithHttpInfo(responseContext);
    }
    async azureOAuthCallback(code, state, _options) {
        const responseWithInfo = await this.azureOAuthCallbackWithHttpInfo(code, state, _options);
        return responseWithInfo.data;
    }
}
import { AzureRegisterApiRequestFactory, AzureRegisterApiResponseProcessor } from "../apis/AzureRegisterApi.js";
export class PromiseAzureRegisterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AzureRegisterApiRequestFactory(configuration), responseProcessor = new AzureRegisterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async azureRegisterWithHttpInfo(token, _options) {
        let requestContextValue = await this.requestFactory.azureRegister(token, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.azureRegisterWithHttpInfo(responseContext);
    }
    async azureRegister(token, _options) {
        const responseWithInfo = await this.azureRegisterWithHttpInfo(token, _options);
        return responseWithInfo.data;
    }
}
import { AzureScanningAccountSetupStatusApiRequestFactory, AzureScanningAccountSetupStatusApiResponseProcessor } from "../apis/AzureScanningAccountSetupStatusApi.js";
export class PromiseAzureScanningAccountSetupStatusApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new AzureScanningAccountSetupStatusApiRequestFactory(configuration), responseProcessor = new AzureScanningAccountSetupStatusApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async azureScanningAccountSetupStatusWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.azureScanningAccountSetupStatus(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.azureScanningAccountSetupStatusWithHttpInfo(responseContext);
    }
    async azureScanningAccountSetupStatus(projectId, _options) {
        const responseWithInfo = await this.azureScanningAccountSetupStatusWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { BackupJobsPopulateApiRequestFactory, BackupJobsPopulateApiResponseProcessor } from "../apis/BackupJobsPopulateApi.js";
export class PromiseBackupJobsPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BackupJobsPopulateApiRequestFactory(configuration), responseProcessor = new BackupJobsPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async backupJobsPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.backupJobsPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.backupJobsPopulateWithHttpInfo(responseContext);
    }
    async backupJobsPopulate(projectId, version, _options) {
        const responseWithInfo = await this.backupJobsPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { BackupPoliciesApiRequestFactory, BackupPoliciesApiResponseProcessor } from "../apis/BackupPoliciesApi.js";
export class PromiseBackupPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BackupPoliciesApiRequestFactory(configuration), responseProcessor = new BackupPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createBackupPolicyWithHttpInfo(projectId, createBackupPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.createBackupPolicy(projectId, createBackupPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createBackupPolicyWithHttpInfo(responseContext);
    }
    async createBackupPolicy(projectId, createBackupPolicyRequest, _options) {
        const responseWithInfo = await this.createBackupPolicyWithHttpInfo(projectId, createBackupPolicyRequest, _options);
        return responseWithInfo.data;
    }
    async deleteBackupPolicyWithHttpInfo(projectId, backupPolicyId, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.deleteBackupPolicy(projectId, backupPolicyId, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteBackupPolicyWithHttpInfo(responseContext);
    }
    async deleteBackupPolicy(projectId, backupPolicyId, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.deleteBackupPolicyWithHttpInfo(projectId, backupPolicyId, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async getBackupPolicyWithHttpInfo(projectId, backupPolicyId, _options) {
        let requestContextValue = await this.requestFactory.getBackupPolicy(projectId, backupPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupPolicyWithHttpInfo(responseContext);
    }
    async getBackupPolicy(projectId, backupPolicyId, _options) {
        const responseWithInfo = await this.getBackupPolicyWithHttpInfo(projectId, backupPolicyId, _options);
        return responseWithInfo.data;
    }
    async listBackupPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listBackupPolicies(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupPoliciesWithHttpInfo(responseContext);
    }
    async listBackupPolicies(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listBackupPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGoogleWorkspacesWithPolicyStatusWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listGoogleWorkspacesWithPolicyStatus(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGoogleWorkspacesWithPolicyStatusWithHttpInfo(responseContext);
    }
    async listGoogleWorkspacesWithPolicyStatus(projectId, _options) {
        const responseWithInfo = await this.listGoogleWorkspacesWithPolicyStatusWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async updateBackupPolicyWithHttpInfo(projectId, backupPolicyId, updateBackupPolicyRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.updateBackupPolicy(projectId, backupPolicyId, updateBackupPolicyRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateBackupPolicyWithHttpInfo(responseContext);
    }
    async updateBackupPolicy(projectId, backupPolicyId, updateBackupPolicyRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.updateBackupPolicyWithHttpInfo(projectId, backupPolicyId, updateBackupPolicyRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { BackupZeroEtlPoliciesApiRequestFactory, BackupZeroEtlPoliciesApiResponseProcessor } from "../apis/BackupZeroEtlPoliciesApi.js";
export class PromiseBackupZeroEtlPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BackupZeroEtlPoliciesApiRequestFactory(configuration), responseProcessor = new BackupZeroEtlPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createBackupZeroEtlPolicyWithHttpInfo(projectId, createBackupZeroEtlPolicyInput, _options) {
        let requestContextValue = await this.requestFactory.createBackupZeroEtlPolicy(projectId, createBackupZeroEtlPolicyInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createBackupZeroEtlPolicyWithHttpInfo(responseContext);
    }
    async createBackupZeroEtlPolicy(projectId, createBackupZeroEtlPolicyInput, _options) {
        const responseWithInfo = await this.createBackupZeroEtlPolicyWithHttpInfo(projectId, createBackupZeroEtlPolicyInput, _options);
        return responseWithInfo.data;
    }
    async deleteBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, _options) {
        let requestContextValue = await this.requestFactory.deleteBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteBackupZeroEtlPolicyWithHttpInfo(responseContext);
    }
    async deleteBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, _options) {
        const responseWithInfo = await this.deleteBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, _options);
        return responseWithInfo.data;
    }
    async getBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, _options) {
        let requestContextValue = await this.requestFactory.getBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupZeroEtlPolicyWithHttpInfo(responseContext);
    }
    async getBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, _options) {
        const responseWithInfo = await this.getBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, _options);
        return responseWithInfo.data;
    }
    async listBackupZeroEtlPoliciesWithHttpInfo(projectId, scope, _options) {
        let requestContextValue = await this.requestFactory.listBackupZeroEtlPolicies(projectId, scope, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupZeroEtlPoliciesWithHttpInfo(responseContext);
    }
    async listBackupZeroEtlPolicies(projectId, scope, _options) {
        const responseWithInfo = await this.listBackupZeroEtlPoliciesWithHttpInfo(projectId, scope, _options);
        return responseWithInfo.data;
    }
    async updateBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, updateBackupZeroEtlPolicyInput, _options) {
        let requestContextValue = await this.requestFactory.updateBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, updateBackupZeroEtlPolicyInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateBackupZeroEtlPolicyWithHttpInfo(responseContext);
    }
    async updateBackupZeroEtlPolicy(projectId, zeroEtlPolicyId, updateBackupZeroEtlPolicyInput, _options) {
        const responseWithInfo = await this.updateBackupZeroEtlPolicyWithHttpInfo(projectId, zeroEtlPolicyId, updateBackupZeroEtlPolicyInput, _options);
        return responseWithInfo.data;
    }
}
import { BackupsApiRequestFactory, BackupsApiResponseProcessor } from "../apis/BackupsApi.js";
export class PromiseBackupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BackupsApiRequestFactory(configuration), responseProcessor = new BackupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async takeLlgSnapshotWithHttpInfo(projectId, id, takeSnapshotRequest, _options) {
        let requestContextValue = await this.requestFactory.takeLlgSnapshot(projectId, id, takeSnapshotRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.takeLlgSnapshotWithHttpInfo(responseContext);
    }
    async takeLlgSnapshot(projectId, id, takeSnapshotRequest, _options) {
        const responseWithInfo = await this.takeLlgSnapshotWithHttpInfo(projectId, id, takeSnapshotRequest, _options);
        return responseWithInfo.data;
    }
    async takeNativeSnapshotWithHttpInfo(projectId, id, takeNativeSnapshotRequest, _options) {
        let requestContextValue = await this.requestFactory.takeNativeSnapshot(projectId, id, takeNativeSnapshotRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.takeNativeSnapshotWithHttpInfo(responseContext);
    }
    async takeNativeSnapshot(projectId, id, takeNativeSnapshotRequest, _options) {
        const responseWithInfo = await this.takeNativeSnapshotWithHttpInfo(projectId, id, takeNativeSnapshotRequest, _options);
        return responseWithInfo.data;
    }
    async takeSnapshotWithHttpInfo(projectId, id, takeSnapshotRequest, _options) {
        let requestContextValue = await this.requestFactory.takeSnapshot(projectId, id, takeSnapshotRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.takeSnapshotWithHttpInfo(responseContext);
    }
    async takeSnapshot(projectId, id, takeSnapshotRequest, _options) {
        const responseWithInfo = await this.takeSnapshotWithHttpInfo(projectId, id, takeSnapshotRequest, _options);
        return responseWithInfo.data;
    }
}
import { BatchValidateResourceConfigurationApiRequestFactory, BatchValidateResourceConfigurationApiResponseProcessor } from "../apis/BatchValidateResourceConfigurationApi.js";
export class PromiseBatchValidateResourceConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BatchValidateResourceConfigurationApiRequestFactory(configuration), responseProcessor = new BatchValidateResourceConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async batchValidateResourceConfigurationWithHttpInfo(projectId, batchValidateResourceConfigurationRequest, _options) {
        let requestContextValue = await this.requestFactory.batchValidateResourceConfiguration(projectId, batchValidateResourceConfigurationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.batchValidateResourceConfigurationWithHttpInfo(responseContext);
    }
    async batchValidateResourceConfiguration(projectId, batchValidateResourceConfigurationRequest, _options) {
        const responseWithInfo = await this.batchValidateResourceConfigurationWithHttpInfo(projectId, batchValidateResourceConfigurationRequest, _options);
        return responseWithInfo.data;
    }
}
import { BillingApiRequestFactory, BillingApiResponseProcessor } from "../apis/BillingApi.js";
export class PromiseBillingApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BillingApiRequestFactory(configuration), responseProcessor = new BillingApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async exportCostDataWithHttpInfo(exportCostDataRequest, _options) {
        let requestContextValue = await this.requestFactory.exportCostData(exportCostDataRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.exportCostDataWithHttpInfo(responseContext);
    }
    async exportCostData(exportCostDataRequest, _options) {
        const responseWithInfo = await this.exportCostDataWithHttpInfo(exportCostDataRequest, _options);
        return responseWithInfo.data;
    }
    async getAgreementsWithHttpInfo(costUnit, _options) {
        let requestContextValue = await this.requestFactory.getAgreements(costUnit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAgreementsWithHttpInfo(responseContext);
    }
    async getAgreements(costUnit, _options) {
        const responseWithInfo = await this.getAgreementsWithHttpInfo(costUnit, _options);
        return responseWithInfo.data;
    }
    async getBillingPreviewWithHttpInfo(billingProvider, costUnit, _options) {
        let requestContextValue = await this.requestFactory.getBillingPreview(billingProvider, costUnit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBillingPreviewWithHttpInfo(responseContext);
    }
    async getBillingPreview(billingProvider, costUnit, _options) {
        const responseWithInfo = await this.getBillingPreviewWithHttpInfo(billingProvider, costUnit, _options);
        return responseWithInfo.data;
    }
    async getBillingTransactionsWithPreviewWithHttpInfo(startTime, endTime, billingProvider, costUnit, _options) {
        let requestContextValue = await this.requestFactory.getBillingTransactionsWithPreview(startTime, endTime, billingProvider, costUnit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBillingTransactionsWithPreviewWithHttpInfo(responseContext);
    }
    async getBillingTransactionsWithPreview(startTime, endTime, billingProvider, costUnit, _options) {
        const responseWithInfo = await this.getBillingTransactionsWithPreviewWithHttpInfo(startTime, endTime, billingProvider, costUnit, _options);
        return responseWithInfo.data;
    }
    async listBillingTransactionsWithHttpInfo(listBillingTransactionHistoryRequest, _options) {
        let requestContextValue = await this.requestFactory.listBillingTransactions(listBillingTransactionHistoryRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBillingTransactionsWithHttpInfo(responseContext);
    }
    async listBillingTransactions(listBillingTransactionHistoryRequest, _options) {
        const responseWithInfo = await this.listBillingTransactionsWithHttpInfo(listBillingTransactionHistoryRequest, _options);
        return responseWithInfo.data;
    }
    async queryCostDataWithHttpInfo(queryCostDataRequest, pageSize, pageToken, _options) {
        let requestContextValue = await this.requestFactory.queryCostData(queryCostDataRequest, pageSize, pageToken, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.queryCostDataWithHttpInfo(responseContext);
    }
    async queryCostData(queryCostDataRequest, pageSize, pageToken, _options) {
        const responseWithInfo = await this.queryCostDataWithHttpInfo(queryCostDataRequest, pageSize, pageToken, _options);
        return responseWithInfo.data;
    }
}
import { BulkRestoreApiRequestFactory, BulkRestoreApiResponseProcessor } from "../apis/BulkRestoreApi.js";
export class PromiseBulkRestoreApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new BulkRestoreApiRequestFactory(configuration), responseProcessor = new BulkRestoreApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async bulkRestoreWithHttpInfo(projectId, bulkRestoreRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.bulkRestore(projectId, bulkRestoreRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.bulkRestoreWithHttpInfo(responseContext);
    }
    async bulkRestore(projectId, bulkRestoreRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.bulkRestoreWithHttpInfo(projectId, bulkRestoreRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async bulkRestoreValidateWithHttpInfo(projectId, bulkRestoreValidateRequest, _options) {
        let requestContextValue = await this.requestFactory.bulkRestoreValidate(projectId, bulkRestoreValidateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.bulkRestoreValidateWithHttpInfo(responseContext);
    }
    async bulkRestoreValidate(projectId, bulkRestoreValidateRequest, _options) {
        const responseWithInfo = await this.bulkRestoreValidateWithHttpInfo(projectId, bulkRestoreValidateRequest, _options);
        return responseWithInfo.data;
    }
    async bulkSelectSnapshotsForResourcesWithHttpInfo(projectId, bulkSelectSnapshotsForResourcesRequest, _options) {
        let requestContextValue = await this.requestFactory.bulkSelectSnapshotsForResources(projectId, bulkSelectSnapshotsForResourcesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.bulkSelectSnapshotsForResourcesWithHttpInfo(responseContext);
    }
    async bulkSelectSnapshotsForResources(projectId, bulkSelectSnapshotsForResourcesRequest, _options) {
        const responseWithInfo = await this.bulkSelectSnapshotsForResourcesWithHttpInfo(projectId, bulkSelectSnapshotsForResourcesRequest, _options);
        return responseWithInfo.data;
    }
    async getBulkRestoreSnapshotHistogramWithHttpInfo(projectId, getBulkRestoreSnapshotHistogramRequest, _options) {
        let requestContextValue = await this.requestFactory.getBulkRestoreSnapshotHistogram(projectId, getBulkRestoreSnapshotHistogramRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBulkRestoreSnapshotHistogramWithHttpInfo(responseContext);
    }
    async getBulkRestoreSnapshotHistogram(projectId, getBulkRestoreSnapshotHistogramRequest, _options) {
        const responseWithInfo = await this.getBulkRestoreSnapshotHistogramWithHttpInfo(projectId, getBulkRestoreSnapshotHistogramRequest, _options);
        return responseWithInfo.data;
    }
}
import { CallbackApiRequestFactory, CallbackApiResponseProcessor } from "../apis/CallbackApi.js";
export class PromiseCallbackApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CallbackApiRequestFactory(configuration), responseProcessor = new CallbackApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async callbackWithHttpInfo(code, _options) {
        let requestContextValue = await this.requestFactory.callback(code, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.callbackWithHttpInfo(responseContext);
    }
    async callback(code, _options) {
        const responseWithInfo = await this.callbackWithHttpInfo(code, _options);
        return responseWithInfo.data;
    }
}
import { CancelWorkflowApiRequestFactory, CancelWorkflowApiResponseProcessor } from "../apis/CancelWorkflowApi.js";
export class PromiseCancelWorkflowApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CancelWorkflowApiRequestFactory(configuration), responseProcessor = new CancelWorkflowApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async cancelWorkflowWithHttpInfo(projectId, workflowId, _options) {
        let requestContextValue = await this.requestFactory.cancelWorkflow(projectId, workflowId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelWorkflowWithHttpInfo(responseContext);
    }
    async cancelWorkflow(projectId, workflowId, _options) {
        const responseWithInfo = await this.cancelWorkflowWithHttpInfo(projectId, workflowId, _options);
        return responseWithInfo.data;
    }
}
import { CheckAzureVMNameExistsApiRequestFactory, CheckAzureVMNameExistsApiResponseProcessor } from "../apis/CheckAzureVMNameExistsApi.js";
export class PromiseCheckAzureVMNameExistsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CheckAzureVMNameExistsApiRequestFactory(configuration), responseProcessor = new CheckAzureVMNameExistsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async checkAzureVMNameExistsWithHttpInfo(projectId, checkAzureVMNameExistsRequest, _options) {
        let requestContextValue = await this.requestFactory.checkAzureVMNameExists(projectId, checkAzureVMNameExistsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.checkAzureVMNameExistsWithHttpInfo(responseContext);
    }
    async checkAzureVMNameExists(projectId, checkAzureVMNameExistsRequest, _options) {
        const responseWithInfo = await this.checkAzureVMNameExistsWithHttpInfo(projectId, checkAzureVMNameExistsRequest, _options);
        return responseWithInfo.data;
    }
}
import { CheckCdcPrerequisitesApiRequestFactory, CheckCdcPrerequisitesApiResponseProcessor } from "../apis/CheckCdcPrerequisitesApi.js";
export class PromiseCheckCdcPrerequisitesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CheckCdcPrerequisitesApiRequestFactory(configuration), responseProcessor = new CheckCdcPrerequisitesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async checkCdcPrerequisitesWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.checkCdcPrerequisites(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.checkCdcPrerequisitesWithHttpInfo(responseContext);
    }
    async checkCdcPrerequisites(projectId, id, _options) {
        const responseWithInfo = await this.checkCdcPrerequisitesWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ClassificationPoliciesApiRequestFactory, ClassificationPoliciesApiResponseProcessor } from "../apis/ClassificationPoliciesApi.js";
export class PromiseClassificationPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ClassificationPoliciesApiRequestFactory(configuration), responseProcessor = new ClassificationPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createClassificationPolicyWithHttpInfo(projectId, createClassificationPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.createClassificationPolicy(projectId, createClassificationPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createClassificationPolicyWithHttpInfo(responseContext);
    }
    async createClassificationPolicy(projectId, createClassificationPolicyRequest, _options) {
        const responseWithInfo = await this.createClassificationPolicyWithHttpInfo(projectId, createClassificationPolicyRequest, _options);
        return responseWithInfo.data;
    }
    async deleteClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, _options) {
        let requestContextValue = await this.requestFactory.deleteClassificationPolicy(projectId, classificationPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteClassificationPolicyWithHttpInfo(responseContext);
    }
    async deleteClassificationPolicy(projectId, classificationPolicyId, _options) {
        const responseWithInfo = await this.deleteClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, _options);
        return responseWithInfo.data;
    }
    async getClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, _options) {
        let requestContextValue = await this.requestFactory.getClassificationPolicy(projectId, classificationPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getClassificationPolicyWithHttpInfo(responseContext);
    }
    async getClassificationPolicy(projectId, classificationPolicyId, _options) {
        const responseWithInfo = await this.getClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, _options);
        return responseWithInfo.data;
    }
    async listClassificationPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listClassificationPolicies(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listClassificationPoliciesWithHttpInfo(responseContext);
    }
    async listClassificationPolicies(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listClassificationPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async updateClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, updateClassificationPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.updateClassificationPolicy(projectId, classificationPolicyId, updateClassificationPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateClassificationPolicyWithHttpInfo(responseContext);
    }
    async updateClassificationPolicy(projectId, classificationPolicyId, updateClassificationPolicyRequest, _options) {
        const responseWithInfo = await this.updateClassificationPolicyWithHttpInfo(projectId, classificationPolicyId, updateClassificationPolicyRequest, _options);
        return responseWithInfo.data;
    }
}
import { ComplianceApiRequestFactory, ComplianceApiResponseProcessor } from "../apis/ComplianceApi.js";
export class PromiseComplianceApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ComplianceApiRequestFactory(configuration), responseProcessor = new ComplianceApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async exportComplianceReportWithHttpInfo(projectId, exportComplianceReportRequest, filter, _options) {
        let requestContextValue = await this.requestFactory.exportComplianceReport(projectId, exportComplianceReportRequest, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.exportComplianceReportWithHttpInfo(responseContext);
    }
    async exportComplianceReport(projectId, exportComplianceReportRequest, filter, _options) {
        const responseWithInfo = await this.exportComplianceReportWithHttpInfo(projectId, exportComplianceReportRequest, filter, _options);
        return responseWithInfo.data;
    }
    async getComplianceControlTrendsWithHttpInfo(projectId, filter, days, _options) {
        let requestContextValue = await this.requestFactory.getComplianceControlTrends(projectId, filter, days, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getComplianceControlTrendsWithHttpInfo(responseContext);
    }
    async getComplianceControlTrends(projectId, filter, days, _options) {
        const responseWithInfo = await this.getComplianceControlTrendsWithHttpInfo(projectId, filter, days, _options);
        return responseWithInfo.data;
    }
    async getComplianceSummaryWithHttpInfo(projectId, filter, asOfDate, _options) {
        let requestContextValue = await this.requestFactory.getComplianceSummary(projectId, filter, asOfDate, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getComplianceSummaryWithHttpInfo(responseContext);
    }
    async getComplianceSummary(projectId, filter, asOfDate, _options) {
        const responseWithInfo = await this.getComplianceSummaryWithHttpInfo(projectId, filter, asOfDate, _options);
        return responseWithInfo.data;
    }
    async getComplianceTrendWithHttpInfo(projectId, filter, days, control, _options) {
        let requestContextValue = await this.requestFactory.getComplianceTrend(projectId, filter, days, control, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getComplianceTrendWithHttpInfo(responseContext);
    }
    async getComplianceTrend(projectId, filter, days, control, _options) {
        const responseWithInfo = await this.getComplianceTrendWithHttpInfo(projectId, filter, days, control, _options);
        return responseWithInfo.data;
    }
    async listComplianceResourcesWithHttpInfo(projectId, pageIndex, pageSize, filter, failingControlId, failingGroupBy, failingGroupValue, _options) {
        let requestContextValue = await this.requestFactory.listComplianceResources(projectId, pageIndex, pageSize, filter, failingControlId, failingGroupBy, failingGroupValue, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listComplianceResourcesWithHttpInfo(responseContext);
    }
    async listComplianceResources(projectId, pageIndex, pageSize, filter, failingControlId, failingGroupBy, failingGroupValue, _options) {
        const responseWithInfo = await this.listComplianceResourcesWithHttpInfo(projectId, pageIndex, pageSize, filter, failingControlId, failingGroupBy, failingGroupValue, _options);
        return responseWithInfo.data;
    }
}
import { ConnectAzureBackupAccountApiRequestFactory, ConnectAzureBackupAccountApiResponseProcessor } from "../apis/ConnectAzureBackupAccountApi.js";
export class PromiseConnectAzureBackupAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectAzureBackupAccountApiRequestFactory(configuration), responseProcessor = new ConnectAzureBackupAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectAzureBackupAccountWithHttpInfo(projectId, connectAzureBackupAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.connectAzureBackupAccount(projectId, connectAzureBackupAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAzureBackupAccountWithHttpInfo(responseContext);
    }
    async connectAzureBackupAccount(projectId, connectAzureBackupAccountRequest, _options) {
        const responseWithInfo = await this.connectAzureBackupAccountWithHttpInfo(projectId, connectAzureBackupAccountRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConnectAzureBackupApplicationsApiRequestFactory, ConnectAzureBackupApplicationsApiResponseProcessor } from "../apis/ConnectAzureBackupApplicationsApi.js";
export class PromiseConnectAzureBackupApplicationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectAzureBackupApplicationsApiRequestFactory(configuration), responseProcessor = new ConnectAzureBackupApplicationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectAzureBackupApplicationsWithHttpInfo(projectId, connectAzureBackupApplicationsRequest, _options) {
        let requestContextValue = await this.requestFactory.connectAzureBackupApplications(projectId, connectAzureBackupApplicationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAzureBackupApplicationsWithHttpInfo(responseContext);
    }
    async connectAzureBackupApplications(projectId, connectAzureBackupApplicationsRequest, _options) {
        const responseWithInfo = await this.connectAzureBackupApplicationsWithHttpInfo(projectId, connectAzureBackupApplicationsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConnectAzureRestoreAccountApiRequestFactory, ConnectAzureRestoreAccountApiResponseProcessor } from "../apis/ConnectAzureRestoreAccountApi.js";
export class PromiseConnectAzureRestoreAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectAzureRestoreAccountApiRequestFactory(configuration), responseProcessor = new ConnectAzureRestoreAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectAzureRestoreAccountWithHttpInfo(projectId, connectAzureRestoreAccountRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.connectAzureRestoreAccount(projectId, connectAzureRestoreAccountRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAzureRestoreAccountWithHttpInfo(responseContext);
    }
    async connectAzureRestoreAccount(projectId, connectAzureRestoreAccountRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.connectAzureRestoreAccountWithHttpInfo(projectId, connectAzureRestoreAccountRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { ConnectAzureSourceAccountApiRequestFactory, ConnectAzureSourceAccountApiResponseProcessor } from "../apis/ConnectAzureSourceAccountApi.js";
export class PromiseConnectAzureSourceAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectAzureSourceAccountApiRequestFactory(configuration), responseProcessor = new ConnectAzureSourceAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectAzureSourceAccountWithHttpInfo(projectId, connectAzureSourceAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.connectAzureSourceAccount(projectId, connectAzureSourceAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAzureSourceAccountWithHttpInfo(responseContext);
    }
    async connectAzureSourceAccount(projectId, connectAzureSourceAccountRequest, _options) {
        const responseWithInfo = await this.connectAzureSourceAccountWithHttpInfo(projectId, connectAzureSourceAccountRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConnectAzureVaultAccountApiRequestFactory, ConnectAzureVaultAccountApiResponseProcessor } from "../apis/ConnectAzureVaultAccountApi.js";
export class PromiseConnectAzureVaultAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectAzureVaultAccountApiRequestFactory(configuration), responseProcessor = new ConnectAzureVaultAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectAzureVaultAccountWithHttpInfo(projectId, connectAzureVaultAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.connectAzureVaultAccount(projectId, connectAzureVaultAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectAzureVaultAccountWithHttpInfo(responseContext);
    }
    async connectAzureVaultAccount(projectId, connectAzureVaultAccountRequest, _options) {
        const responseWithInfo = await this.connectAzureVaultAccountWithHttpInfo(projectId, connectAzureVaultAccountRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConnectBYOAzureBackupApplicationsApiRequestFactory, ConnectBYOAzureBackupApplicationsApiResponseProcessor } from "../apis/ConnectBYOAzureBackupApplicationsApi.js";
export class PromiseConnectBYOAzureBackupApplicationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConnectBYOAzureBackupApplicationsApiRequestFactory(configuration), responseProcessor = new ConnectBYOAzureBackupApplicationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async connectBYOAzureBackupApplicationsWithHttpInfo(projectId, connectBYOAzureBackupApplicationsRequest, _options) {
        let requestContextValue = await this.requestFactory.connectBYOAzureBackupApplications(projectId, connectBYOAzureBackupApplicationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.connectBYOAzureBackupApplicationsWithHttpInfo(responseContext);
    }
    async connectBYOAzureBackupApplications(projectId, connectBYOAzureBackupApplicationsRequest, _options) {
        const responseWithInfo = await this.connectBYOAzureBackupApplicationsWithHttpInfo(projectId, connectBYOAzureBackupApplicationsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConvertOtherSnapshotsOnDemandApiRequestFactory, ConvertOtherSnapshotsOnDemandApiResponseProcessor } from "../apis/ConvertOtherSnapshotsOnDemandApi.js";
export class PromiseConvertOtherSnapshotsOnDemandApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConvertOtherSnapshotsOnDemandApiRequestFactory(configuration), responseProcessor = new ConvertOtherSnapshotsOnDemandApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async convertOtherSnapshotsOnDemandWithHttpInfo(projectId, id, convertOtherSnapshotsOnDemandRequest, _options) {
        let requestContextValue = await this.requestFactory.convertOtherSnapshotsOnDemand(projectId, id, convertOtherSnapshotsOnDemandRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.convertOtherSnapshotsOnDemandWithHttpInfo(responseContext);
    }
    async convertOtherSnapshotsOnDemand(projectId, id, convertOtherSnapshotsOnDemandRequest, _options) {
        const responseWithInfo = await this.convertOtherSnapshotsOnDemandWithHttpInfo(projectId, id, convertOtherSnapshotsOnDemandRequest, _options);
        return responseWithInfo.data;
    }
}
import { ConvertResourceSnapshotsApiRequestFactory, ConvertResourceSnapshotsApiResponseProcessor } from "../apis/ConvertResourceSnapshotsApi.js";
export class PromiseConvertResourceSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ConvertResourceSnapshotsApiRequestFactory(configuration), responseProcessor = new ConvertResourceSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async convertResourceSnapshotsWithHttpInfo(projectId, id, convertResourceSnapshotsRequest, _options) {
        let requestContextValue = await this.requestFactory.convertResourceSnapshots(projectId, id, convertResourceSnapshotsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.convertResourceSnapshotsWithHttpInfo(responseContext);
    }
    async convertResourceSnapshots(projectId, id, convertResourceSnapshotsRequest, _options) {
        const responseWithInfo = await this.convertResourceSnapshotsWithHttpInfo(projectId, id, convertResourceSnapshotsRequest, _options);
        return responseWithInfo.data;
    }
}
import { CreateApiCredentialsApiRequestFactory, CreateApiCredentialsApiResponseProcessor } from "../apis/CreateApiCredentialsApi.js";
export class PromiseCreateApiCredentialsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateApiCredentialsApiRequestFactory(configuration), responseProcessor = new CreateApiCredentialsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createApiCredentialsWithHttpInfo(createApiCredentialsRequest, _options) {
        let requestContextValue = await this.requestFactory.createApiCredentials(createApiCredentialsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createApiCredentialsWithHttpInfo(responseContext);
    }
    async createApiCredentials(createApiCredentialsRequest, _options) {
        const responseWithInfo = await this.createApiCredentialsWithHttpInfo(createApiCredentialsRequest, _options);
        return responseWithInfo.data;
    }
}
import { CreateBackupPolicyApiRequestFactory, CreateBackupPolicyApiResponseProcessor } from "../apis/CreateBackupPolicyApi.js";
export class PromiseCreateBackupPolicyApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateBackupPolicyApiRequestFactory(configuration), responseProcessor = new CreateBackupPolicyApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createPolicyWithHttpInfo(projectId, createPolicyInput, _options) {
        let requestContextValue = await this.requestFactory.createPolicy(projectId, createPolicyInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createPolicyWithHttpInfo(responseContext);
    }
    async createPolicy(projectId, createPolicyInput, _options) {
        const responseWithInfo = await this.createPolicyWithHttpInfo(projectId, createPolicyInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateControlApiRequestFactory, CreateControlApiResponseProcessor } from "../apis/CreateControlApi.js";
export class PromiseCreateControlApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateControlApiRequestFactory(configuration), responseProcessor = new CreateControlApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createControlWithHttpInfo(projectId, control, _options) {
        let requestContextValue = await this.requestFactory.createControl(projectId, control, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createControlWithHttpInfo(responseContext);
    }
    async createControl(projectId, control, _options) {
        const responseWithInfo = await this.createControlWithHttpInfo(projectId, control, _options);
        return responseWithInfo.data;
    }
}
import { CreateFindingExclusionPatternApiRequestFactory, CreateFindingExclusionPatternApiResponseProcessor } from "../apis/CreateFindingExclusionPatternApi.js";
export class PromiseCreateFindingExclusionPatternApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateFindingExclusionPatternApiRequestFactory(configuration), responseProcessor = new CreateFindingExclusionPatternApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createFindingExclusionPatternWithHttpInfo(projectId, createFindingExclusionPatternInput, _options) {
        let requestContextValue = await this.requestFactory.createFindingExclusionPattern(projectId, createFindingExclusionPatternInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createFindingExclusionPatternWithHttpInfo(responseContext);
    }
    async createFindingExclusionPattern(projectId, createFindingExclusionPatternInput, _options) {
        const responseWithInfo = await this.createFindingExclusionPatternWithHttpInfo(projectId, createFindingExclusionPatternInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateObjectStoreExclusionRuleApiRequestFactory, CreateObjectStoreExclusionRuleApiResponseProcessor } from "../apis/CreateObjectStoreExclusionRuleApi.js";
export class PromiseCreateObjectStoreExclusionRuleApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateObjectStoreExclusionRuleApiRequestFactory(configuration), responseProcessor = new CreateObjectStoreExclusionRuleApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createObjectStoreExclusionRuleWithHttpInfo(projectId, objectStoreExclusionRule, _options) {
        let requestContextValue = await this.requestFactory.createObjectStoreExclusionRule(projectId, objectStoreExclusionRule, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createObjectStoreExclusionRuleWithHttpInfo(responseContext);
    }
    async createObjectStoreExclusionRule(projectId, objectStoreExclusionRule, _options) {
        const responseWithInfo = await this.createObjectStoreExclusionRuleWithHttpInfo(projectId, objectStoreExclusionRule, _options);
        return responseWithInfo.data;
    }
}
import { CreateProjectApiRequestFactory, CreateProjectApiResponseProcessor } from "../apis/CreateProjectApi.js";
export class PromiseCreateProjectApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateProjectApiRequestFactory(configuration), responseProcessor = new CreateProjectApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createProjectWithHttpInfo(createProjectInput, _options) {
        let requestContextValue = await this.requestFactory.createProject(createProjectInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createProjectWithHttpInfo(responseContext);
    }
    async createProject(createProjectInput, _options) {
        const responseWithInfo = await this.createProjectWithHttpInfo(createProjectInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateProjectSourceAccountApiRequestFactory, CreateProjectSourceAccountApiResponseProcessor } from "../apis/CreateProjectSourceAccountApi.js";
export class PromiseCreateProjectSourceAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateProjectSourceAccountApiRequestFactory(configuration), responseProcessor = new CreateProjectSourceAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createProjectSourceAccountWithHttpInfo(projectId, createAccountInput, _options) {
        let requestContextValue = await this.requestFactory.createProjectSourceAccount(projectId, createAccountInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createProjectSourceAccountWithHttpInfo(responseContext);
    }
    async createProjectSourceAccount(projectId, createAccountInput, _options) {
        const responseWithInfo = await this.createProjectSourceAccountWithHttpInfo(projectId, createAccountInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateProjectUserApiRequestFactory, CreateProjectUserApiResponseProcessor } from "../apis/CreateProjectUserApi.js";
export class PromiseCreateProjectUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateProjectUserApiRequestFactory(configuration), responseProcessor = new CreateProjectUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createProjectUserWithHttpInfo(projectId, createUserInput, _options) {
        let requestContextValue = await this.requestFactory.createProjectUser(projectId, createUserInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createProjectUserWithHttpInfo(responseContext);
    }
    async createProjectUser(projectId, createUserInput, _options) {
        const responseWithInfo = await this.createProjectUserWithHttpInfo(projectId, createUserInput, _options);
        return responseWithInfo.data;
    }
}
import { CreatePulumiConfigurationApiRequestFactory, CreatePulumiConfigurationApiResponseProcessor } from "../apis/CreatePulumiConfigurationApi.js";
export class PromiseCreatePulumiConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreatePulumiConfigurationApiRequestFactory(configuration), responseProcessor = new CreatePulumiConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createPulumiConfigurationWithHttpInfo(createPulumiConfigurationRequest, _options) {
        let requestContextValue = await this.requestFactory.createPulumiConfiguration(createPulumiConfigurationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createPulumiConfigurationWithHttpInfo(responseContext);
    }
    async createPulumiConfiguration(createPulumiConfigurationRequest, _options) {
        const responseWithInfo = await this.createPulumiConfigurationWithHttpInfo(createPulumiConfigurationRequest, _options);
        return responseWithInfo.data;
    }
}
import { CreateRestoreAccountApiRequestFactory, CreateRestoreAccountApiResponseProcessor } from "../apis/CreateRestoreAccountApi.js";
export class PromiseCreateRestoreAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateRestoreAccountApiRequestFactory(configuration), responseProcessor = new CreateRestoreAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createRestoreAccountWithHttpInfo(createAccountInput, _options) {
        let requestContextValue = await this.requestFactory.createRestoreAccount(createAccountInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createRestoreAccountWithHttpInfo(responseContext);
    }
    async createRestoreAccount(createAccountInput, _options) {
        const responseWithInfo = await this.createRestoreAccountWithHttpInfo(createAccountInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateSamlIdentityProviderConfigApiRequestFactory, CreateSamlIdentityProviderConfigApiResponseProcessor } from "../apis/CreateSamlIdentityProviderConfigApi.js";
export class PromiseCreateSamlIdentityProviderConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateSamlIdentityProviderConfigApiRequestFactory(configuration), responseProcessor = new CreateSamlIdentityProviderConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createSamlIdentityProviderConfigWithHttpInfo(createSamlIdentityProviderConfigInput, _options) {
        let requestContextValue = await this.requestFactory.createSamlIdentityProviderConfig(createSamlIdentityProviderConfigInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createSamlIdentityProviderConfigWithHttpInfo(responseContext);
    }
    async createSamlIdentityProviderConfig(createSamlIdentityProviderConfigInput, _options) {
        const responseWithInfo = await this.createSamlIdentityProviderConfigWithHttpInfo(createSamlIdentityProviderConfigInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateScanJobApiRequestFactory, CreateScanJobApiResponseProcessor } from "../apis/CreateScanJobApi.js";
export class PromiseCreateScanJobApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateScanJobApiRequestFactory(configuration), responseProcessor = new CreateScanJobApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createScanJobWithHttpInfo(projectId, createScanJobInput, _options) {
        let requestContextValue = await this.requestFactory.createScanJob(projectId, createScanJobInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createScanJobWithHttpInfo(responseContext);
    }
    async createScanJob(projectId, createScanJobInput, _options) {
        const responseWithInfo = await this.createScanJobWithHttpInfo(projectId, createScanJobInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateScanningAccountApiRequestFactory, CreateScanningAccountApiResponseProcessor } from "../apis/CreateScanningAccountApi.js";
export class PromiseCreateScanningAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateScanningAccountApiRequestFactory(configuration), responseProcessor = new CreateScanningAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createScanningAccountWithHttpInfo(createAccountInput, _options) {
        let requestContextValue = await this.requestFactory.createScanningAccount(createAccountInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createScanningAccountWithHttpInfo(responseContext);
    }
    async createScanningAccount(createAccountInput, _options) {
        const responseWithInfo = await this.createScanningAccountWithHttpInfo(createAccountInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateSnapshotsCleanupJobApiRequestFactory, CreateSnapshotsCleanupJobApiResponseProcessor } from "../apis/CreateSnapshotsCleanupJobApi.js";
export class PromiseCreateSnapshotsCleanupJobApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateSnapshotsCleanupJobApiRequestFactory(configuration), responseProcessor = new CreateSnapshotsCleanupJobApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createSnapshotsCleanupJobWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.createSnapshotsCleanupJob(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createSnapshotsCleanupJobWithHttpInfo(responseContext);
    }
    async createSnapshotsCleanupJob(projectId, _options) {
        const responseWithInfo = await this.createSnapshotsCleanupJobWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { CreateSourceAccountApiRequestFactory, CreateSourceAccountApiResponseProcessor } from "../apis/CreateSourceAccountApi.js";
export class PromiseCreateSourceAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateSourceAccountApiRequestFactory(configuration), responseProcessor = new CreateSourceAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createSourceAccountWithHttpInfo(createAccountInput, _options) {
        let requestContextValue = await this.requestFactory.createSourceAccount(createAccountInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createSourceAccountWithHttpInfo(responseContext);
    }
    async createSourceAccount(createAccountInput, _options) {
        const responseWithInfo = await this.createSourceAccountWithHttpInfo(createAccountInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateUserApiRequestFactory, CreateUserApiResponseProcessor } from "../apis/CreateUserApi.js";
export class PromiseCreateUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateUserApiRequestFactory(configuration), responseProcessor = new CreateUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createUserWithHttpInfo(createUserInput, _options) {
        let requestContextValue = await this.requestFactory.createUser(createUserInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createUserWithHttpInfo(responseContext);
    }
    async createUser(createUserInput, _options) {
        const responseWithInfo = await this.createUserWithHttpInfo(createUserInput, _options);
        return responseWithInfo.data;
    }
}
import { CreateVaultAccountApiRequestFactory, CreateVaultAccountApiResponseProcessor } from "../apis/CreateVaultAccountApi.js";
export class PromiseCreateVaultAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CreateVaultAccountApiRequestFactory(configuration), responseProcessor = new CreateVaultAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createVaultAccountWithHttpInfo(createAccountInput, _options) {
        let requestContextValue = await this.requestFactory.createVaultAccount(createAccountInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createVaultAccountWithHttpInfo(responseContext);
    }
    async createVaultAccount(createAccountInput, _options) {
        const responseWithInfo = await this.createVaultAccountWithHttpInfo(createAccountInput, _options);
        return responseWithInfo.data;
    }
}
import { CrossCloudFederationSetupStatusApiRequestFactory, CrossCloudFederationSetupStatusApiResponseProcessor } from "../apis/CrossCloudFederationSetupStatusApi.js";
export class PromiseCrossCloudFederationSetupStatusApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new CrossCloudFederationSetupStatusApiRequestFactory(configuration), responseProcessor = new CrossCloudFederationSetupStatusApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async crossCloudFederationSetupStatusWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.crossCloudFederationSetupStatus(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.crossCloudFederationSetupStatusWithHttpInfo(responseContext);
    }
    async crossCloudFederationSetupStatus(_options) {
        const responseWithInfo = await this.crossCloudFederationSetupStatusWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { DashboardApiRequestFactory, DashboardApiResponseProcessor } from "../apis/DashboardApi.js";
export class PromiseDashboardApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DashboardApiRequestFactory(configuration), responseProcessor = new DashboardApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDailyStorageSummariesWithHttpInfo(projectId, startDate, endDate, _options) {
        let requestContextValue = await this.requestFactory.getDailyStorageSummaries(projectId, startDate, endDate, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDailyStorageSummariesWithHttpInfo(responseContext);
    }
    async getDailyStorageSummaries(projectId, startDate, endDate, _options) {
        const responseWithInfo = await this.getDailyStorageSummariesWithHttpInfo(projectId, startDate, endDate, _options);
        return responseWithInfo.data;
    }
}
import { DatabaseSnapshotsApiRequestFactory, DatabaseSnapshotsApiResponseProcessor } from "../apis/DatabaseSnapshotsApi.js";
export class PromiseDatabaseSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DatabaseSnapshotsApiRequestFactory(configuration), responseProcessor = new DatabaseSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getQueryResultWithHttpInfo(projectId, queryId, pageSize, pageToken, _options) {
        let requestContextValue = await this.requestFactory.getQueryResult(projectId, queryId, pageSize, pageToken, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getQueryResultWithHttpInfo(responseContext);
    }
    async getQueryResult(projectId, queryId, pageSize, pageToken, _options) {
        const responseWithInfo = await this.getQueryResultWithHttpInfo(projectId, queryId, pageSize, pageToken, _options);
        return responseWithInfo.data;
    }
    async getQueryStatusWithHttpInfo(projectId, queryId, _options) {
        let requestContextValue = await this.requestFactory.getQueryStatus(projectId, queryId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getQueryStatusWithHttpInfo(responseContext);
    }
    async getQueryStatus(projectId, queryId, _options) {
        const responseWithInfo = await this.getQueryStatusWithHttpInfo(projectId, queryId, _options);
        return responseWithInfo.data;
    }
    async runCrossResourceQueryWithHttpInfo(projectId, crossResourceQueryRequest, _options) {
        let requestContextValue = await this.requestFactory.runCrossResourceQuery(projectId, crossResourceQueryRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.runCrossResourceQueryWithHttpInfo(responseContext);
    }
    async runCrossResourceQuery(projectId, crossResourceQueryRequest, _options) {
        const responseWithInfo = await this.runCrossResourceQueryWithHttpInfo(projectId, crossResourceQueryRequest, _options);
        return responseWithInfo.data;
    }
    async runQueryWithHttpInfo(projectId, snapshotId, queryDBRequest, _options) {
        let requestContextValue = await this.requestFactory.runQuery(projectId, snapshotId, queryDBRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.runQueryWithHttpInfo(responseContext);
    }
    async runQuery(projectId, snapshotId, queryDBRequest, _options) {
        const responseWithInfo = await this.runQueryWithHttpInfo(projectId, snapshotId, queryDBRequest, _options);
        return responseWithInfo.data;
    }
}
import { DeleteApiCredentialsApiRequestFactory, DeleteApiCredentialsApiResponseProcessor } from "../apis/DeleteApiCredentialsApi.js";
export class PromiseDeleteApiCredentialsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteApiCredentialsApiRequestFactory(configuration), responseProcessor = new DeleteApiCredentialsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteApiCredentialsWithHttpInfo(clientId, _options) {
        let requestContextValue = await this.requestFactory.deleteApiCredentials(clientId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteApiCredentialsWithHttpInfo(responseContext);
    }
    async deleteApiCredentials(clientId, _options) {
        const responseWithInfo = await this.deleteApiCredentialsWithHttpInfo(clientId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteApplicationOverrideApiRequestFactory, DeleteApplicationOverrideApiResponseProcessor } from "../apis/DeleteApplicationOverrideApi.js";
export class PromiseDeleteApplicationOverrideApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteApplicationOverrideApiRequestFactory(configuration), responseProcessor = new DeleteApplicationOverrideApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteApplicationOverrideWithHttpInfo(projectId, applicationOverride, _options) {
        let requestContextValue = await this.requestFactory.deleteApplicationOverride(projectId, applicationOverride, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteApplicationOverrideWithHttpInfo(responseContext);
    }
    async deleteApplicationOverride(projectId, applicationOverride, _options) {
        const responseWithInfo = await this.deleteApplicationOverrideWithHttpInfo(projectId, applicationOverride, _options);
        return responseWithInfo.data;
    }
}
import { DeleteBackupPolicyApiRequestFactory, DeleteBackupPolicyApiResponseProcessor } from "../apis/DeleteBackupPolicyApi.js";
export class PromiseDeleteBackupPolicyApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteBackupPolicyApiRequestFactory(configuration), responseProcessor = new DeleteBackupPolicyApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deletePolicyWithHttpInfo(projectId, id, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.deletePolicy(projectId, id, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deletePolicyWithHttpInfo(responseContext);
    }
    async deletePolicy(projectId, id, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.deletePolicyWithHttpInfo(projectId, id, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteControlApiRequestFactory, DeleteControlApiResponseProcessor } from "../apis/DeleteControlApi.js";
export class PromiseDeleteControlApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteControlApiRequestFactory(configuration), responseProcessor = new DeleteControlApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteControlWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteControl(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteControlWithHttpInfo(responseContext);
    }
    async deleteControl(projectId, id, _options) {
        const responseWithInfo = await this.deleteControlWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteDynamoDbBackupConfigApiRequestFactory, DeleteDynamoDbBackupConfigApiResponseProcessor } from "../apis/DeleteDynamoDbBackupConfigApi.js";
export class PromiseDeleteDynamoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteDynamoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new DeleteDynamoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.deleteDynamoDbBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteDynamoDbBackupConfigWithHttpInfo(responseContext);
    }
    async deleteDynamoDbBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.deleteDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteFindingExclusionPatternApiRequestFactory, DeleteFindingExclusionPatternApiResponseProcessor } from "../apis/DeleteFindingExclusionPatternApi.js";
export class PromiseDeleteFindingExclusionPatternApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteFindingExclusionPatternApiRequestFactory(configuration), responseProcessor = new DeleteFindingExclusionPatternApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteFindingExclusionPatternWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteFindingExclusionPattern(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteFindingExclusionPatternWithHttpInfo(responseContext);
    }
    async deleteFindingExclusionPattern(projectId, id, _options) {
        const responseWithInfo = await this.deleteFindingExclusionPatternWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteKeyspacesBackupConfigApiRequestFactory, DeleteKeyspacesBackupConfigApiResponseProcessor } from "../apis/DeleteKeyspacesBackupConfigApi.js";
export class PromiseDeleteKeyspacesBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteKeyspacesBackupConfigApiRequestFactory(configuration), responseProcessor = new DeleteKeyspacesBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteKeyspacesBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.deleteKeyspacesBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteKeyspacesBackupConfigWithHttpInfo(responseContext);
    }
    async deleteKeyspacesBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.deleteKeyspacesBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteMongoDbBackupConfigApiRequestFactory, DeleteMongoDbBackupConfigApiResponseProcessor } from "../apis/DeleteMongoDbBackupConfigApi.js";
export class PromiseDeleteMongoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteMongoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new DeleteMongoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteMongoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.deleteMongoDbBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteMongoDbBackupConfigWithHttpInfo(responseContext);
    }
    async deleteMongoDbBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.deleteMongoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteObjectStoreExclusionRuleApiRequestFactory, DeleteObjectStoreExclusionRuleApiResponseProcessor } from "../apis/DeleteObjectStoreExclusionRuleApi.js";
export class PromiseDeleteObjectStoreExclusionRuleApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteObjectStoreExclusionRuleApiRequestFactory(configuration), responseProcessor = new DeleteObjectStoreExclusionRuleApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteObjectStoreExclusionRuleWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteObjectStoreExclusionRule(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteObjectStoreExclusionRuleWithHttpInfo(responseContext);
    }
    async deleteObjectStoreExclusionRule(projectId, id, _options) {
        const responseWithInfo = await this.deleteObjectStoreExclusionRuleWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteProjectApiRequestFactory, DeleteProjectApiResponseProcessor } from "../apis/DeleteProjectApi.js";
export class PromiseDeleteProjectApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteProjectApiRequestFactory(configuration), responseProcessor = new DeleteProjectApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteProjectWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.deleteProject(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteProjectWithHttpInfo(responseContext);
    }
    async deleteProject(projectId, _options) {
        const responseWithInfo = await this.deleteProjectWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteProjectUserApiRequestFactory, DeleteProjectUserApiResponseProcessor } from "../apis/DeleteProjectUserApi.js";
export class PromiseDeleteProjectUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteProjectUserApiRequestFactory(configuration), responseProcessor = new DeleteProjectUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteProjectUserWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteProjectUser(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteProjectUserWithHttpInfo(responseContext);
    }
    async deleteProjectUser(projectId, id, _options) {
        const responseWithInfo = await this.deleteProjectUserWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteSamlIdentityProviderConfigApiRequestFactory, DeleteSamlIdentityProviderConfigApiResponseProcessor } from "../apis/DeleteSamlIdentityProviderConfigApi.js";
export class PromiseDeleteSamlIdentityProviderConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteSamlIdentityProviderConfigApiRequestFactory(configuration), responseProcessor = new DeleteSamlIdentityProviderConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteSamlIdentityProviderConfigWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.deleteSamlIdentityProviderConfig(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteSamlIdentityProviderConfigWithHttpInfo(responseContext);
    }
    async deleteSamlIdentityProviderConfig(id, _options) {
        const responseWithInfo = await this.deleteSamlIdentityProviderConfigWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteScanningAccountApiRequestFactory, DeleteScanningAccountApiResponseProcessor } from "../apis/DeleteScanningAccountApi.js";
export class PromiseDeleteScanningAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteScanningAccountApiRequestFactory(configuration), responseProcessor = new DeleteScanningAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteScanningAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.deleteScanningAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteScanningAccountWithHttpInfo(responseContext);
    }
    async deleteScanningAccount(accountId, _options) {
        const responseWithInfo = await this.deleteScanningAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
}
import { DeleteUserApiRequestFactory, DeleteUserApiResponseProcessor } from "../apis/DeleteUserApi.js";
export class PromiseDeleteUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteUserApiRequestFactory(configuration), responseProcessor = new DeleteUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteUserWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.deleteUser(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteUserWithHttpInfo(responseContext);
    }
    async deleteUser(id, _options) {
        const responseWithInfo = await this.deleteUserWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
}
import { DeleteVaultAccountApiRequestFactory, DeleteVaultAccountApiResponseProcessor } from "../apis/DeleteVaultAccountApi.js";
export class PromiseDeleteVaultAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DeleteVaultAccountApiRequestFactory(configuration), responseProcessor = new DeleteVaultAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteVaultAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.deleteVaultAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteVaultAccountWithHttpInfo(responseContext);
    }
    async deleteVaultAccount(accountId, _options) {
        const responseWithInfo = await this.deleteVaultAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
}
import { DiscoveryApiRequestFactory, DiscoveryApiResponseProcessor } from "../apis/DiscoveryApi.js";
export class PromiseDiscoveryApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DiscoveryApiRequestFactory(configuration), responseProcessor = new DiscoveryApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async invokeDiscoverWithHttpInfo(projectId, accountId, discoveryInvokeParams, _options) {
        let requestContextValue = await this.requestFactory.invokeDiscover(projectId, accountId, discoveryInvokeParams, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.invokeDiscoverWithHttpInfo(responseContext);
    }
    async invokeDiscover(projectId, accountId, discoveryInvokeParams, _options) {
        const responseWithInfo = await this.invokeDiscoverWithHttpInfo(projectId, accountId, discoveryInvokeParams, _options);
        return responseWithInfo.data;
    }
    async syncGoogleWorkspaceDirectoryWithHttpInfo(projectId, googleWorkspaceId, _options) {
        let requestContextValue = await this.requestFactory.syncGoogleWorkspaceDirectory(projectId, googleWorkspaceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.syncGoogleWorkspaceDirectoryWithHttpInfo(responseContext);
    }
    async syncGoogleWorkspaceDirectory(projectId, googleWorkspaceId, _options) {
        const responseWithInfo = await this.syncGoogleWorkspaceDirectoryWithHttpInfo(projectId, googleWorkspaceId, _options);
        return responseWithInfo.data;
    }
}
import { DiscoveryStatusApiRequestFactory, DiscoveryStatusApiResponseProcessor } from "../apis/DiscoveryStatusApi.js";
export class PromiseDiscoveryStatusApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DiscoveryStatusApiRequestFactory(configuration), responseProcessor = new DiscoveryStatusApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async discoveryStatusWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.discoveryStatus(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.discoveryStatusWithHttpInfo(responseContext);
    }
    async discoveryStatus(projectId, accountId, _options) {
        const responseWithInfo = await this.discoveryStatusWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { DlsgPoliciesApiRequestFactory, DlsgPoliciesApiResponseProcessor } from "../apis/DlsgPoliciesApi.js";
export class PromiseDlsgPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DlsgPoliciesApiRequestFactory(configuration), responseProcessor = new DlsgPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createDlsgPolicyWithHttpInfo(projectId, createDlsgPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.createDlsgPolicy(projectId, createDlsgPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createDlsgPolicyWithHttpInfo(responseContext);
    }
    async createDlsgPolicy(projectId, createDlsgPolicyRequest, _options) {
        const responseWithInfo = await this.createDlsgPolicyWithHttpInfo(projectId, createDlsgPolicyRequest, _options);
        return responseWithInfo.data;
    }
    async deleteDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, _options) {
        let requestContextValue = await this.requestFactory.deleteDlsgPolicy(projectId, dlsgPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteDlsgPolicyWithHttpInfo(responseContext);
    }
    async deleteDlsgPolicy(projectId, dlsgPolicyId, _options) {
        const responseWithInfo = await this.deleteDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, _options);
        return responseWithInfo.data;
    }
    async getDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, _options) {
        let requestContextValue = await this.requestFactory.getDlsgPolicy(projectId, dlsgPolicyId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDlsgPolicyWithHttpInfo(responseContext);
    }
    async getDlsgPolicy(projectId, dlsgPolicyId, _options) {
        const responseWithInfo = await this.getDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, _options);
        return responseWithInfo.data;
    }
    async listDlsgPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listDlsgPolicies(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listDlsgPoliciesWithHttpInfo(responseContext);
    }
    async listDlsgPolicies(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listDlsgPoliciesWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async updateDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, updateDlsgPolicyRequest, _options) {
        let requestContextValue = await this.requestFactory.updateDlsgPolicy(projectId, dlsgPolicyId, updateDlsgPolicyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateDlsgPolicyWithHttpInfo(responseContext);
    }
    async updateDlsgPolicy(projectId, dlsgPolicyId, updateDlsgPolicyRequest, _options) {
        const responseWithInfo = await this.updateDlsgPolicyWithHttpInfo(projectId, dlsgPolicyId, updateDlsgPolicyRequest, _options);
        return responseWithInfo.data;
    }
}
import { DlsgSettingsApiRequestFactory, DlsgSettingsApiResponseProcessor } from "../apis/DlsgSettingsApi.js";
export class PromiseDlsgSettingsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new DlsgSettingsApiRequestFactory(configuration), responseProcessor = new DlsgSettingsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDlsgSettingsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getDlsgSettings(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDlsgSettingsWithHttpInfo(responseContext);
    }
    async getDlsgSettings(projectId, _options) {
        const responseWithInfo = await this.getDlsgSettingsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async saveDlsgSettingsWithHttpInfo(projectId, dlsgSettings, _options) {
        let requestContextValue = await this.requestFactory.saveDlsgSettings(projectId, dlsgSettings, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveDlsgSettingsWithHttpInfo(responseContext);
    }
    async saveDlsgSettings(projectId, dlsgSettings, _options) {
        const responseWithInfo = await this.saveDlsgSettingsWithHttpInfo(projectId, dlsgSettings, _options);
        return responseWithInfo.data;
    }
}
import { EonAccountsApiRequestFactory, EonAccountsApiResponseProcessor } from "../apis/EonAccountsApi.js";
export class PromiseEonAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new EonAccountsApiRequestFactory(configuration), responseProcessor = new EonAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deactivateAccountWithHttpInfo(accountId, deactivateAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.deactivateAccount(accountId, deactivateAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deactivateAccountWithHttpInfo(responseContext);
    }
    async deactivateAccount(accountId, deactivateAccountRequest, _options) {
        const responseWithInfo = await this.deactivateAccountWithHttpInfo(accountId, deactivateAccountRequest, _options);
        return responseWithInfo.data;
    }
    async reactivateAccountWithHttpInfo(accountId, reactivateAccountRequest, _options) {
        let requestContextValue = await this.requestFactory.reactivateAccount(accountId, reactivateAccountRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reactivateAccountWithHttpInfo(responseContext);
    }
    async reactivateAccount(accountId, reactivateAccountRequest, _options) {
        const responseWithInfo = await this.reactivateAccountWithHttpInfo(accountId, reactivateAccountRequest, _options);
        return responseWithInfo.data;
    }
}
import { FabricApiRequestFactory, FabricApiResponseProcessor } from "../apis/FabricApi.js";
export class PromiseFabricApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FabricApiRequestFactory(configuration), responseProcessor = new FabricApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createFabricTableShortcutDemoWithHttpInfo(projectId, createFabricTableShortcutDemoRequest, _options) {
        let requestContextValue = await this.requestFactory.createFabricTableShortcutDemo(projectId, createFabricTableShortcutDemoRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createFabricTableShortcutDemoWithHttpInfo(responseContext);
    }
    async createFabricTableShortcutDemo(projectId, createFabricTableShortcutDemoRequest, _options) {
        const responseWithInfo = await this.createFabricTableShortcutDemoWithHttpInfo(projectId, createFabricTableShortcutDemoRequest, _options);
        return responseWithInfo.data;
    }
}
import { FeatureFlagOverrideApiRequestFactory, FeatureFlagOverrideApiResponseProcessor } from "../apis/FeatureFlagOverrideApi.js";
export class PromiseFeatureFlagOverrideApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FeatureFlagOverrideApiRequestFactory(configuration), responseProcessor = new FeatureFlagOverrideApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async featureFlagOverrideWithHttpInfo(projectId, flagKey, value, _options) {
        let requestContextValue = await this.requestFactory.featureFlagOverride(projectId, flagKey, value, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.featureFlagOverrideWithHttpInfo(responseContext);
    }
    async featureFlagOverride(projectId, flagKey, value, _options) {
        const responseWithInfo = await this.featureFlagOverrideWithHttpInfo(projectId, flagKey, value, _options);
        return responseWithInfo.data;
    }
    async featureFlagOverrideClearWithHttpInfo(projectId, flagKey, _options) {
        let requestContextValue = await this.requestFactory.featureFlagOverrideClear(projectId, flagKey, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.featureFlagOverrideClearWithHttpInfo(responseContext);
    }
    async featureFlagOverrideClear(projectId, flagKey, _options) {
        const responseWithInfo = await this.featureFlagOverrideClearWithHttpInfo(projectId, flagKey, _options);
        return responseWithInfo.data;
    }
    async featureFlagOverrideGetWithHttpInfo(projectId, flagKey, _options) {
        let requestContextValue = await this.requestFactory.featureFlagOverrideGet(projectId, flagKey, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.featureFlagOverrideGetWithHttpInfo(responseContext);
    }
    async featureFlagOverrideGet(projectId, flagKey, _options) {
        const responseWithInfo = await this.featureFlagOverrideGetWithHttpInfo(projectId, flagKey, _options);
        return responseWithInfo.data;
    }
}
import { FileExplorerCountPathFilesApiRequestFactory, FileExplorerCountPathFilesApiResponseProcessor } from "../apis/FileExplorerCountPathFilesApi.js";
export class PromiseFileExplorerCountPathFilesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FileExplorerCountPathFilesApiRequestFactory(configuration), responseProcessor = new FileExplorerCountPathFilesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async fileExplorerCountPathFilesWithHttpInfo(projectId, resourceId, prefix, snapshotId, limit, _options) {
        let requestContextValue = await this.requestFactory.fileExplorerCountPathFiles(projectId, resourceId, prefix, snapshotId, limit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.fileExplorerCountPathFilesWithHttpInfo(responseContext);
    }
    async fileExplorerCountPathFiles(projectId, resourceId, prefix, snapshotId, limit, _options) {
        const responseWithInfo = await this.fileExplorerCountPathFilesWithHttpInfo(projectId, resourceId, prefix, snapshotId, limit, _options);
        return responseWithInfo.data;
    }
}
import { FileExplorerCountSecurityAlertsInResourceApiRequestFactory, FileExplorerCountSecurityAlertsInResourceApiResponseProcessor } from "../apis/FileExplorerCountSecurityAlertsInResourceApi.js";
export class PromiseFileExplorerCountSecurityAlertsInResourceApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FileExplorerCountSecurityAlertsInResourceApiRequestFactory(configuration), responseProcessor = new FileExplorerCountSecurityAlertsInResourceApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async fileExplorerCountSecurityAlertsInResourceWithHttpInfo(projectId, resourceId, snapshotId, _options) {
        let requestContextValue = await this.requestFactory.fileExplorerCountSecurityAlertsInResource(projectId, resourceId, snapshotId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.fileExplorerCountSecurityAlertsInResourceWithHttpInfo(responseContext);
    }
    async fileExplorerCountSecurityAlertsInResource(projectId, resourceId, snapshotId, _options) {
        const responseWithInfo = await this.fileExplorerCountSecurityAlertsInResourceWithHttpInfo(projectId, resourceId, snapshotId, _options);
        return responseWithInfo.data;
    }
}
import { FileExplorerCountWorkspaceEntitiesApiRequestFactory, FileExplorerCountWorkspaceEntitiesApiResponseProcessor } from "../apis/FileExplorerCountWorkspaceEntitiesApi.js";
export class PromiseFileExplorerCountWorkspaceEntitiesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FileExplorerCountWorkspaceEntitiesApiRequestFactory(configuration), responseProcessor = new FileExplorerCountWorkspaceEntitiesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async fileExplorerCountWorkspaceEntitiesWithHttpInfo(projectId, resourceId, prefix, snapshotId, _options) {
        let requestContextValue = await this.requestFactory.fileExplorerCountWorkspaceEntities(projectId, resourceId, prefix, snapshotId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.fileExplorerCountWorkspaceEntitiesWithHttpInfo(responseContext);
    }
    async fileExplorerCountWorkspaceEntities(projectId, resourceId, prefix, snapshotId, _options) {
        const responseWithInfo = await this.fileExplorerCountWorkspaceEntitiesWithHttpInfo(projectId, resourceId, prefix, snapshotId, _options);
        return responseWithInfo.data;
    }
}
import { FileExplorerListPathApiRequestFactory, FileExplorerListPathApiResponseProcessor } from "../apis/FileExplorerListPathApi.js";
export class PromiseFileExplorerListPathApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new FileExplorerListPathApiRequestFactory(configuration), responseProcessor = new FileExplorerListPathApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async fileExplorerListPathWithHttpInfo(projectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, securityAlertedOnly, snapshotId, sortField, sortDir, _options) {
        let requestContextValue = await this.requestFactory.fileExplorerListPath(projectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, securityAlertedOnly, snapshotId, sortField, sortDir, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.fileExplorerListPathWithHttpInfo(responseContext);
    }
    async fileExplorerListPath(projectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, securityAlertedOnly, snapshotId, sortField, sortDir, _options) {
        const responseWithInfo = await this.fileExplorerListPathWithHttpInfo(projectId, resourceId, prefix, pageSize, pageToken, queryExecutionId, securityAlertedOnly, snapshotId, sortField, sortDir, _options);
        return responseWithInfo.data;
    }
}
import { ForceDeleteSnapshotsApiRequestFactory, ForceDeleteSnapshotsApiResponseProcessor } from "../apis/ForceDeleteSnapshotsApi.js";
export class PromiseForceDeleteSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ForceDeleteSnapshotsApiRequestFactory(configuration), responseProcessor = new ForceDeleteSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async forceDeleteSnapshotsWithHttpInfo(projectId, forceDeleteSnapshotsRequest, _options) {
        let requestContextValue = await this.requestFactory.forceDeleteSnapshots(projectId, forceDeleteSnapshotsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.forceDeleteSnapshotsWithHttpInfo(responseContext);
    }
    async forceDeleteSnapshots(projectId, forceDeleteSnapshotsRequest, _options) {
        const responseWithInfo = await this.forceDeleteSnapshotsWithHttpInfo(projectId, forceDeleteSnapshotsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ForgotPasswordApiRequestFactory, ForgotPasswordApiResponseProcessor } from "../apis/ForgotPasswordApi.js";
export class PromiseForgotPasswordApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ForgotPasswordApiRequestFactory(configuration), responseProcessor = new ForgotPasswordApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async forgotPasswordWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.forgotPassword(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.forgotPasswordWithHttpInfo(responseContext);
    }
    async forgotPassword(_options) {
        const responseWithInfo = await this.forgotPasswordWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { GcpRegisterApiRequestFactory, GcpRegisterApiResponseProcessor } from "../apis/GcpRegisterApi.js";
export class PromiseGcpRegisterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GcpRegisterApiRequestFactory(configuration), responseProcessor = new GcpRegisterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async gcpRegisterWithHttpInfo(xGcpMarketplaceToken, _options) {
        let requestContextValue = await this.requestFactory.gcpRegister(xGcpMarketplaceToken, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.gcpRegisterWithHttpInfo(responseContext);
    }
    async gcpRegister(xGcpMarketplaceToken, _options) {
        const responseWithInfo = await this.gcpRegisterWithHttpInfo(xGcpMarketplaceToken, _options);
        return responseWithInfo.data;
    }
}
import { GdsgEnvironmentsApiRequestFactory, GdsgEnvironmentsApiResponseProcessor } from "../apis/GdsgEnvironmentsApi.js";
export class PromiseGdsgEnvironmentsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GdsgEnvironmentsApiRequestFactory(configuration), responseProcessor = new GdsgEnvironmentsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async deleteGdsgObjectsWithHttpInfo(projectId, environmentId, region, deleteGdsgObjectsRequest, _options) {
        let requestContextValue = await this.requestFactory.deleteGdsgObjects(projectId, environmentId, region, deleteGdsgObjectsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteGdsgObjectsWithHttpInfo(responseContext);
    }
    async deleteGdsgObjects(projectId, environmentId, region, deleteGdsgObjectsRequest, _options) {
        const responseWithInfo = await this.deleteGdsgObjectsWithHttpInfo(projectId, environmentId, region, deleteGdsgObjectsRequest, _options);
        return responseWithInfo.data;
    }
    async downloadGdsgObjectsWithHttpInfo(projectId, environmentId, region, downloadGdsgObjectsRequest, _options) {
        let requestContextValue = await this.requestFactory.downloadGdsgObjects(projectId, environmentId, region, downloadGdsgObjectsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.downloadGdsgObjectsWithHttpInfo(responseContext);
    }
    async downloadGdsgObjects(projectId, environmentId, region, downloadGdsgObjectsRequest, _options) {
        const responseWithInfo = await this.downloadGdsgObjectsWithHttpInfo(projectId, environmentId, region, downloadGdsgObjectsRequest, _options);
        return responseWithInfo.data;
    }
    async getGdsgDevStatsWithHttpInfo(projectId, environmentId, _options) {
        let requestContextValue = await this.requestFactory.getGdsgDevStats(projectId, environmentId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGdsgDevStatsWithHttpInfo(responseContext);
    }
    async getGdsgDevStats(projectId, environmentId, _options) {
        const responseWithInfo = await this.getGdsgDevStatsWithHttpInfo(projectId, environmentId, _options);
        return responseWithInfo.data;
    }
    async getGdsgRegionStatsWithHttpInfo(projectId, environmentId, sinceMs, untilMs, bucketIntervalMs, _options) {
        let requestContextValue = await this.requestFactory.getGdsgRegionStats(projectId, environmentId, sinceMs, untilMs, bucketIntervalMs, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGdsgRegionStatsWithHttpInfo(responseContext);
    }
    async getGdsgRegionStats(projectId, environmentId, sinceMs, untilMs, bucketIntervalMs, _options) {
        const responseWithInfo = await this.getGdsgRegionStatsWithHttpInfo(projectId, environmentId, sinceMs, untilMs, bucketIntervalMs, _options);
        return responseWithInfo.data;
    }
    async listGdsgEnvironmentsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGdsgEnvironments(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGdsgEnvironmentsWithHttpInfo(responseContext);
    }
    async listGdsgEnvironments(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGdsgEnvironmentsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listGdsgObjectsWithHttpInfo(projectId, environmentId, region, role, bucket, prefix, delimiter, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGdsgObjects(projectId, environmentId, region, role, bucket, prefix, delimiter, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGdsgObjectsWithHttpInfo(responseContext);
    }
    async listGdsgObjects(projectId, environmentId, region, role, bucket, prefix, delimiter, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGdsgObjectsWithHttpInfo(projectId, environmentId, region, role, bucket, prefix, delimiter, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { GetAWSStackDetailsApiRequestFactory, GetAWSStackDetailsApiResponseProcessor } from "../apis/GetAWSStackDetailsApi.js";
export class PromiseGetAWSStackDetailsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAWSStackDetailsApiRequestFactory(configuration), responseProcessor = new GetAWSStackDetailsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAWSStackDetailsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getAWSStackDetails(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAWSStackDetailsWithHttpInfo(responseContext);
    }
    async getAWSStackDetails(projectId, accountId, _options) {
        const responseWithInfo = await this.getAWSStackDetailsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetAccountUIPreferencesApiRequestFactory, GetAccountUIPreferencesApiResponseProcessor } from "../apis/GetAccountUIPreferencesApi.js";
export class PromiseGetAccountUIPreferencesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAccountUIPreferencesApiRequestFactory(configuration), responseProcessor = new GetAccountUIPreferencesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAccountUIPreferencesWithHttpInfo(key, _options) {
        let requestContextValue = await this.requestFactory.getAccountUIPreferences(key, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAccountUIPreferencesWithHttpInfo(responseContext);
    }
    async getAccountUIPreferences(key, _options) {
        const responseWithInfo = await this.getAccountUIPreferencesWithHttpInfo(key, _options);
        return responseWithInfo.data;
    }
}
import { GetAccountsApiRequestFactory, GetAccountsApiResponseProcessor } from "../apis/GetAccountsApi.js";
export class PromiseGetAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAccountsApiRequestFactory(configuration), responseProcessor = new GetAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAccountWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.getAccount(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAccountWithHttpInfo(responseContext);
    }
    async getAccount(id, _options) {
        const responseWithInfo = await this.getAccountWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
    async getAccountLogoWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getAccountLogo(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAccountLogoWithHttpInfo(responseContext);
    }
    async getAccountLogo(_options) {
        const responseWithInfo = await this.getAccountLogoWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { GetAdditionalAzureAppsOnboardingStatusApiRequestFactory, GetAdditionalAzureAppsOnboardingStatusApiResponseProcessor } from "../apis/GetAdditionalAzureAppsOnboardingStatusApi.js";
export class PromiseGetAdditionalAzureAppsOnboardingStatusApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAdditionalAzureAppsOnboardingStatusApiRequestFactory(configuration), responseProcessor = new GetAdditionalAzureAppsOnboardingStatusApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAdditionalAzureAppsOnboardingStatusWithHttpInfo(projectId, workflowId, _options) {
        let requestContextValue = await this.requestFactory.getAdditionalAzureAppsOnboardingStatus(projectId, workflowId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAdditionalAzureAppsOnboardingStatusWithHttpInfo(responseContext);
    }
    async getAdditionalAzureAppsOnboardingStatus(projectId, workflowId, _options) {
        const responseWithInfo = await this.getAdditionalAzureAppsOnboardingStatusWithHttpInfo(projectId, workflowId, _options);
        return responseWithInfo.data;
    }
}
import { GetAmiApiRequestFactory, GetAmiApiResponseProcessor } from "../apis/GetAmiApi.js";
export class PromiseGetAmiApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAmiApiRequestFactory(configuration), responseProcessor = new GetAmiApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAmiWithHttpInfo(projectId, accountId, region, id, _options) {
        let requestContextValue = await this.requestFactory.getAmi(projectId, accountId, region, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAmiWithHttpInfo(responseContext);
    }
    async getAmi(projectId, accountId, region, id, _options) {
        const responseWithInfo = await this.getAmiWithHttpInfo(projectId, accountId, region, id, _options);
        return responseWithInfo.data;
    }
}
import { GetApiCredentialsApiRequestFactory, GetApiCredentialsApiResponseProcessor } from "../apis/GetApiCredentialsApi.js";
export class PromiseGetApiCredentialsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetApiCredentialsApiRequestFactory(configuration), responseProcessor = new GetApiCredentialsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getApiCredentialsWithHttpInfo(clientId, _options) {
        let requestContextValue = await this.requestFactory.getApiCredentials(clientId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getApiCredentialsWithHttpInfo(responseContext);
    }
    async getApiCredentials(clientId, _options) {
        const responseWithInfo = await this.getApiCredentialsWithHttpInfo(clientId, _options);
        return responseWithInfo.data;
    }
}
import { GetAwsPrivateLinkApiRequestFactory, GetAwsPrivateLinkApiResponseProcessor } from "../apis/GetAwsPrivateLinkApi.js";
export class PromiseGetAwsPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAwsPrivateLinkApiRequestFactory(configuration), responseProcessor = new GetAwsPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAwsPrivateLinkWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getAwsPrivateLink(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAwsPrivateLinkWithHttpInfo(responseContext);
    }
    async getAwsPrivateLink(projectId, id, _options) {
        const responseWithInfo = await this.getAwsPrivateLinkWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetAzureBackupApplicationsApiRequestFactory, GetAzureBackupApplicationsApiResponseProcessor } from "../apis/GetAzureBackupApplicationsApi.js";
export class PromiseGetAzureBackupApplicationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAzureBackupApplicationsApiRequestFactory(configuration), responseProcessor = new GetAzureBackupApplicationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAzureBackupApplicationsWithHttpInfo(projectId, accountType, tenantId, _options) {
        let requestContextValue = await this.requestFactory.getAzureBackupApplications(projectId, accountType, tenantId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureBackupApplicationsWithHttpInfo(responseContext);
    }
    async getAzureBackupApplications(projectId, accountType, tenantId, _options) {
        const responseWithInfo = await this.getAzureBackupApplicationsWithHttpInfo(projectId, accountType, tenantId, _options);
        return responseWithInfo.data;
    }
}
import { GetAzureConsentUrlApiRequestFactory, GetAzureConsentUrlApiResponseProcessor } from "../apis/GetAzureConsentUrlApi.js";
export class PromiseGetAzureConsentUrlApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAzureConsentUrlApiRequestFactory(configuration), responseProcessor = new GetAzureConsentUrlApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAzureConsentUrlWithHttpInfo(projectId, requestId, tenantId, _options) {
        let requestContextValue = await this.requestFactory.getAzureConsentUrl(projectId, requestId, tenantId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureConsentUrlWithHttpInfo(responseContext);
    }
    async getAzureConsentUrl(projectId, requestId, tenantId, _options) {
        const responseWithInfo = await this.getAzureConsentUrlWithHttpInfo(projectId, requestId, tenantId, _options);
        return responseWithInfo.data;
    }
}
import { GetAzureEonApplicationApiRequestFactory, GetAzureEonApplicationApiResponseProcessor } from "../apis/GetAzureEonApplicationApi.js";
export class PromiseGetAzureEonApplicationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAzureEonApplicationApiRequestFactory(configuration), responseProcessor = new GetAzureEonApplicationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAzureEonApplicationWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getAzureEonApplication(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureEonApplicationWithHttpInfo(responseContext);
    }
    async getAzureEonApplication(projectId, _options) {
        const responseWithInfo = await this.getAzureEonApplicationWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetAzureQueryExportStorageApiRequestFactory, GetAzureQueryExportStorageApiResponseProcessor } from "../apis/GetAzureQueryExportStorageApi.js";
export class PromiseGetAzureQueryExportStorageApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAzureQueryExportStorageApiRequestFactory(configuration), responseProcessor = new GetAzureQueryExportStorageApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAzureQueryExportStorageWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getAzureQueryExportStorage(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureQueryExportStorageWithHttpInfo(responseContext);
    }
    async getAzureQueryExportStorage(projectId, accountId, _options) {
        const responseWithInfo = await this.getAzureQueryExportStorageWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetAzureSubscriptionsApiRequestFactory, GetAzureSubscriptionsApiResponseProcessor } from "../apis/GetAzureSubscriptionsApi.js";
export class PromiseGetAzureSubscriptionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetAzureSubscriptionsApiRequestFactory(configuration), responseProcessor = new GetAzureSubscriptionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getAzureSubscriptionsWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.getAzureSubscriptions(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureSubscriptionsWithHttpInfo(responseContext);
    }
    async getAzureSubscriptions(projectId, requestId, _options) {
        const responseWithInfo = await this.getAzureSubscriptionsWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
}
import { GetBackedUpResourcesApiRequestFactory, GetBackedUpResourcesApiResponseProcessor } from "../apis/GetBackedUpResourcesApi.js";
export class PromiseGetBackedUpResourcesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackedUpResourcesApiRequestFactory(configuration), responseProcessor = new GetBackedUpResourcesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBackedUpResourcesWithHttpInfo(projectId, cloudProvider, _options) {
        let requestContextValue = await this.requestFactory.getBackedUpResources(projectId, cloudProvider, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackedUpResourcesWithHttpInfo(responseContext);
    }
    async getBackedUpResources(projectId, cloudProvider, _options) {
        const responseWithInfo = await this.getBackedUpResourcesWithHttpInfo(projectId, cloudProvider, _options);
        return responseWithInfo.data;
    }
}
import { GetBackedUpStorageDateHistogramApiRequestFactory, GetBackedUpStorageDateHistogramApiResponseProcessor } from "../apis/GetBackedUpStorageDateHistogramApi.js";
export class PromiseGetBackedUpStorageDateHistogramApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackedUpStorageDateHistogramApiRequestFactory(configuration), responseProcessor = new GetBackedUpStorageDateHistogramApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBackedUpStorageDateHistogramWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getBackedUpStorageDateHistogram(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackedUpStorageDateHistogramWithHttpInfo(responseContext);
    }
    async getBackedUpStorageDateHistogram(projectId, _options) {
        const responseWithInfo = await this.getBackedUpStorageDateHistogramWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetBackupControlViolationsApiRequestFactory, GetBackupControlViolationsApiResponseProcessor } from "../apis/GetBackupControlViolationsApi.js";
export class PromiseGetBackupControlViolationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackupControlViolationsApiRequestFactory(configuration), responseProcessor = new GetBackupControlViolationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBackupControlViolationsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getBackupControlViolations(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupControlViolationsWithHttpInfo(responseContext);
    }
    async getBackupControlViolations(projectId, _options) {
        const responseWithInfo = await this.getBackupControlViolationsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetBackupPolicyApiRequestFactory, GetBackupPolicyApiResponseProcessor } from "../apis/GetBackupPolicyApi.js";
export class PromiseGetBackupPolicyApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackupPolicyApiRequestFactory(configuration), responseProcessor = new GetBackupPolicyApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getPolicyWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getPolicy(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getPolicyWithHttpInfo(responseContext);
    }
    async getPolicy(projectId, id, _options) {
        const responseWithInfo = await this.getPolicyWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetBackupScopeApiRequestFactory, GetBackupScopeApiResponseProcessor } from "../apis/GetBackupScopeApi.js";
export class PromiseGetBackupScopeApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackupScopeApiRequestFactory(configuration), responseProcessor = new GetBackupScopeApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBackupScopeWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getBackupScope(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupScopeWithHttpInfo(responseContext);
    }
    async getBackupScope(projectId, _options) {
        const responseWithInfo = await this.getBackupScopeWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetBackupScopeSummaryApiRequestFactory, GetBackupScopeSummaryApiResponseProcessor } from "../apis/GetBackupScopeSummaryApi.js";
export class PromiseGetBackupScopeSummaryApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBackupScopeSummaryApiRequestFactory(configuration), responseProcessor = new GetBackupScopeSummaryApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBackupScopeSummaryWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getBackupScopeSummary(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupScopeSummaryWithHttpInfo(responseContext);
    }
    async getBackupScopeSummary(projectId, _options) {
        const responseWithInfo = await this.getBackupScopeSummaryWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetBillingPeriodsApiRequestFactory, GetBillingPeriodsApiResponseProcessor } from "../apis/GetBillingPeriodsApi.js";
export class PromiseGetBillingPeriodsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetBillingPeriodsApiRequestFactory(configuration), responseProcessor = new GetBillingPeriodsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getBillingPeriodsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getBillingPeriods(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBillingPeriodsWithHttpInfo(responseContext);
    }
    async getBillingPeriods(_options) {
        const responseWithInfo = await this.getBillingPeriodsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { GetControlApiRequestFactory, GetControlApiResponseProcessor } from "../apis/GetControlApi.js";
export class PromiseGetControlApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetControlApiRequestFactory(configuration), responseProcessor = new GetControlApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getControlWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getControl(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getControlWithHttpInfo(responseContext);
    }
    async getControl(projectId, id, _options) {
        const responseWithInfo = await this.getControlWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetControlPlaneConfigApiRequestFactory, GetControlPlaneConfigApiResponseProcessor } from "../apis/GetControlPlaneConfigApi.js";
export class PromiseGetControlPlaneConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetControlPlaneConfigApiRequestFactory(configuration), responseProcessor = new GetControlPlaneConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getControlPlaneConfigWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getControlPlaneConfig(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getControlPlaneConfigWithHttpInfo(responseContext);
    }
    async getControlPlaneConfig(_options) {
        const responseWithInfo = await this.getControlPlaneConfigWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { GetCreditsApiRequestFactory, GetCreditsApiResponseProcessor } from "../apis/GetCreditsApi.js";
export class PromiseGetCreditsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetCreditsApiRequestFactory(configuration), responseProcessor = new GetCreditsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getCreditsWithHttpInfo(costUnit, _options) {
        let requestContextValue = await this.requestFactory.getCredits(costUnit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getCreditsWithHttpInfo(responseContext);
    }
    async getCredits(costUnit, _options) {
        const responseWithInfo = await this.getCreditsWithHttpInfo(costUnit, _options);
        return responseWithInfo.data;
    }
}
import { GetCreditsByProviderApiRequestFactory, GetCreditsByProviderApiResponseProcessor } from "../apis/GetCreditsByProviderApi.js";
export class PromiseGetCreditsByProviderApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetCreditsByProviderApiRequestFactory(configuration), responseProcessor = new GetCreditsByProviderApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getCreditsByProviderWithHttpInfo(provider, costUnit, _options) {
        let requestContextValue = await this.requestFactory.getCreditsByProvider(provider, costUnit, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getCreditsByProviderWithHttpInfo(responseContext);
    }
    async getCreditsByProvider(provider, costUnit, _options) {
        const responseWithInfo = await this.getCreditsByProviderWithHttpInfo(provider, costUnit, _options);
        return responseWithInfo.data;
    }
}
import { GetDashboardHomeDriftProtectionApiRequestFactory, GetDashboardHomeDriftProtectionApiResponseProcessor } from "../apis/GetDashboardHomeDriftProtectionApi.js";
export class PromiseGetDashboardHomeDriftProtectionApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetDashboardHomeDriftProtectionApiRequestFactory(configuration), responseProcessor = new GetDashboardHomeDriftProtectionApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDashboardHomeDriftProtectionWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getDashboardHomeDriftProtection(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDashboardHomeDriftProtectionWithHttpInfo(responseContext);
    }
    async getDashboardHomeDriftProtection(projectId, _options) {
        const responseWithInfo = await this.getDashboardHomeDriftProtectionWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetDatabasesByServerApiRequestFactory, GetDatabasesByServerApiResponseProcessor } from "../apis/GetDatabasesByServerApi.js";
export class PromiseGetDatabasesByServerApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetDatabasesByServerApiRequestFactory(configuration), responseProcessor = new GetDatabasesByServerApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDatabasesByServerWithHttpInfo(projectId, serverId, snapshotId, dbName, _options) {
        let requestContextValue = await this.requestFactory.getDatabasesByServer(projectId, serverId, snapshotId, dbName, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDatabasesByServerWithHttpInfo(responseContext);
    }
    async getDatabasesByServer(projectId, serverId, snapshotId, dbName, _options) {
        const responseWithInfo = await this.getDatabasesByServerWithHttpInfo(projectId, serverId, snapshotId, dbName, _options);
        return responseWithInfo.data;
    }
}
import { GetDynamoDbBackupConfigApiRequestFactory, GetDynamoDbBackupConfigApiResponseProcessor } from "../apis/GetDynamoDbBackupConfigApi.js";
export class PromiseGetDynamoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetDynamoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new GetDynamoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.getDynamoDbBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDynamoDbBackupConfigWithHttpInfo(responseContext);
    }
    async getDynamoDbBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.getDynamoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { GetDynamoDbBackupConfigApplicationApiRequestFactory, GetDynamoDbBackupConfigApplicationApiResponseProcessor } from "../apis/GetDynamoDbBackupConfigApplicationApi.js";
export class PromiseGetDynamoDbBackupConfigApplicationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetDynamoDbBackupConfigApplicationApiRequestFactory(configuration), responseProcessor = new GetDynamoDbBackupConfigApplicationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getDynamoDbBackupConfigApplicationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getDynamoDbBackupConfigApplication(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getDynamoDbBackupConfigApplicationWithHttpInfo(responseContext);
    }
    async getDynamoDbBackupConfigApplication(projectId, id, _options) {
        const responseWithInfo = await this.getDynamoDbBackupConfigApplicationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetGwsBackupFilterApiRequestFactory, GetGwsBackupFilterApiResponseProcessor } from "../apis/GetGwsBackupFilterApi.js";
export class PromiseGetGwsBackupFilterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetGwsBackupFilterApiRequestFactory(configuration), responseProcessor = new GetGwsBackupFilterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getGwsBackupFilterWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getGwsBackupFilter(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getGwsBackupFilterWithHttpInfo(responseContext);
    }
    async getGwsBackupFilter(projectId, id, _options) {
        const responseWithInfo = await this.getGwsBackupFilterWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetInfectedDataByClassificationApiRequestFactory, GetInfectedDataByClassificationApiResponseProcessor } from "../apis/GetInfectedDataByClassificationApi.js";
export class PromiseGetInfectedDataByClassificationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetInfectedDataByClassificationApiRequestFactory(configuration), responseProcessor = new GetInfectedDataByClassificationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getInfectedDataByClassificationWithHttpInfo(projectId, prod, providers, _options) {
        let requestContextValue = await this.requestFactory.getInfectedDataByClassification(projectId, prod, providers, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getInfectedDataByClassificationWithHttpInfo(responseContext);
    }
    async getInfectedDataByClassification(projectId, prod, providers, _options) {
        const responseWithInfo = await this.getInfectedDataByClassificationWithHttpInfo(projectId, prod, providers, _options);
        return responseWithInfo.data;
    }
}
import { GetInfectedDataOverTimeApiRequestFactory, GetInfectedDataOverTimeApiResponseProcessor } from "../apis/GetInfectedDataOverTimeApi.js";
export class PromiseGetInfectedDataOverTimeApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetInfectedDataOverTimeApiRequestFactory(configuration), responseProcessor = new GetInfectedDataOverTimeApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getInfectedDataOverTimeWithHttpInfo(projectId, days, prod, providers, _options) {
        let requestContextValue = await this.requestFactory.getInfectedDataOverTime(projectId, days, prod, providers, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getInfectedDataOverTimeWithHttpInfo(responseContext);
    }
    async getInfectedDataOverTime(projectId, days, prod, providers, _options) {
        const responseWithInfo = await this.getInfectedDataOverTimeWithHttpInfo(projectId, days, prod, providers, _options);
        return responseWithInfo.data;
    }
}
import { GetKeyspacesBackupConfigApiRequestFactory, GetKeyspacesBackupConfigApiResponseProcessor } from "../apis/GetKeyspacesBackupConfigApi.js";
export class PromiseGetKeyspacesBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetKeyspacesBackupConfigApiRequestFactory(configuration), responseProcessor = new GetKeyspacesBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getKeyspacesBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.getKeyspacesBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getKeyspacesBackupConfigWithHttpInfo(responseContext);
    }
    async getKeyspacesBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.getKeyspacesBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { GetKeyspacesBackupConfigApplicationApiRequestFactory, GetKeyspacesBackupConfigApplicationApiResponseProcessor } from "../apis/GetKeyspacesBackupConfigApplicationApi.js";
export class PromiseGetKeyspacesBackupConfigApplicationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetKeyspacesBackupConfigApplicationApiRequestFactory(configuration), responseProcessor = new GetKeyspacesBackupConfigApplicationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getKeyspacesBackupConfigApplicationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getKeyspacesBackupConfigApplication(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getKeyspacesBackupConfigApplicationWithHttpInfo(responseContext);
    }
    async getKeyspacesBackupConfigApplication(projectId, id, _options) {
        const responseWithInfo = await this.getKeyspacesBackupConfigApplicationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetMongoAtlasPrivateLinkApiRequestFactory, GetMongoAtlasPrivateLinkApiResponseProcessor } from "../apis/GetMongoAtlasPrivateLinkApi.js";
export class PromiseGetMongoAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetMongoAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new GetMongoAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.getMongoAtlasPrivateLink(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getMongoAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async getMongoAtlasPrivateLink(projectId, accountId, region, _options) {
        const responseWithInfo = await this.getMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { GetMongoDbBackupConfigApiRequestFactory, GetMongoDbBackupConfigApiResponseProcessor } from "../apis/GetMongoDbBackupConfigApi.js";
export class PromiseGetMongoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetMongoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new GetMongoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getMongoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.getMongoDbBackupConfig(projectId, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getMongoDbBackupConfigWithHttpInfo(responseContext);
    }
    async getMongoDbBackupConfig(projectId, accountId, resourceId, _options) {
        const responseWithInfo = await this.getMongoDbBackupConfigWithHttpInfo(projectId, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { GetObjectStoreExclusionRuleApiRequestFactory, GetObjectStoreExclusionRuleApiResponseProcessor } from "../apis/GetObjectStoreExclusionRuleApi.js";
export class PromiseGetObjectStoreExclusionRuleApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetObjectStoreExclusionRuleApiRequestFactory(configuration), responseProcessor = new GetObjectStoreExclusionRuleApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getObjectStoreExclusionRuleWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getObjectStoreExclusionRule(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getObjectStoreExclusionRuleWithHttpInfo(responseContext);
    }
    async getObjectStoreExclusionRule(projectId, id, _options) {
        const responseWithInfo = await this.getObjectStoreExclusionRuleWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetProjectApiRequestFactory, GetProjectApiResponseProcessor } from "../apis/GetProjectApi.js";
export class PromiseGetProjectApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetProjectApiRequestFactory(configuration), responseProcessor = new GetProjectApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getProject(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectWithHttpInfo(responseContext);
    }
    async getProject(projectId, _options) {
        const responseWithInfo = await this.getProjectWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetProjectScanningAccountApiRequestFactory, GetProjectScanningAccountApiResponseProcessor } from "../apis/GetProjectScanningAccountApi.js";
export class PromiseGetProjectScanningAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetProjectScanningAccountApiRequestFactory(configuration), responseProcessor = new GetProjectScanningAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectScanningAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getProjectScanningAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectScanningAccountWithHttpInfo(responseContext);
    }
    async getProjectScanningAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.getProjectScanningAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetProjectSourceAccountApiRequestFactory, GetProjectSourceAccountApiResponseProcessor } from "../apis/GetProjectSourceAccountApi.js";
export class PromiseGetProjectSourceAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetProjectSourceAccountApiRequestFactory(configuration), responseProcessor = new GetProjectSourceAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectSourceAccountWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getProjectSourceAccount(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectSourceAccountWithHttpInfo(responseContext);
    }
    async getProjectSourceAccount(projectId, accountId, _options) {
        const responseWithInfo = await this.getProjectSourceAccountWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetProjectSourceAccountConfigurationApiRequestFactory, GetProjectSourceAccountConfigurationApiResponseProcessor } from "../apis/GetProjectSourceAccountConfigurationApi.js";
export class PromiseGetProjectSourceAccountConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetProjectSourceAccountConfigurationApiRequestFactory(configuration), responseProcessor = new GetProjectSourceAccountConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectSourceAccountConfigurationWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getProjectSourceAccountConfiguration(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectSourceAccountConfigurationWithHttpInfo(responseContext);
    }
    async getProjectSourceAccountConfiguration(projectId, accountId, _options) {
        const responseWithInfo = await this.getProjectSourceAccountConfigurationWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetProjectUserApiRequestFactory, GetProjectUserApiResponseProcessor } from "../apis/GetProjectUserApi.js";
export class PromiseGetProjectUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetProjectUserApiRequestFactory(configuration), responseProcessor = new GetProjectUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectUserWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getProjectUser(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectUserWithHttpInfo(responseContext);
    }
    async getProjectUser(projectId, id, _options) {
        const responseWithInfo = await this.getProjectUserWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetPulumiConfigurationApiRequestFactory, GetPulumiConfigurationApiResponseProcessor } from "../apis/GetPulumiConfigurationApi.js";
export class PromiseGetPulumiConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetPulumiConfigurationApiRequestFactory(configuration), responseProcessor = new GetPulumiConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getPulumiConfigurationWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.getPulumiConfiguration(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getPulumiConfigurationWithHttpInfo(responseContext);
    }
    async getPulumiConfiguration(id, _options) {
        const responseWithInfo = await this.getPulumiConfigurationWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
}
import { GetResourceConfigurationApiRequestFactory, GetResourceConfigurationApiResponseProcessor } from "../apis/GetResourceConfigurationApi.js";
export class PromiseGetResourceConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetResourceConfigurationApiRequestFactory(configuration), responseProcessor = new GetResourceConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getResourceConfigurationInternalWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getResourceConfigurationInternal(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getResourceConfigurationInternalWithHttpInfo(responseContext);
    }
    async getResourceConfigurationInternal(projectId, id, _options) {
        const responseWithInfo = await this.getResourceConfigurationInternalWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetRestoreCloudAccountTagsConfigurationApiRequestFactory, GetRestoreCloudAccountTagsConfigurationApiResponseProcessor } from "../apis/GetRestoreCloudAccountTagsConfigurationApi.js";
export class PromiseGetRestoreCloudAccountTagsConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetRestoreCloudAccountTagsConfigurationApiRequestFactory(configuration), responseProcessor = new GetRestoreCloudAccountTagsConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getRestoreCloudAccountTagsConfigurationWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreCloudAccountTagsConfiguration(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreCloudAccountTagsConfigurationWithHttpInfo(responseContext);
    }
    async getRestoreCloudAccountTagsConfiguration(projectId, accountId, _options) {
        const responseWithInfo = await this.getRestoreCloudAccountTagsConfigurationWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetSamlIdentityProviderConfigApiRequestFactory, GetSamlIdentityProviderConfigApiResponseProcessor } from "../apis/GetSamlIdentityProviderConfigApi.js";
export class PromiseGetSamlIdentityProviderConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSamlIdentityProviderConfigApiRequestFactory(configuration), responseProcessor = new GetSamlIdentityProviderConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSamlIdentityProviderConfigWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.getSamlIdentityProviderConfig(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSamlIdentityProviderConfigWithHttpInfo(responseContext);
    }
    async getSamlIdentityProviderConfig(id, _options) {
        const responseWithInfo = await this.getSamlIdentityProviderConfigWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
}
import { GetScanningAccountApiRequestFactory, GetScanningAccountApiResponseProcessor } from "../apis/GetScanningAccountApi.js";
export class PromiseGetScanningAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetScanningAccountApiRequestFactory(configuration), responseProcessor = new GetScanningAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getScanningAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.getScanningAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getScanningAccountWithHttpInfo(responseContext);
    }
    async getScanningAccount(accountId, _options) {
        const responseWithInfo = await this.getScanningAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetSecurityPlanApiRequestFactory, GetSecurityPlanApiResponseProcessor } from "../apis/GetSecurityPlanApi.js";
export class PromiseGetSecurityPlanApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSecurityPlanApiRequestFactory(configuration), responseProcessor = new GetSecurityPlanApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSecurityPlanWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getSecurityPlan(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSecurityPlanWithHttpInfo(responseContext);
    }
    async getSecurityPlan(projectId, _options) {
        const responseWithInfo = await this.getSecurityPlanWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetSecuritySummaryApiRequestFactory, GetSecuritySummaryApiResponseProcessor } from "../apis/GetSecuritySummaryApi.js";
export class PromiseGetSecuritySummaryApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSecuritySummaryApiRequestFactory(configuration), responseProcessor = new GetSecuritySummaryApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSecuritySummaryWithHttpInfo(projectId, prod, providers, _options) {
        let requestContextValue = await this.requestFactory.getSecuritySummary(projectId, prod, providers, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSecuritySummaryWithHttpInfo(responseContext);
    }
    async getSecuritySummary(projectId, prod, providers, _options) {
        const responseWithInfo = await this.getSecuritySummaryWithHttpInfo(projectId, prod, providers, _options);
        return responseWithInfo.data;
    }
}
import { GetServiceProviderDetailsApiRequestFactory, GetServiceProviderDetailsApiResponseProcessor } from "../apis/GetServiceProviderDetailsApi.js";
export class PromiseGetServiceProviderDetailsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetServiceProviderDetailsApiRequestFactory(configuration), responseProcessor = new GetServiceProviderDetailsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getServiceProviderDetailsWithHttpInfo(providerName, _options) {
        let requestContextValue = await this.requestFactory.getServiceProviderDetails(providerName, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getServiceProviderDetailsWithHttpInfo(responseContext);
    }
    async getServiceProviderDetails(providerName, _options) {
        const responseWithInfo = await this.getServiceProviderDetailsWithHttpInfo(providerName, _options);
        return responseWithInfo.data;
    }
}
import { GetSnapshotsByServerApiRequestFactory, GetSnapshotsByServerApiResponseProcessor } from "../apis/GetSnapshotsByServerApi.js";
export class PromiseGetSnapshotsByServerApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSnapshotsByServerApiRequestFactory(configuration), responseProcessor = new GetSnapshotsByServerApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSnapshotsByServerWithHttpInfo(projectId, serverId, start, end, _options) {
        let requestContextValue = await this.requestFactory.getSnapshotsByServer(projectId, serverId, start, end, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSnapshotsByServerWithHttpInfo(responseContext);
    }
    async getSnapshotsByServer(projectId, serverId, start, end, _options) {
        const responseWithInfo = await this.getSnapshotsByServerWithHttpInfo(projectId, serverId, start, end, _options);
        return responseWithInfo.data;
    }
}
import { GetSnapshotsSummaryApiRequestFactory, GetSnapshotsSummaryApiResponseProcessor } from "../apis/GetSnapshotsSummaryApi.js";
export class PromiseGetSnapshotsSummaryApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSnapshotsSummaryApiRequestFactory(configuration), responseProcessor = new GetSnapshotsSummaryApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSnapshotsSummaryWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getSnapshotsSummary(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSnapshotsSummaryWithHttpInfo(responseContext);
    }
    async getSnapshotsSummary(projectId, id, _options) {
        const responseWithInfo = await this.getSnapshotsSummaryWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { GetSourceStorageBackupStatusApiRequestFactory, GetSourceStorageBackupStatusApiResponseProcessor } from "../apis/GetSourceStorageBackupStatusApi.js";
export class PromiseGetSourceStorageBackupStatusApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSourceStorageBackupStatusApiRequestFactory(configuration), responseProcessor = new GetSourceStorageBackupStatusApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSourceStorageBackupStatusWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getSourceStorageBackupStatus(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSourceStorageBackupStatusWithHttpInfo(responseContext);
    }
    async getSourceStorageBackupStatus(projectId, _options) {
        const responseWithInfo = await this.getSourceStorageBackupStatusWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { GetSqlVmNetworkInfoApiRequestFactory, GetSqlVmNetworkInfoApiResponseProcessor } from "../apis/GetSqlVmNetworkInfoApi.js";
export class PromiseGetSqlVmNetworkInfoApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetSqlVmNetworkInfoApiRequestFactory(configuration), responseProcessor = new GetSqlVmNetworkInfoApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getSqlVmNetworkInfoWithHttpInfo(projectId, getSqlVmNetworkInfoRequest, _options) {
        let requestContextValue = await this.requestFactory.getSqlVmNetworkInfo(projectId, getSqlVmNetworkInfoRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSqlVmNetworkInfoWithHttpInfo(responseContext);
    }
    async getSqlVmNetworkInfo(projectId, getSqlVmNetworkInfoRequest, _options) {
        const responseWithInfo = await this.getSqlVmNetworkInfoWithHttpInfo(projectId, getSqlVmNetworkInfoRequest, _options);
        return responseWithInfo.data;
    }
}
import { GetTotalSourceStorageSizeApiRequestFactory, GetTotalSourceStorageSizeApiResponseProcessor } from "../apis/GetTotalSourceStorageSizeApi.js";
export class PromiseGetTotalSourceStorageSizeApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetTotalSourceStorageSizeApiRequestFactory(configuration), responseProcessor = new GetTotalSourceStorageSizeApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getTotalSourceStorageSizeWithHttpInfo(projectId, totalSourceStorageSizeRequest, _options) {
        let requestContextValue = await this.requestFactory.getTotalSourceStorageSize(projectId, totalSourceStorageSizeRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getTotalSourceStorageSizeWithHttpInfo(responseContext);
    }
    async getTotalSourceStorageSize(projectId, totalSourceStorageSizeRequest, _options) {
        const responseWithInfo = await this.getTotalSourceStorageSizeWithHttpInfo(projectId, totalSourceStorageSizeRequest, _options);
        return responseWithInfo.data;
    }
}
import { GetUserApiRequestFactory, GetUserApiResponseProcessor } from "../apis/GetUserApi.js";
export class PromiseGetUserApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetUserApiRequestFactory(configuration), responseProcessor = new GetUserApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getUserWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.getUser(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getUserWithHttpInfo(responseContext);
    }
    async getUser(id, _options) {
        const responseWithInfo = await this.getUserWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
}
import { GetVaultAccountApiRequestFactory, GetVaultAccountApiResponseProcessor } from "../apis/GetVaultAccountApi.js";
export class PromiseGetVaultAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetVaultAccountApiRequestFactory(configuration), responseProcessor = new GetVaultAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getVaultAccountWithHttpInfo(accountId, _options) {
        let requestContextValue = await this.requestFactory.getVaultAccount(accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getVaultAccountWithHttpInfo(responseContext);
    }
    async getVaultAccount(accountId, _options) {
        const responseWithInfo = await this.getVaultAccountWithHttpInfo(accountId, _options);
        return responseWithInfo.data;
    }
}
import { GetViewerApiRequestFactory, GetViewerApiResponseProcessor } from "../apis/GetViewerApi.js";
export class PromiseGetViewerApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetViewerApiRequestFactory(configuration), responseProcessor = new GetViewerApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getViewerWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getViewer(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getViewerWithHttpInfo(responseContext);
    }
    async getViewer(_options) {
        const responseWithInfo = await this.getViewerWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { GetViewerUIPreferencesApiRequestFactory, GetViewerUIPreferencesApiResponseProcessor } from "../apis/GetViewerUIPreferencesApi.js";
export class PromiseGetViewerUIPreferencesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new GetViewerUIPreferencesApiRequestFactory(configuration), responseProcessor = new GetViewerUIPreferencesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getViewerUIPreferencesWithHttpInfo(key, _options) {
        let requestContextValue = await this.requestFactory.getViewerUIPreferences(key, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getViewerUIPreferencesWithHttpInfo(responseContext);
    }
    async getViewerUIPreferences(key, _options) {
        const responseWithInfo = await this.getViewerUIPreferencesWithHttpInfo(key, _options);
        return responseWithInfo.data;
    }
}
import { IamApiRequestFactory, IamApiResponseProcessor } from "../apis/IamApi.js";
export class PromiseIamApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new IamApiRequestFactory(configuration), responseProcessor = new IamApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createIdpGroupWithHttpInfo(createIdpGroupRequest, _options) {
        let requestContextValue = await this.requestFactory.createIdpGroup(createIdpGroupRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createIdpGroupWithHttpInfo(responseContext);
    }
    async createIdpGroup(createIdpGroupRequest, _options) {
        const responseWithInfo = await this.createIdpGroupWithHttpInfo(createIdpGroupRequest, _options);
        return responseWithInfo.data;
    }
    async createRoleWithHttpInfo(createRoleRequest, _options) {
        let requestContextValue = await this.requestFactory.createRole(createRoleRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createRoleWithHttpInfo(responseContext);
    }
    async createRole(createRoleRequest, _options) {
        const responseWithInfo = await this.createRoleWithHttpInfo(createRoleRequest, _options);
        return responseWithInfo.data;
    }
    async deleteIdpGroupWithHttpInfo(groupId, _options) {
        let requestContextValue = await this.requestFactory.deleteIdpGroup(groupId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteIdpGroupWithHttpInfo(responseContext);
    }
    async deleteIdpGroup(groupId, _options) {
        const responseWithInfo = await this.deleteIdpGroupWithHttpInfo(groupId, _options);
        return responseWithInfo.data;
    }
    async deleteRoleWithHttpInfo(roleId, _options) {
        let requestContextValue = await this.requestFactory.deleteRole(roleId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteRoleWithHttpInfo(responseContext);
    }
    async deleteRole(roleId, _options) {
        const responseWithInfo = await this.deleteRoleWithHttpInfo(roleId, _options);
        return responseWithInfo.data;
    }
    async getIdpGroupWithHttpInfo(groupId, _options) {
        let requestContextValue = await this.requestFactory.getIdpGroup(groupId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getIdpGroupWithHttpInfo(responseContext);
    }
    async getIdpGroup(groupId, _options) {
        const responseWithInfo = await this.getIdpGroupWithHttpInfo(groupId, _options);
        return responseWithInfo.data;
    }
    async getRoleWithHttpInfo(roleId, _options) {
        let requestContextValue = await this.requestFactory.getRole(roleId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRoleWithHttpInfo(responseContext);
    }
    async getRole(roleId, _options) {
        const responseWithInfo = await this.getRoleWithHttpInfo(roleId, _options);
        return responseWithInfo.data;
    }
    async getRoleUsageWithHttpInfo(roleId, _options) {
        let requestContextValue = await this.requestFactory.getRoleUsage(roleId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRoleUsageWithHttpInfo(responseContext);
    }
    async getRoleUsage(roleId, _options) {
        const responseWithInfo = await this.getRoleUsageWithHttpInfo(roleId, _options);
        return responseWithInfo.data;
    }
    async listIdpGroupsWithHttpInfo(pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listIdpGroups(pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listIdpGroupsWithHttpInfo(responseContext);
    }
    async listIdpGroups(pageToken, pageSize, _options) {
        const responseWithInfo = await this.listIdpGroupsWithHttpInfo(pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listIdpsWithHttpInfo(pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listIdps(pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listIdpsWithHttpInfo(responseContext);
    }
    async listIdps(pageToken, pageSize, _options) {
        const responseWithInfo = await this.listIdpsWithHttpInfo(pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listPermissionsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listPermissions(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listPermissionsWithHttpInfo(responseContext);
    }
    async listPermissions(_options) {
        const responseWithInfo = await this.listPermissionsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
    async listRolesWithHttpInfo(pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listRoles(pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRolesWithHttpInfo(responseContext);
    }
    async listRoles(pageToken, pageSize, _options) {
        const responseWithInfo = await this.listRolesWithHttpInfo(pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async updateIdpGroupWithHttpInfo(groupId, updateIdpGroupRequest, _options) {
        let requestContextValue = await this.requestFactory.updateIdpGroup(groupId, updateIdpGroupRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateIdpGroupWithHttpInfo(responseContext);
    }
    async updateIdpGroup(groupId, updateIdpGroupRequest, _options) {
        const responseWithInfo = await this.updateIdpGroupWithHttpInfo(groupId, updateIdpGroupRequest, _options);
        return responseWithInfo.data;
    }
    async updateRoleWithHttpInfo(roleId, updateRoleRequest, _options) {
        let requestContextValue = await this.requestFactory.updateRole(roleId, updateRoleRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRoleWithHttpInfo(responseContext);
    }
    async updateRole(roleId, updateRoleRequest, _options) {
        const responseWithInfo = await this.updateRoleWithHttpInfo(roleId, updateRoleRequest, _options);
        return responseWithInfo.data;
    }
}
import { InitiateAuthApiRequestFactory, InitiateAuthApiResponseProcessor } from "../apis/InitiateAuthApi.js";
export class PromiseInitiateAuthApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new InitiateAuthApiRequestFactory(configuration), responseProcessor = new InitiateAuthApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async initiateAuthWithHttpInfo(initAuthInput, _options) {
        let requestContextValue = await this.requestFactory.initiateAuth(initAuthInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.initiateAuthWithHttpInfo(responseContext);
    }
    async initiateAuth(initAuthInput, _options) {
        const responseWithInfo = await this.initiateAuthWithHttpInfo(initAuthInput, _options);
        return responseWithInfo.data;
    }
}
import { IntegrationsApiRequestFactory, IntegrationsApiResponseProcessor } from "../apis/IntegrationsApi.js";
export class PromiseIntegrationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new IntegrationsApiRequestFactory(configuration), responseProcessor = new IntegrationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createIntegrationWithHttpInfo(projectId, createIntegrationRequest, _options) {
        let requestContextValue = await this.requestFactory.createIntegration(projectId, createIntegrationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createIntegrationWithHttpInfo(responseContext);
    }
    async createIntegration(projectId, createIntegrationRequest, _options) {
        const responseWithInfo = await this.createIntegrationWithHttpInfo(projectId, createIntegrationRequest, _options);
        return responseWithInfo.data;
    }
    async deleteIntegrationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteIntegration(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteIntegrationWithHttpInfo(responseContext);
    }
    async deleteIntegration(projectId, id, _options) {
        const responseWithInfo = await this.deleteIntegrationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getIntegrationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getIntegration(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getIntegrationWithHttpInfo(responseContext);
    }
    async getIntegration(projectId, id, _options) {
        const responseWithInfo = await this.getIntegrationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async listIntegrationsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listIntegrations(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listIntegrationsWithHttpInfo(responseContext);
    }
    async listIntegrations(projectId, _options) {
        const responseWithInfo = await this.listIntegrationsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async syncIntegrationWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.syncIntegration(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.syncIntegrationWithHttpInfo(responseContext);
    }
    async syncIntegration(projectId, id, _options) {
        const responseWithInfo = await this.syncIntegrationWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async updateIntegrationWithHttpInfo(projectId, id, updateIntegrationRequest, _options) {
        let requestContextValue = await this.requestFactory.updateIntegration(projectId, id, updateIntegrationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateIntegrationWithHttpInfo(responseContext);
    }
    async updateIntegration(projectId, id, updateIntegrationRequest, _options) {
        const responseWithInfo = await this.updateIntegrationWithHttpInfo(projectId, id, updateIntegrationRequest, _options);
        return responseWithInfo.data;
    }
}
import { InternalOAuth2ApiRequestFactory, InternalOAuth2ApiResponseProcessor } from "../apis/InternalOAuth2Api.js";
export class PromiseInternalOAuth2Api {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new InternalOAuth2ApiRequestFactory(configuration), responseProcessor = new InternalOAuth2ApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async internalOAuth2CognitoExchangeWithHttpInfo(cognitoExchangeRequest, _options) {
        let requestContextValue = await this.requestFactory.internalOAuth2CognitoExchange(cognitoExchangeRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.internalOAuth2CognitoExchangeWithHttpInfo(responseContext);
    }
    async internalOAuth2CognitoExchange(cognitoExchangeRequest, _options) {
        const responseWithInfo = await this.internalOAuth2CognitoExchangeWithHttpInfo(cognitoExchangeRequest, _options);
        return responseWithInfo.data;
    }
    async internalOAuth2IntrospectWithHttpInfo(introspectRequest, _options) {
        let requestContextValue = await this.requestFactory.internalOAuth2Introspect(introspectRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.internalOAuth2IntrospectWithHttpInfo(responseContext);
    }
    async internalOAuth2Introspect(introspectRequest, _options) {
        const responseWithInfo = await this.internalOAuth2IntrospectWithHttpInfo(introspectRequest, _options);
        return responseWithInfo.data;
    }
}
import { InventoryPopulateApiRequestFactory, InventoryPopulateApiResponseProcessor } from "../apis/InventoryPopulateApi.js";
export class PromiseInventoryPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new InventoryPopulateApiRequestFactory(configuration), responseProcessor = new InventoryPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async inventoryPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.inventoryPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.inventoryPopulateWithHttpInfo(responseContext);
    }
    async inventoryPopulate(projectId, version, _options) {
        const responseWithInfo = await this.inventoryPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { IsSecurityPlanEnabledApiRequestFactory, IsSecurityPlanEnabledApiResponseProcessor } from "../apis/IsSecurityPlanEnabledApi.js";
export class PromiseIsSecurityPlanEnabledApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new IsSecurityPlanEnabledApiRequestFactory(configuration), responseProcessor = new IsSecurityPlanEnabledApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async isSecurityPlanEnabledWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.isSecurityPlanEnabled(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.isSecurityPlanEnabledWithHttpInfo(responseContext);
    }
    async isSecurityPlanEnabled(projectId, _options) {
        const responseWithInfo = await this.isSecurityPlanEnabledWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { IsVaultDeletableApiRequestFactory, IsVaultDeletableApiResponseProcessor } from "../apis/IsVaultDeletableApi.js";
export class PromiseIsVaultDeletableApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new IsVaultDeletableApiRequestFactory(configuration), responseProcessor = new IsVaultDeletableApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async isVaultDeletableWithHttpInfo(projectId, vaultId, _options) {
        let requestContextValue = await this.requestFactory.isVaultDeletable(projectId, vaultId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.isVaultDeletableWithHttpInfo(responseContext);
    }
    async isVaultDeletable(projectId, vaultId, _options) {
        const responseWithInfo = await this.isVaultDeletableWithHttpInfo(projectId, vaultId, _options);
        return responseWithInfo.data;
    }
}
import { JobsApiRequestFactory, JobsApiResponseProcessor } from "../apis/JobsApi.js";
export class PromiseJobsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new JobsApiRequestFactory(configuration), responseProcessor = new JobsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async backupJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options) {
        let requestContextValue = await this.requestFactory.backupJobsDownload(projectId, filter, sort, columns, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.backupJobsDownloadWithHttpInfo(responseContext);
    }
    async backupJobsDownload(projectId, filter, sort, columns, _options) {
        const responseWithInfo = await this.backupJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options);
        return responseWithInfo.data;
    }
    async cancelBackupJobWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.cancelBackupJob(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelBackupJobWithHttpInfo(responseContext);
    }
    async cancelBackupJob(projectId, jobId, _options) {
        const responseWithInfo = await this.cancelBackupJobWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async cancelRestoreJobWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.cancelRestoreJob(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelRestoreJobWithHttpInfo(responseContext);
    }
    async cancelRestoreJob(projectId, jobId, _options) {
        const responseWithInfo = await this.cancelRestoreJobWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async getBackupJobWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.getBackupJob(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupJobWithHttpInfo(responseContext);
    }
    async getBackupJob(projectId, jobId, _options) {
        const responseWithInfo = await this.getBackupJobWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async getBackupJobProgressStepsWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.getBackupJobProgressSteps(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getBackupJobProgressStepsWithHttpInfo(responseContext);
    }
    async getBackupJobProgressSteps(projectId, jobId, _options) {
        const responseWithInfo = await this.getBackupJobProgressStepsWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async getRestoreJobWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreJob(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreJobWithHttpInfo(responseContext);
    }
    async getRestoreJob(projectId, jobId, _options) {
        const responseWithInfo = await this.getRestoreJobWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async getRestoreJobProgressStepsWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreJobProgressSteps(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreJobProgressStepsWithHttpInfo(responseContext);
    }
    async getRestoreJobProgressSteps(projectId, jobId, _options) {
        const responseWithInfo = await this.getRestoreJobProgressStepsWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async getScanJobWithHttpInfo(projectId, jobId, _options) {
        let requestContextValue = await this.requestFactory.getScanJob(projectId, jobId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getScanJobWithHttpInfo(responseContext);
    }
    async getScanJob(projectId, jobId, _options) {
        const responseWithInfo = await this.getScanJobWithHttpInfo(projectId, jobId, _options);
        return responseWithInfo.data;
    }
    async listBackupJobsWithHttpInfo(projectId, pageToken, pageSize, listBackupJobsRequest, _options) {
        let requestContextValue = await this.requestFactory.listBackupJobs(projectId, pageToken, pageSize, listBackupJobsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupJobsWithHttpInfo(responseContext);
    }
    async listBackupJobs(projectId, pageToken, pageSize, listBackupJobsRequest, _options) {
        const responseWithInfo = await this.listBackupJobsWithHttpInfo(projectId, pageToken, pageSize, listBackupJobsRequest, _options);
        return responseWithInfo.data;
    }
    async listBackupJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listBackupJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupJobsInternalWithHttpInfo(responseContext);
    }
    async listBackupJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listBackupJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
    async listRestoreJobsWithHttpInfo(projectId, pageToken, pageSize, listRestoreJobsRequest, _options) {
        let requestContextValue = await this.requestFactory.listRestoreJobs(projectId, pageToken, pageSize, listRestoreJobsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreJobsWithHttpInfo(responseContext);
    }
    async listRestoreJobs(projectId, pageToken, pageSize, listRestoreJobsRequest, _options) {
        const responseWithInfo = await this.listRestoreJobsWithHttpInfo(projectId, pageToken, pageSize, listRestoreJobsRequest, _options);
        return responseWithInfo.data;
    }
    async listRestoreJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listRestoreJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreJobsInternalWithHttpInfo(responseContext);
    }
    async listRestoreJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listRestoreJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
    async listScanJobsWithHttpInfo(projectId, pageToken, pageSize, listScanJobsRequest, _options) {
        let requestContextValue = await this.requestFactory.listScanJobs(projectId, pageToken, pageSize, listScanJobsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listScanJobsWithHttpInfo(responseContext);
    }
    async listScanJobs(projectId, pageToken, pageSize, listScanJobsRequest, _options) {
        const responseWithInfo = await this.listScanJobsWithHttpInfo(projectId, pageToken, pageSize, listScanJobsRequest, _options);
        return responseWithInfo.data;
    }
    async listScanJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listScanJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listScanJobsInternalWithHttpInfo(responseContext);
    }
    async listScanJobsInternal(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listScanJobsInternalWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
    async restoreJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options) {
        let requestContextValue = await this.requestFactory.restoreJobsDownload(projectId, filter, sort, columns, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreJobsDownloadWithHttpInfo(responseContext);
    }
    async restoreJobsDownload(projectId, filter, sort, columns, _options) {
        const responseWithInfo = await this.restoreJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options);
        return responseWithInfo.data;
    }
    async scanJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options) {
        let requestContextValue = await this.requestFactory.scanJobsDownload(projectId, filter, sort, columns, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.scanJobsDownloadWithHttpInfo(responseContext);
    }
    async scanJobsDownload(projectId, filter, sort, columns, _options) {
        const responseWithInfo = await this.scanJobsDownloadWithHttpInfo(projectId, filter, sort, columns, _options);
        return responseWithInfo.data;
    }
}
import { LimitsApiRequestFactory, LimitsApiResponseProcessor } from "../apis/LimitsApi.js";
export class PromiseLimitsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LimitsApiRequestFactory(configuration), responseProcessor = new LimitsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async disableResourceOverrideLimitsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.disableResourceOverrideLimits(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.disableResourceOverrideLimitsWithHttpInfo(responseContext);
    }
    async disableResourceOverrideLimits(projectId, id, _options) {
        const responseWithInfo = await this.disableResourceOverrideLimitsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async enableResourceOverrideLimitsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.enableResourceOverrideLimits(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.enableResourceOverrideLimitsWithHttpInfo(responseContext);
    }
    async enableResourceOverrideLimits(projectId, id, _options) {
        const responseWithInfo = await this.enableResourceOverrideLimitsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getProjectLimitsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getProjectLimits(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectLimitsWithHttpInfo(responseContext);
    }
    async getProjectLimits(projectId, _options) {
        const responseWithInfo = await this.getProjectLimitsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async getProjectMaxRetentionDaysLimitWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getProjectMaxRetentionDaysLimit(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectMaxRetentionDaysLimitWithHttpInfo(responseContext);
    }
    async getProjectMaxRetentionDaysLimit(projectId, _options) {
        const responseWithInfo = await this.getProjectMaxRetentionDaysLimitWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async setProjectLimitsWithHttpInfo(projectId, requestBody, _options) {
        let requestContextValue = await this.requestFactory.setProjectLimits(projectId, requestBody, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.setProjectLimitsWithHttpInfo(responseContext);
    }
    async setProjectLimits(projectId, requestBody, _options) {
        const responseWithInfo = await this.setProjectLimitsWithHttpInfo(projectId, requestBody, _options);
        return responseWithInfo.data;
    }
}
import { ListAllPulumiConfigurationsApiRequestFactory, ListAllPulumiConfigurationsApiResponseProcessor } from "../apis/ListAllPulumiConfigurationsApi.js";
export class PromiseListAllPulumiConfigurationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAllPulumiConfigurationsApiRequestFactory(configuration), responseProcessor = new ListAllPulumiConfigurationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAllPulumiConfigurationsWithHttpInfo(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options) {
        let requestContextValue = await this.requestFactory.listAllPulumiConfigurations(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAllPulumiConfigurationsWithHttpInfo(responseContext);
    }
    async listAllPulumiConfigurations(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options) {
        const responseWithInfo = await this.listAllPulumiConfigurationsWithHttpInfo(pageIndex, pageSize, sort, pulumiConfigurationsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAllRegionsApiRequestFactory, ListAllRegionsApiResponseProcessor } from "../apis/ListAllRegionsApi.js";
export class PromiseListAllRegionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAllRegionsApiRequestFactory(configuration), responseProcessor = new ListAllRegionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAllRegionsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listAllRegions(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAllRegionsWithHttpInfo(responseContext);
    }
    async listAllRegions(_options) {
        const responseWithInfo = await this.listAllRegionsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListAllRestoreAccountsApiRequestFactory, ListAllRestoreAccountsApiResponseProcessor } from "../apis/ListAllRestoreAccountsApi.js";
export class PromiseListAllRestoreAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAllRestoreAccountsApiRequestFactory(configuration), responseProcessor = new ListAllRestoreAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAllRestoreAccountsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listAllRestoreAccounts(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAllRestoreAccountsWithHttpInfo(responseContext);
    }
    async listAllRestoreAccounts(_options) {
        const responseWithInfo = await this.listAllRestoreAccountsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListApiCredentialsApiRequestFactory, ListApiCredentialsApiResponseProcessor } from "../apis/ListApiCredentialsApi.js";
export class PromiseListApiCredentialsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListApiCredentialsApiRequestFactory(configuration), responseProcessor = new ListApiCredentialsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listApiCredentialsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listApiCredentials(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listApiCredentialsWithHttpInfo(responseContext);
    }
    async listApiCredentials(_options) {
        const responseWithInfo = await this.listApiCredentialsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListApplicationOverridesApiRequestFactory, ListApplicationOverridesApiResponseProcessor } from "../apis/ListApplicationOverridesApi.js";
export class PromiseListApplicationOverridesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListApplicationOverridesApiRequestFactory(configuration), responseProcessor = new ListApplicationOverridesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listApplicationOverridesWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listApplicationOverrides(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listApplicationOverridesWithHttpInfo(responseContext);
    }
    async listApplicationOverrides(projectId, id, _options) {
        const responseWithInfo = await this.listApplicationOverridesWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListAtlasClustersApiRequestFactory, ListAtlasClustersApiResponseProcessor } from "../apis/ListAtlasClustersApi.js";
export class PromiseListAtlasClustersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAtlasClustersApiRequestFactory(configuration), responseProcessor = new ListAtlasClustersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAtlasClustersWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listAtlasClusters(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAtlasClustersWithHttpInfo(responseContext);
    }
    async listAtlasClusters(projectId, accountId, _options) {
        const responseWithInfo = await this.listAtlasClustersWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListAtlasProjectsApiRequestFactory, ListAtlasProjectsApiResponseProcessor } from "../apis/ListAtlasProjectsApi.js";
export class PromiseListAtlasProjectsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAtlasProjectsApiRequestFactory(configuration), responseProcessor = new ListAtlasProjectsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAtlasProjectsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listAtlasProjects(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAtlasProjectsWithHttpInfo(responseContext);
    }
    async listAtlasProjects(projectId, accountId, _options) {
        const responseWithInfo = await this.listAtlasProjectsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListAuditLogsApiRequestFactory, ListAuditLogsApiResponseProcessor } from "../apis/ListAuditLogsApi.js";
export class PromiseListAuditLogsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAuditLogsApiRequestFactory(configuration), responseProcessor = new ListAuditLogsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAuditLogsOldWithHttpInfo(pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listAuditLogsOld(pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAuditLogsOldWithHttpInfo(responseContext);
    }
    async listAuditLogsOld(pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listAuditLogsOldWithHttpInfo(pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListAvailabilityZonesApiRequestFactory, ListAvailabilityZonesApiResponseProcessor } from "../apis/ListAvailabilityZonesApi.js";
export class PromiseListAvailabilityZonesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAvailabilityZonesApiRequestFactory(configuration), responseProcessor = new ListAvailabilityZonesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAvailabilityZonesWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAvailabilityZones(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAvailabilityZonesWithHttpInfo(responseContext);
    }
    async listAvailabilityZones(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAvailabilityZonesWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsInstanceProfilesApiRequestFactory, ListAwsInstanceProfilesApiResponseProcessor } from "../apis/ListAwsInstanceProfilesApi.js";
export class PromiseListAwsInstanceProfilesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsInstanceProfilesApiRequestFactory(configuration), responseProcessor = new ListAwsInstanceProfilesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsInstanceProfilesWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsInstanceProfiles(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsInstanceProfilesWithHttpInfo(responseContext);
    }
    async listAwsInstanceProfiles(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAwsInstanceProfilesWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsInstanceTypesApiRequestFactory, ListAwsInstanceTypesApiResponseProcessor } from "../apis/ListAwsInstanceTypesApi.js";
export class PromiseListAwsInstanceTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsInstanceTypesApiRequestFactory(configuration), responseProcessor = new ListAwsInstanceTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsInstanceTypesWithHttpInfo(projectId, accountId, region, subnetId, volumeCount, totalVolumeBytes, _options) {
        let requestContextValue = await this.requestFactory.listAwsInstanceTypes(projectId, accountId, region, subnetId, volumeCount, totalVolumeBytes, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsInstanceTypesWithHttpInfo(responseContext);
    }
    async listAwsInstanceTypes(projectId, accountId, region, subnetId, volumeCount, totalVolumeBytes, _options) {
        const responseWithInfo = await this.listAwsInstanceTypesWithHttpInfo(projectId, accountId, region, subnetId, volumeCount, totalVolumeBytes, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsRdsInstanceClassesApiRequestFactory, ListAwsRdsInstanceClassesApiResponseProcessor } from "../apis/ListAwsRdsInstanceClassesApi.js";
export class PromiseListAwsRdsInstanceClassesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsRdsInstanceClassesApiRequestFactory(configuration), responseProcessor = new ListAwsRdsInstanceClassesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsRdsInstanceClassesWithHttpInfo(projectId, engine, engineVersion, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsRdsInstanceClasses(projectId, engine, engineVersion, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsRdsInstanceClassesWithHttpInfo(responseContext);
    }
    async listAwsRdsInstanceClasses(projectId, engine, engineVersion, accountId, region, _options) {
        const responseWithInfo = await this.listAwsRdsInstanceClassesWithHttpInfo(projectId, engine, engineVersion, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsRdsSubnetGroupsApiRequestFactory, ListAwsRdsSubnetGroupsApiResponseProcessor } from "../apis/ListAwsRdsSubnetGroupsApi.js";
export class PromiseListAwsRdsSubnetGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsRdsSubnetGroupsApiRequestFactory(configuration), responseProcessor = new ListAwsRdsSubnetGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsRdsSubnetGroupsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsRdsSubnetGroups(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsRdsSubnetGroupsWithHttpInfo(responseContext);
    }
    async listAwsRdsSubnetGroups(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAwsRdsSubnetGroupsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsSecurityGroupsApiRequestFactory, ListAwsSecurityGroupsApiResponseProcessor } from "../apis/ListAwsSecurityGroupsApi.js";
export class PromiseListAwsSecurityGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsSecurityGroupsApiRequestFactory(configuration), responseProcessor = new ListAwsSecurityGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsSecurityGroupsWithHttpInfo(projectId, accountId, region, vpcId, subnetId, _options) {
        let requestContextValue = await this.requestFactory.listAwsSecurityGroups(projectId, accountId, region, vpcId, subnetId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsSecurityGroupsWithHttpInfo(responseContext);
    }
    async listAwsSecurityGroups(projectId, accountId, region, vpcId, subnetId, _options) {
        const responseWithInfo = await this.listAwsSecurityGroupsWithHttpInfo(projectId, accountId, region, vpcId, subnetId, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsSubnetsApiRequestFactory, ListAwsSubnetsApiResponseProcessor } from "../apis/ListAwsSubnetsApi.js";
export class PromiseListAwsSubnetsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsSubnetsApiRequestFactory(configuration), responseProcessor = new ListAwsSubnetsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsSubnetsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsSubnets(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsSubnetsWithHttpInfo(responseContext);
    }
    async listAwsSubnets(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAwsSubnetsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsSubnetsV2ApiRequestFactory, ListAwsSubnetsV2ApiResponseProcessor } from "../apis/ListAwsSubnetsV2Api.js";
export class PromiseListAwsSubnetsV2Api {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsSubnetsV2ApiRequestFactory(configuration), responseProcessor = new ListAwsSubnetsV2ApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsSubnetsV2WithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsSubnetsV2(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsSubnetsV2WithHttpInfo(responseContext);
    }
    async listAwsSubnetsV2(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAwsSubnetsV2WithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAwsVpcsApiRequestFactory, ListAwsVpcsApiResponseProcessor } from "../apis/ListAwsVpcsApi.js";
export class PromiseListAwsVpcsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAwsVpcsApiRequestFactory(configuration), responseProcessor = new ListAwsVpcsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAwsVpcsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listAwsVpcs(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAwsVpcsWithHttpInfo(responseContext);
    }
    async listAwsVpcs(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listAwsVpcsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureAppRegistrationsApiRequestFactory, ListAzureAppRegistrationsApiResponseProcessor } from "../apis/ListAzureAppRegistrationsApi.js";
export class PromiseListAzureAppRegistrationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureAppRegistrationsApiRequestFactory(configuration), responseProcessor = new ListAzureAppRegistrationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureAppRegistrationsWithHttpInfo(projectId, tenantId, _options) {
        let requestContextValue = await this.requestFactory.listAzureAppRegistrations(projectId, tenantId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureAppRegistrationsWithHttpInfo(responseContext);
    }
    async listAzureAppRegistrations(projectId, tenantId, _options) {
        const responseWithInfo = await this.listAzureAppRegistrationsWithHttpInfo(projectId, tenantId, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureFileSharesApiRequestFactory, ListAzureFileSharesApiResponseProcessor } from "../apis/ListAzureFileSharesApi.js";
export class PromiseListAzureFileSharesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureFileSharesApiRequestFactory(configuration), responseProcessor = new ListAzureFileSharesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureFileSharesWithHttpInfo(projectId, accountId, listAzureFileSharesRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureFileShares(projectId, accountId, listAzureFileSharesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureFileSharesWithHttpInfo(responseContext);
    }
    async listAzureFileShares(projectId, accountId, listAzureFileSharesRequest, _options) {
        const responseWithInfo = await this.listAzureFileSharesWithHttpInfo(projectId, accountId, listAzureFileSharesRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureRegionsApiRequestFactory, ListAzureRegionsApiResponseProcessor } from "../apis/ListAzureRegionsApi.js";
export class PromiseListAzureRegionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureRegionsApiRequestFactory(configuration), responseProcessor = new ListAzureRegionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureRegionsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listAzureRegions(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureRegionsWithHttpInfo(responseContext);
    }
    async listAzureRegions(projectId, _options) {
        const responseWithInfo = await this.listAzureRegionsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureResourceGroupsApiRequestFactory, ListAzureResourceGroupsApiResponseProcessor } from "../apis/ListAzureResourceGroupsApi.js";
export class PromiseListAzureResourceGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureResourceGroupsApiRequestFactory(configuration), responseProcessor = new ListAzureResourceGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureResourceGroupsWithHttpInfo(projectId, accountId, includeAll, _options) {
        let requestContextValue = await this.requestFactory.listAzureResourceGroups(projectId, accountId, includeAll, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureResourceGroupsWithHttpInfo(responseContext);
    }
    async listAzureResourceGroups(projectId, accountId, includeAll, _options) {
        const responseWithInfo = await this.listAzureResourceGroupsWithHttpInfo(projectId, accountId, includeAll, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureSKUsApiRequestFactory, ListAzureSKUsApiResponseProcessor } from "../apis/ListAzureSKUsApi.js";
export class PromiseListAzureSKUsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureSKUsApiRequestFactory(configuration), responseProcessor = new ListAzureSKUsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureSKUsWithHttpInfo(projectId, accountId, listAzureSKUsRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureSKUs(projectId, accountId, listAzureSKUsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureSKUsWithHttpInfo(responseContext);
    }
    async listAzureSKUs(projectId, accountId, listAzureSKUsRequest, _options) {
        const responseWithInfo = await this.listAzureSKUsWithHttpInfo(projectId, accountId, listAzureSKUsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureStorageAccountContainersApiRequestFactory, ListAzureStorageAccountContainersApiResponseProcessor } from "../apis/ListAzureStorageAccountContainersApi.js";
export class PromiseListAzureStorageAccountContainersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureStorageAccountContainersApiRequestFactory(configuration), responseProcessor = new ListAzureStorageAccountContainersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureStorageAccountContainersWithHttpInfo(projectId, accountId, listAzureStorageAccountContainersRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureStorageAccountContainers(projectId, accountId, listAzureStorageAccountContainersRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureStorageAccountContainersWithHttpInfo(responseContext);
    }
    async listAzureStorageAccountContainers(projectId, accountId, listAzureStorageAccountContainersRequest, _options) {
        const responseWithInfo = await this.listAzureStorageAccountContainersWithHttpInfo(projectId, accountId, listAzureStorageAccountContainersRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureStorageAccountsApiRequestFactory, ListAzureStorageAccountsApiResponseProcessor } from "../apis/ListAzureStorageAccountsApi.js";
export class PromiseListAzureStorageAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureStorageAccountsApiRequestFactory(configuration), responseProcessor = new ListAzureStorageAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureStorageAccountsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listAzureStorageAccounts(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureStorageAccountsWithHttpInfo(responseContext);
    }
    async listAzureStorageAccounts(projectId, accountId, _options) {
        const responseWithInfo = await this.listAzureStorageAccountsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureSubnetsApiRequestFactory, ListAzureSubnetsApiResponseProcessor } from "../apis/ListAzureSubnetsApi.js";
export class PromiseListAzureSubnetsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureSubnetsApiRequestFactory(configuration), responseProcessor = new ListAzureSubnetsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureSubnetsWithHttpInfo(projectId, accountId, listAzureSubnetsRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureSubnets(projectId, accountId, listAzureSubnetsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureSubnetsWithHttpInfo(responseContext);
    }
    async listAzureSubnets(projectId, accountId, listAzureSubnetsRequest, _options) {
        const responseWithInfo = await this.listAzureSubnetsWithHttpInfo(projectId, accountId, listAzureSubnetsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureSubscriptionResourceGroupsApiRequestFactory, ListAzureSubscriptionResourceGroupsApiResponseProcessor } from "../apis/ListAzureSubscriptionResourceGroupsApi.js";
export class PromiseListAzureSubscriptionResourceGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureSubscriptionResourceGroupsApiRequestFactory(configuration), responseProcessor = new ListAzureSubscriptionResourceGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureSubscriptionResourceGroupsWithHttpInfo(projectId, subscriptionId, requestId, _options) {
        let requestContextValue = await this.requestFactory.listAzureSubscriptionResourceGroups(projectId, subscriptionId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureSubscriptionResourceGroupsWithHttpInfo(responseContext);
    }
    async listAzureSubscriptionResourceGroups(projectId, subscriptionId, requestId, _options) {
        const responseWithInfo = await this.listAzureSubscriptionResourceGroupsWithHttpInfo(projectId, subscriptionId, requestId, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureUnassignedNetworkInterfacesApiRequestFactory, ListAzureUnassignedNetworkInterfacesApiResponseProcessor } from "../apis/ListAzureUnassignedNetworkInterfacesApi.js";
export class PromiseListAzureUnassignedNetworkInterfacesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureUnassignedNetworkInterfacesApiRequestFactory(configuration), responseProcessor = new ListAzureUnassignedNetworkInterfacesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureUnassignedNetworkInterfacesWithHttpInfo(projectId, accountId, listAzureUnassignedNetworkInterfacesRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureUnassignedNetworkInterfaces(projectId, accountId, listAzureUnassignedNetworkInterfacesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureUnassignedNetworkInterfacesWithHttpInfo(responseContext);
    }
    async listAzureUnassignedNetworkInterfaces(projectId, accountId, listAzureUnassignedNetworkInterfacesRequest, _options) {
        const responseWithInfo = await this.listAzureUnassignedNetworkInterfacesWithHttpInfo(projectId, accountId, listAzureUnassignedNetworkInterfacesRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListAzureVirtualNetworksApiRequestFactory, ListAzureVirtualNetworksApiResponseProcessor } from "../apis/ListAzureVirtualNetworksApi.js";
export class PromiseListAzureVirtualNetworksApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListAzureVirtualNetworksApiRequestFactory(configuration), responseProcessor = new ListAzureVirtualNetworksApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAzureVirtualNetworksWithHttpInfo(projectId, accountId, listAzureVirtualNetworksRequest, _options) {
        let requestContextValue = await this.requestFactory.listAzureVirtualNetworks(projectId, accountId, listAzureVirtualNetworksRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureVirtualNetworksWithHttpInfo(responseContext);
    }
    async listAzureVirtualNetworks(projectId, accountId, listAzureVirtualNetworksRequest, _options) {
        const responseWithInfo = await this.listAzureVirtualNetworksWithHttpInfo(projectId, accountId, listAzureVirtualNetworksRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListBackupJobErrorCodesApiRequestFactory, ListBackupJobErrorCodesApiResponseProcessor } from "../apis/ListBackupJobErrorCodesApi.js";
export class PromiseListBackupJobErrorCodesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListBackupJobErrorCodesApiRequestFactory(configuration), responseProcessor = new ListBackupJobErrorCodesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listBackupJobErrorCodesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listBackupJobErrorCodes(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupJobErrorCodesWithHttpInfo(responseContext);
    }
    async listBackupJobErrorCodes(projectId, _options) {
        const responseWithInfo = await this.listBackupJobErrorCodesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListBackupJobsTypesApiRequestFactory, ListBackupJobsTypesApiResponseProcessor } from "../apis/ListBackupJobsTypesApi.js";
export class PromiseListBackupJobsTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListBackupJobsTypesApiRequestFactory(configuration), responseProcessor = new ListBackupJobsTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listBackupJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listBackupJobsTypes(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listBackupJobsTypesWithHttpInfo(responseContext);
    }
    async listBackupJobsTypes(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listBackupJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListBackupPoliciesApiRequestFactory, ListBackupPoliciesApiResponseProcessor } from "../apis/ListBackupPoliciesApi.js";
export class PromiseListBackupPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListBackupPoliciesApiRequestFactory(configuration), responseProcessor = new ListBackupPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listPoliciesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listPolicies(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listPoliciesWithHttpInfo(responseContext);
    }
    async listPolicies(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listPoliciesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListClassificationCategoriesApiRequestFactory, ListClassificationCategoriesApiResponseProcessor } from "../apis/ListClassificationCategoriesApi.js";
export class PromiseListClassificationCategoriesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListClassificationCategoriesApiRequestFactory(configuration), responseProcessor = new ListClassificationCategoriesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listClassificationCategoriesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listClassificationCategories(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listClassificationCategoriesWithHttpInfo(responseContext);
    }
    async listClassificationCategories(projectId, _options) {
        const responseWithInfo = await this.listClassificationCategoriesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListCloudSnapshotsApiRequestFactory, ListCloudSnapshotsApiResponseProcessor } from "../apis/ListCloudSnapshotsApi.js";
export class PromiseListCloudSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListCloudSnapshotsApiRequestFactory(configuration), responseProcessor = new ListCloudSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listCloudSnapshotsWithHttpInfo(projectId, id, start, end, _options) {
        let requestContextValue = await this.requestFactory.listCloudSnapshots(projectId, id, start, end, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listCloudSnapshotsWithHttpInfo(responseContext);
    }
    async listCloudSnapshots(projectId, id, start, end, _options) {
        const responseWithInfo = await this.listCloudSnapshotsWithHttpInfo(projectId, id, start, end, _options);
        return responseWithInfo.data;
    }
}
import { ListControlViolationsApiRequestFactory, ListControlViolationsApiResponseProcessor } from "../apis/ListControlViolationsApi.js";
export class PromiseListControlViolationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListControlViolationsApiRequestFactory(configuration), responseProcessor = new ListControlViolationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listControlViolationsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listControlViolations(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listControlViolationsWithHttpInfo(responseContext);
    }
    async listControlViolations(projectId, id, _options) {
        const responseWithInfo = await this.listControlViolationsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListControlsApiRequestFactory, ListControlsApiResponseProcessor } from "../apis/ListControlsApi.js";
export class PromiseListControlsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListControlsApiRequestFactory(configuration), responseProcessor = new ListControlsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listControlsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listControls(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listControlsWithHttpInfo(responseContext);
    }
    async listControls(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listControlsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListCredentialFindingsApiRequestFactory, ListCredentialFindingsApiResponseProcessor } from "../apis/ListCredentialFindingsApi.js";
export class PromiseListCredentialFindingsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListCredentialFindingsApiRequestFactory(configuration), responseProcessor = new ListCredentialFindingsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listCredentialFindingsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listCredentialFindings(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listCredentialFindingsWithHttpInfo(responseContext);
    }
    async listCredentialFindings(projectId, id, _options) {
        const responseWithInfo = await this.listCredentialFindingsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListDailySnapshotsApiRequestFactory, ListDailySnapshotsApiResponseProcessor } from "../apis/ListDailySnapshotsApi.js";
export class PromiseListDailySnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListDailySnapshotsApiRequestFactory(configuration), responseProcessor = new ListDailySnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listDailySnapshotsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listDailySnapshots(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listDailySnapshotsWithHttpInfo(responseContext);
    }
    async listDailySnapshots(projectId, id, _options) {
        const responseWithInfo = await this.listDailySnapshotsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListDataClassificationEntitiesApiRequestFactory, ListDataClassificationEntitiesApiResponseProcessor } from "../apis/ListDataClassificationEntitiesApi.js";
export class PromiseListDataClassificationEntitiesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListDataClassificationEntitiesApiRequestFactory(configuration), responseProcessor = new ListDataClassificationEntitiesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listDataClassificationEntitiesWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listDataClassificationEntities(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listDataClassificationEntitiesWithHttpInfo(responseContext);
    }
    async listDataClassificationEntities(projectId, id, _options) {
        const responseWithInfo = await this.listDataClassificationEntitiesWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListDocumentdbClusterParameterGroupsApiRequestFactory, ListDocumentdbClusterParameterGroupsApiResponseProcessor } from "../apis/ListDocumentdbClusterParameterGroupsApi.js";
export class PromiseListDocumentdbClusterParameterGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListDocumentdbClusterParameterGroupsApiRequestFactory(configuration), responseProcessor = new ListDocumentdbClusterParameterGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listDocumentdbClusterParameterGroupsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listDocumentdbClusterParameterGroups(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listDocumentdbClusterParameterGroupsWithHttpInfo(responseContext);
    }
    async listDocumentdbClusterParameterGroups(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listDocumentdbClusterParameterGroupsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListDynamoDbBackupConfigsApiRequestFactory, ListDynamoDbBackupConfigsApiResponseProcessor } from "../apis/ListDynamoDbBackupConfigsApi.js";
export class PromiseListDynamoDbBackupConfigsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListDynamoDbBackupConfigsApiRequestFactory(configuration), responseProcessor = new ListDynamoDbBackupConfigsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listDynamoDbBackupConfigsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listDynamoDbBackupConfigs(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listDynamoDbBackupConfigsWithHttpInfo(responseContext);
    }
    async listDynamoDbBackupConfigs(projectId, _options) {
        const responseWithInfo = await this.listDynamoDbBackupConfigsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListEncryptionKeysApiRequestFactory, ListEncryptionKeysApiResponseProcessor } from "../apis/ListEncryptionKeysApi.js";
export class PromiseListEncryptionKeysApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListEncryptionKeysApiRequestFactory(configuration), responseProcessor = new ListEncryptionKeysApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listEncryptionKeysWithHttpInfo(projectId, accountId, region, includeAwsManagedKeys, _options) {
        let requestContextValue = await this.requestFactory.listEncryptionKeys(projectId, accountId, region, includeAwsManagedKeys, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listEncryptionKeysWithHttpInfo(responseContext);
    }
    async listEncryptionKeys(projectId, accountId, region, includeAwsManagedKeys, _options) {
        const responseWithInfo = await this.listEncryptionKeysWithHttpInfo(projectId, accountId, region, includeAwsManagedKeys, _options);
        return responseWithInfo.data;
    }
}
import { ListExclusionPatternResourcesApiRequestFactory, ListExclusionPatternResourcesApiResponseProcessor } from "../apis/ListExclusionPatternResourcesApi.js";
export class PromiseListExclusionPatternResourcesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListExclusionPatternResourcesApiRequestFactory(configuration), responseProcessor = new ListExclusionPatternResourcesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listExclusionPatternResourcesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listExclusionPatternResources(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listExclusionPatternResourcesWithHttpInfo(responseContext);
    }
    async listExclusionPatternResources(projectId, _options) {
        const responseWithInfo = await this.listExclusionPatternResourcesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListFindingExclusionPatternsApiRequestFactory, ListFindingExclusionPatternsApiResponseProcessor } from "../apis/ListFindingExclusionPatternsApi.js";
export class PromiseListFindingExclusionPatternsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListFindingExclusionPatternsApiRequestFactory(configuration), responseProcessor = new ListFindingExclusionPatternsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listFindingExclusionPatternsWithHttpInfo(projectId, pageSize, pageToken, listFindingExclusionPatternsRequest, _options) {
        let requestContextValue = await this.requestFactory.listFindingExclusionPatterns(projectId, pageSize, pageToken, listFindingExclusionPatternsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listFindingExclusionPatternsWithHttpInfo(responseContext);
    }
    async listFindingExclusionPatterns(projectId, pageSize, pageToken, listFindingExclusionPatternsRequest, _options) {
        const responseWithInfo = await this.listFindingExclusionPatternsWithHttpInfo(projectId, pageSize, pageToken, listFindingExclusionPatternsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListGCSBucketsApiRequestFactory, ListGCSBucketsApiResponseProcessor } from "../apis/ListGCSBucketsApi.js";
export class PromiseListGCSBucketsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGCSBucketsApiRequestFactory(configuration), responseProcessor = new ListGCSBucketsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGCSBucketsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listGCSBuckets(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGCSBucketsWithHttpInfo(responseContext);
    }
    async listGCSBuckets(projectId, accountId, _options) {
        const responseWithInfo = await this.listGCSBucketsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpDiskTypesApiRequestFactory, ListGcpDiskTypesApiResponseProcessor } from "../apis/ListGcpDiskTypesApi.js";
export class PromiseListGcpDiskTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpDiskTypesApiRequestFactory(configuration), responseProcessor = new ListGcpDiskTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpDiskTypesWithHttpInfo(projectId, accountId, zone, machineType, _options) {
        let requestContextValue = await this.requestFactory.listGcpDiskTypes(projectId, accountId, zone, machineType, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpDiskTypesWithHttpInfo(responseContext);
    }
    async listGcpDiskTypes(projectId, accountId, zone, machineType, _options) {
        const responseWithInfo = await this.listGcpDiskTypesWithHttpInfo(projectId, accountId, zone, machineType, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpEncryptionKeysApiRequestFactory, ListGcpEncryptionKeysApiResponseProcessor } from "../apis/ListGcpEncryptionKeysApi.js";
export class PromiseListGcpEncryptionKeysApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpEncryptionKeysApiRequestFactory(configuration), responseProcessor = new ListGcpEncryptionKeysApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpEncryptionKeysWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listGcpEncryptionKeys(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpEncryptionKeysWithHttpInfo(responseContext);
    }
    async listGcpEncryptionKeys(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listGcpEncryptionKeysWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpMachineTypesApiRequestFactory, ListGcpMachineTypesApiResponseProcessor } from "../apis/ListGcpMachineTypesApi.js";
export class PromiseListGcpMachineTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpMachineTypesApiRequestFactory(configuration), responseProcessor = new ListGcpMachineTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpMachineTypesWithHttpInfo(projectId, accountId, zone, _options) {
        let requestContextValue = await this.requestFactory.listGcpMachineTypes(projectId, accountId, zone, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpMachineTypesWithHttpInfo(responseContext);
    }
    async listGcpMachineTypes(projectId, accountId, zone, _options) {
        const responseWithInfo = await this.listGcpMachineTypesWithHttpInfo(projectId, accountId, zone, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpNetworksApiRequestFactory, ListGcpNetworksApiResponseProcessor } from "../apis/ListGcpNetworksApi.js";
export class PromiseListGcpNetworksApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpNetworksApiRequestFactory(configuration), responseProcessor = new ListGcpNetworksApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpNetworksWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listGcpNetworks(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpNetworksWithHttpInfo(responseContext);
    }
    async listGcpNetworks(projectId, accountId, _options) {
        const responseWithInfo = await this.listGcpNetworksWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpSharedNetworksApiRequestFactory, ListGcpSharedNetworksApiResponseProcessor } from "../apis/ListGcpSharedNetworksApi.js";
export class PromiseListGcpSharedNetworksApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpSharedNetworksApiRequestFactory(configuration), responseProcessor = new ListGcpSharedNetworksApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpSharedNetworksWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listGcpSharedNetworks(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpSharedNetworksWithHttpInfo(responseContext);
    }
    async listGcpSharedNetworks(projectId, accountId, _options) {
        const responseWithInfo = await this.listGcpSharedNetworksWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpSharedSubnetsApiRequestFactory, ListGcpSharedSubnetsApiResponseProcessor } from "../apis/ListGcpSharedSubnetsApi.js";
export class PromiseListGcpSharedSubnetsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpSharedSubnetsApiRequestFactory(configuration), responseProcessor = new ListGcpSharedSubnetsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpSharedSubnetsWithHttpInfo(projectId, accountId, region, network, _options) {
        let requestContextValue = await this.requestFactory.listGcpSharedSubnets(projectId, accountId, region, network, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpSharedSubnetsWithHttpInfo(responseContext);
    }
    async listGcpSharedSubnets(projectId, accountId, region, network, _options) {
        const responseWithInfo = await this.listGcpSharedSubnetsWithHttpInfo(projectId, accountId, region, network, _options);
        return responseWithInfo.data;
    }
}
import { ListGcpSubnetsApiRequestFactory, ListGcpSubnetsApiResponseProcessor } from "../apis/ListGcpSubnetsApi.js";
export class PromiseListGcpSubnetsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGcpSubnetsApiRequestFactory(configuration), responseProcessor = new ListGcpSubnetsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGcpSubnetsWithHttpInfo(projectId, accountId, region, network, _options) {
        let requestContextValue = await this.requestFactory.listGcpSubnets(projectId, accountId, region, network, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGcpSubnetsWithHttpInfo(responseContext);
    }
    async listGcpSubnets(projectId, accountId, region, network, _options) {
        const responseWithInfo = await this.listGcpSubnetsWithHttpInfo(projectId, accountId, region, network, _options);
        return responseWithInfo.data;
    }
}
import { ListGoogleWorkspaceGroupsApiRequestFactory, ListGoogleWorkspaceGroupsApiResponseProcessor } from "../apis/ListGoogleWorkspaceGroupsApi.js";
export class PromiseListGoogleWorkspaceGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGoogleWorkspaceGroupsApiRequestFactory(configuration), responseProcessor = new ListGoogleWorkspaceGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGoogleWorkspaceGroupsWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGoogleWorkspaceGroups(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGoogleWorkspaceGroupsWithHttpInfo(responseContext);
    }
    async listGoogleWorkspaceGroups(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGoogleWorkspaceGroupsWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListGoogleWorkspaceOrgUnitsApiRequestFactory, ListGoogleWorkspaceOrgUnitsApiResponseProcessor } from "../apis/ListGoogleWorkspaceOrgUnitsApi.js";
export class PromiseListGoogleWorkspaceOrgUnitsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGoogleWorkspaceOrgUnitsApiRequestFactory(configuration), responseProcessor = new ListGoogleWorkspaceOrgUnitsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGoogleWorkspaceOrgUnitsWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGoogleWorkspaceOrgUnits(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGoogleWorkspaceOrgUnitsWithHttpInfo(responseContext);
    }
    async listGoogleWorkspaceOrgUnits(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGoogleWorkspaceOrgUnitsWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListGoogleWorkspaceSharedDrivesApiRequestFactory, ListGoogleWorkspaceSharedDrivesApiResponseProcessor } from "../apis/ListGoogleWorkspaceSharedDrivesApi.js";
export class PromiseListGoogleWorkspaceSharedDrivesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGoogleWorkspaceSharedDrivesApiRequestFactory(configuration), responseProcessor = new ListGoogleWorkspaceSharedDrivesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGoogleWorkspaceSharedDrivesWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGoogleWorkspaceSharedDrives(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGoogleWorkspaceSharedDrivesWithHttpInfo(responseContext);
    }
    async listGoogleWorkspaceSharedDrives(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGoogleWorkspaceSharedDrivesWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListGoogleWorkspaceUsersApiRequestFactory, ListGoogleWorkspaceUsersApiResponseProcessor } from "../apis/ListGoogleWorkspaceUsersApi.js";
export class PromiseListGoogleWorkspaceUsersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGoogleWorkspaceUsersApiRequestFactory(configuration), responseProcessor = new ListGoogleWorkspaceUsersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGoogleWorkspaceUsersWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGoogleWorkspaceUsers(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGoogleWorkspaceUsersWithHttpInfo(responseContext);
    }
    async listGoogleWorkspaceUsers(projectId, googleWorkspaceId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listGoogleWorkspaceUsersWithHttpInfo(projectId, googleWorkspaceId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListGroupConversationsApiRequestFactory, ListGroupConversationsApiResponseProcessor } from "../apis/ListGroupConversationsApi.js";
export class PromiseListGroupConversationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListGroupConversationsApiRequestFactory(configuration), responseProcessor = new ListGroupConversationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listGroupConversationsWithHttpInfo(projectId, resourceId, listGroupConversationsRequest, pageIndex, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listGroupConversations(projectId, resourceId, listGroupConversationsRequest, pageIndex, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listGroupConversationsWithHttpInfo(responseContext);
    }
    async listGroupConversations(projectId, resourceId, listGroupConversationsRequest, pageIndex, pageSize, _options) {
        const responseWithInfo = await this.listGroupConversationsWithHttpInfo(projectId, resourceId, listGroupConversationsRequest, pageIndex, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryApiRequestFactory, ListInventoryApiResponseProcessor } from "../apis/ListInventoryApi.js";
export class PromiseListInventoryApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryApiRequestFactory(configuration), responseProcessor = new ListInventoryApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async inventoryDownloadWithHttpInfo(projectId, filter, sort, columns, _options) {
        let requestContextValue = await this.requestFactory.inventoryDownload(projectId, filter, sort, columns, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.inventoryDownloadWithHttpInfo(responseContext);
    }
    async inventoryDownload(projectId, filter, sort, columns, _options) {
        const responseWithInfo = await this.inventoryDownloadWithHttpInfo(projectId, filter, sort, columns, _options);
        return responseWithInfo.data;
    }
    async listInventoryWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventory(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryWithHttpInfo(responseContext);
    }
    async listInventory(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listInventoryWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryAppsApiRequestFactory, ListInventoryAppsApiResponseProcessor } from "../apis/ListInventoryAppsApi.js";
export class PromiseListInventoryAppsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryAppsApiRequestFactory(configuration), responseProcessor = new ListInventoryAppsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryAppsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventoryApps(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryAppsWithHttpInfo(responseContext);
    }
    async listInventoryApps(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventoryAppsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryBackupRegionsApiRequestFactory, ListInventoryBackupRegionsApiResponseProcessor } from "../apis/ListInventoryBackupRegionsApi.js";
export class PromiseListInventoryBackupRegionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryBackupRegionsApiRequestFactory(configuration), responseProcessor = new ListInventoryBackupRegionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryBackupRegionsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listInventoryBackupRegions(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryBackupRegionsWithHttpInfo(responseContext);
    }
    async listInventoryBackupRegions(projectId, _options) {
        const responseWithInfo = await this.listInventoryBackupRegionsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryClassificationEntitiesApiRequestFactory, ListInventoryClassificationEntitiesApiResponseProcessor } from "../apis/ListInventoryClassificationEntitiesApi.js";
export class PromiseListInventoryClassificationEntitiesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryClassificationEntitiesApiRequestFactory(configuration), responseProcessor = new ListInventoryClassificationEntitiesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryClassificationEntitiesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listInventoryClassificationEntities(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryClassificationEntitiesWithHttpInfo(responseContext);
    }
    async listInventoryClassificationEntities(projectId, _options) {
        const responseWithInfo = await this.listInventoryClassificationEntitiesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryEnvironmentsApiRequestFactory, ListInventoryEnvironmentsApiResponseProcessor } from "../apis/ListInventoryEnvironmentsApi.js";
export class PromiseListInventoryEnvironmentsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryEnvironmentsApiRequestFactory(configuration), responseProcessor = new ListInventoryEnvironmentsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryEnvironmentsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listInventoryEnvironments(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryEnvironmentsWithHttpInfo(responseContext);
    }
    async listInventoryEnvironments(projectId, _options) {
        const responseWithInfo = await this.listInventoryEnvironmentsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryGlobalDatabasesApiRequestFactory, ListInventoryGlobalDatabasesApiResponseProcessor } from "../apis/ListInventoryGlobalDatabasesApi.js";
export class PromiseListInventoryGlobalDatabasesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryGlobalDatabasesApiRequestFactory(configuration), responseProcessor = new ListInventoryGlobalDatabasesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryGlobalDatabasesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventoryGlobalDatabases(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryGlobalDatabasesWithHttpInfo(responseContext);
    }
    async listInventoryGlobalDatabases(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventoryGlobalDatabasesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryItemSnapshotsApiRequestFactory, ListInventoryItemSnapshotsApiResponseProcessor } from "../apis/ListInventoryItemSnapshotsApi.js";
export class PromiseListInventoryItemSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryItemSnapshotsApiRequestFactory(configuration), responseProcessor = new ListInventoryItemSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryItemSnapshotsWithHttpInfo(projectId, id, pageIndex, pageSize, snapshotType, start, end, _options) {
        let requestContextValue = await this.requestFactory.listInventoryItemSnapshots(projectId, id, pageIndex, pageSize, snapshotType, start, end, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryItemSnapshotsWithHttpInfo(responseContext);
    }
    async listInventoryItemSnapshots(projectId, id, pageIndex, pageSize, snapshotType, start, end, _options) {
        const responseWithInfo = await this.listInventoryItemSnapshotsWithHttpInfo(projectId, id, pageIndex, pageSize, snapshotType, start, end, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryNetworksApiRequestFactory, ListInventoryNetworksApiResponseProcessor } from "../apis/ListInventoryNetworksApi.js";
export class PromiseListInventoryNetworksApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryNetworksApiRequestFactory(configuration), responseProcessor = new ListInventoryNetworksApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryNetworksWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventoryNetworks(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryNetworksWithHttpInfo(responseContext);
    }
    async listInventoryNetworks(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventoryNetworksWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryResourceGroupsApiRequestFactory, ListInventoryResourceGroupsApiResponseProcessor } from "../apis/ListInventoryResourceGroupsApi.js";
export class PromiseListInventoryResourceGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryResourceGroupsApiRequestFactory(configuration), responseProcessor = new ListInventoryResourceGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryResourceGroupsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventoryResourceGroups(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryResourceGroupsWithHttpInfo(responseContext);
    }
    async listInventoryResourceGroups(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventoryResourceGroupsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryResourceIdentifiersApiRequestFactory, ListInventoryResourceIdentifiersApiResponseProcessor } from "../apis/ListInventoryResourceIdentifiersApi.js";
export class PromiseListInventoryResourceIdentifiersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryResourceIdentifiersApiRequestFactory(configuration), responseProcessor = new ListInventoryResourceIdentifiersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryResourceIdentifiersWithHttpInfo(projectId, pageIndex, pageSize, filter, includeSaas, _options) {
        let requestContextValue = await this.requestFactory.listInventoryResourceIdentifiers(projectId, pageIndex, pageSize, filter, includeSaas, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryResourceIdentifiersWithHttpInfo(responseContext);
    }
    async listInventoryResourceIdentifiers(projectId, pageIndex, pageSize, filter, includeSaas, _options) {
        const responseWithInfo = await this.listInventoryResourceIdentifiersWithHttpInfo(projectId, pageIndex, pageSize, filter, includeSaas, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryResourceTypesApiRequestFactory, ListInventoryResourceTypesApiResponseProcessor } from "../apis/ListInventoryResourceTypesApi.js";
export class PromiseListInventoryResourceTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryResourceTypesApiRequestFactory(configuration), responseProcessor = new ListInventoryResourceTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryResourceTypesWithHttpInfo(projectId, includeSaas, _options) {
        let requestContextValue = await this.requestFactory.listInventoryResourceTypes(projectId, includeSaas, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryResourceTypesWithHttpInfo(responseContext);
    }
    async listInventoryResourceTypes(projectId, includeSaas, _options) {
        const responseWithInfo = await this.listInventoryResourceTypesWithHttpInfo(projectId, includeSaas, _options);
        return responseWithInfo.data;
    }
}
import { ListInventorySensitivityAnnotationValuesApiRequestFactory, ListInventorySensitivityAnnotationValuesApiResponseProcessor } from "../apis/ListInventorySensitivityAnnotationValuesApi.js";
export class PromiseListInventorySensitivityAnnotationValuesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventorySensitivityAnnotationValuesApiRequestFactory(configuration), responseProcessor = new ListInventorySensitivityAnnotationValuesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventorySensitivityAnnotationValuesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listInventorySensitivityAnnotationValues(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventorySensitivityAnnotationValuesWithHttpInfo(responseContext);
    }
    async listInventorySensitivityAnnotationValues(projectId, _options) {
        const responseWithInfo = await this.listInventorySensitivityAnnotationValuesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListInventorySourceAccountTagsApiRequestFactory, ListInventorySourceAccountTagsApiResponseProcessor } from "../apis/ListInventorySourceAccountTagsApi.js";
export class PromiseListInventorySourceAccountTagsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventorySourceAccountTagsApiRequestFactory(configuration), responseProcessor = new ListInventorySourceAccountTagsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventorySourceAccountTagsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventorySourceAccountTags(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventorySourceAccountTagsWithHttpInfo(responseContext);
    }
    async listInventorySourceAccountTags(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventorySourceAccountTagsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventorySourceRegionsApiRequestFactory, ListInventorySourceRegionsApiResponseProcessor } from "../apis/ListInventorySourceRegionsApi.js";
export class PromiseListInventorySourceRegionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventorySourceRegionsApiRequestFactory(configuration), responseProcessor = new ListInventorySourceRegionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventorySourceRegionsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventorySourceRegions(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventorySourceRegionsWithHttpInfo(responseContext);
    }
    async listInventorySourceRegions(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventorySourceRegionsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventorySubnetsApiRequestFactory, ListInventorySubnetsApiResponseProcessor } from "../apis/ListInventorySubnetsApi.js";
export class PromiseListInventorySubnetsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventorySubnetsApiRequestFactory(configuration), responseProcessor = new ListInventorySubnetsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventorySubnetsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventorySubnets(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventorySubnetsWithHttpInfo(responseContext);
    }
    async listInventorySubnets(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventorySubnetsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListInventoryTagsApiRequestFactory, ListInventoryTagsApiResponseProcessor } from "../apis/ListInventoryTagsApi.js";
export class PromiseListInventoryTagsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListInventoryTagsApiRequestFactory(configuration), responseProcessor = new ListInventoryTagsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listInventoryTagsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listInventoryTags(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInventoryTagsWithHttpInfo(responseContext);
    }
    async listInventoryTags(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listInventoryTagsWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListKeyspacesBackupConfigsApiRequestFactory, ListKeyspacesBackupConfigsApiResponseProcessor } from "../apis/ListKeyspacesBackupConfigsApi.js";
export class PromiseListKeyspacesBackupConfigsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListKeyspacesBackupConfigsApiRequestFactory(configuration), responseProcessor = new ListKeyspacesBackupConfigsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listKeyspacesBackupConfigsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listKeyspacesBackupConfigs(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listKeyspacesBackupConfigsWithHttpInfo(responseContext);
    }
    async listKeyspacesBackupConfigs(projectId, _options) {
        const responseWithInfo = await this.listKeyspacesBackupConfigsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListKnownAppsApiRequestFactory, ListKnownAppsApiResponseProcessor } from "../apis/ListKnownAppsApi.js";
export class PromiseListKnownAppsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListKnownAppsApiRequestFactory(configuration), responseProcessor = new ListKnownAppsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listKnownAppsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listKnownApps(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listKnownAppsWithHttpInfo(responseContext);
    }
    async listKnownApps(projectId, _options) {
        const responseWithInfo = await this.listKnownAppsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListMicrosoft365GroupMembersApiRequestFactory, ListMicrosoft365GroupMembersApiResponseProcessor } from "../apis/ListMicrosoft365GroupMembersApi.js";
export class PromiseListMicrosoft365GroupMembersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMicrosoft365GroupMembersApiRequestFactory(configuration), responseProcessor = new ListMicrosoft365GroupMembersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMicrosoft365GroupMembersWithHttpInfo(projectId, microsoft365Id, groupId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listMicrosoft365GroupMembers(projectId, microsoft365Id, groupId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMicrosoft365GroupMembersWithHttpInfo(responseContext);
    }
    async listMicrosoft365GroupMembers(projectId, microsoft365Id, groupId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listMicrosoft365GroupMembersWithHttpInfo(projectId, microsoft365Id, groupId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { ListMicrosoft365GroupsApiRequestFactory, ListMicrosoft365GroupsApiResponseProcessor } from "../apis/ListMicrosoft365GroupsApi.js";
export class PromiseListMicrosoft365GroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMicrosoft365GroupsApiRequestFactory(configuration), responseProcessor = new ListMicrosoft365GroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMicrosoft365GroupsWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365GroupsRequest, _options) {
        let requestContextValue = await this.requestFactory.listMicrosoft365Groups(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365GroupsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMicrosoft365GroupsWithHttpInfo(responseContext);
    }
    async listMicrosoft365Groups(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365GroupsRequest, _options) {
        const responseWithInfo = await this.listMicrosoft365GroupsWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365GroupsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListMicrosoft365SharePointSitesApiRequestFactory, ListMicrosoft365SharePointSitesApiResponseProcessor } from "../apis/ListMicrosoft365SharePointSitesApi.js";
export class PromiseListMicrosoft365SharePointSitesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMicrosoft365SharePointSitesApiRequestFactory(configuration), responseProcessor = new ListMicrosoft365SharePointSitesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMicrosoft365SharePointSitesWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365SharePointSitesRequest, _options) {
        let requestContextValue = await this.requestFactory.listMicrosoft365SharePointSites(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365SharePointSitesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMicrosoft365SharePointSitesWithHttpInfo(responseContext);
    }
    async listMicrosoft365SharePointSites(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365SharePointSitesRequest, _options) {
        const responseWithInfo = await this.listMicrosoft365SharePointSitesWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365SharePointSitesRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListMicrosoft365UsersApiRequestFactory, ListMicrosoft365UsersApiResponseProcessor } from "../apis/ListMicrosoft365UsersApi.js";
export class PromiseListMicrosoft365UsersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMicrosoft365UsersApiRequestFactory(configuration), responseProcessor = new ListMicrosoft365UsersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMicrosoft365UsersWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365UsersRequest, _options) {
        let requestContextValue = await this.requestFactory.listMicrosoft365Users(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365UsersRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMicrosoft365UsersWithHttpInfo(responseContext);
    }
    async listMicrosoft365Users(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365UsersRequest, _options) {
        const responseWithInfo = await this.listMicrosoft365UsersWithHttpInfo(projectId, microsoft365Id, pageToken, pageSize, listMicrosoft365UsersRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListMongoAtlasPrivateLinksApiRequestFactory, ListMongoAtlasPrivateLinksApiResponseProcessor } from "../apis/ListMongoAtlasPrivateLinksApi.js";
export class PromiseListMongoAtlasPrivateLinksApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMongoAtlasPrivateLinksApiRequestFactory(configuration), responseProcessor = new ListMongoAtlasPrivateLinksApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMongoAtlasPrivateLinksWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listMongoAtlasPrivateLinks(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMongoAtlasPrivateLinksWithHttpInfo(responseContext);
    }
    async listMongoAtlasPrivateLinks(projectId, accountId, _options) {
        const responseWithInfo = await this.listMongoAtlasPrivateLinksWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListMongoDbBackupConfigsApiRequestFactory, ListMongoDbBackupConfigsApiResponseProcessor } from "../apis/ListMongoDbBackupConfigsApi.js";
export class PromiseListMongoDbBackupConfigsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListMongoDbBackupConfigsApiRequestFactory(configuration), responseProcessor = new ListMongoDbBackupConfigsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listMongoDbBackupConfigsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listMongoDbBackupConfigs(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listMongoDbBackupConfigsWithHttpInfo(responseContext);
    }
    async listMongoDbBackupConfigs(projectId, _options) {
        const responseWithInfo = await this.listMongoDbBackupConfigsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListNeptuneClusterParameterGroupsApiRequestFactory, ListNeptuneClusterParameterGroupsApiResponseProcessor } from "../apis/ListNeptuneClusterParameterGroupsApi.js";
export class PromiseListNeptuneClusterParameterGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListNeptuneClusterParameterGroupsApiRequestFactory(configuration), responseProcessor = new ListNeptuneClusterParameterGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listNeptuneClusterParameterGroupsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listNeptuneClusterParameterGroups(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listNeptuneClusterParameterGroupsWithHttpInfo(responseContext);
    }
    async listNeptuneClusterParameterGroups(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listNeptuneClusterParameterGroupsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListObjectStoreExclusionRulesApiRequestFactory, ListObjectStoreExclusionRulesApiResponseProcessor } from "../apis/ListObjectStoreExclusionRulesApi.js";
export class PromiseListObjectStoreExclusionRulesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListObjectStoreExclusionRulesApiRequestFactory(configuration), responseProcessor = new ListObjectStoreExclusionRulesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listObjectStoreExclusionRulesWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listObjectStoreExclusionRules(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listObjectStoreExclusionRulesWithHttpInfo(responseContext);
    }
    async listObjectStoreExclusionRules(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listObjectStoreExclusionRulesWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListPathSnapshotsApiRequestFactory, ListPathSnapshotsApiResponseProcessor } from "../apis/ListPathSnapshotsApi.js";
export class PromiseListPathSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListPathSnapshotsApiRequestFactory(configuration), responseProcessor = new ListPathSnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listPathSnapshotsWithHttpInfo(projectId, resourceId, start, end, pageIndex, pageSize, pathSnapshotsRequest, _options) {
        let requestContextValue = await this.requestFactory.listPathSnapshots(projectId, resourceId, start, end, pageIndex, pageSize, pathSnapshotsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listPathSnapshotsWithHttpInfo(responseContext);
    }
    async listPathSnapshots(projectId, resourceId, start, end, pageIndex, pageSize, pathSnapshotsRequest, _options) {
        const responseWithInfo = await this.listPathSnapshotsWithHttpInfo(projectId, resourceId, start, end, pageIndex, pageSize, pathSnapshotsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ListProjectScanningAccountsApiRequestFactory, ListProjectScanningAccountsApiResponseProcessor } from "../apis/ListProjectScanningAccountsApi.js";
export class PromiseListProjectScanningAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListProjectScanningAccountsApiRequestFactory(configuration), responseProcessor = new ListProjectScanningAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listProjectScanningAccountsWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listProjectScanningAccounts(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listProjectScanningAccountsWithHttpInfo(responseContext);
    }
    async listProjectScanningAccounts(projectId, _options) {
        const responseWithInfo = await this.listProjectScanningAccountsWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListProjectSourceAccountsApiRequestFactory, ListProjectSourceAccountsApiResponseProcessor } from "../apis/ListProjectSourceAccountsApi.js";
export class PromiseListProjectSourceAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListProjectSourceAccountsApiRequestFactory(configuration), responseProcessor = new ListProjectSourceAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listProjectSourceAccountsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listProjectSourceAccounts(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listProjectSourceAccountsWithHttpInfo(responseContext);
    }
    async listProjectSourceAccounts(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listProjectSourceAccountsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListProjectUsersApiRequestFactory, ListProjectUsersApiResponseProcessor } from "../apis/ListProjectUsersApi.js";
export class PromiseListProjectUsersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListProjectUsersApiRequestFactory(configuration), responseProcessor = new ListProjectUsersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listProjectUsersWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listProjectUsers(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listProjectUsersWithHttpInfo(responseContext);
    }
    async listProjectUsers(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listProjectUsersWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListRestoreJobErrorCodesApiRequestFactory, ListRestoreJobErrorCodesApiResponseProcessor } from "../apis/ListRestoreJobErrorCodesApi.js";
export class PromiseListRestoreJobErrorCodesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListRestoreJobErrorCodesApiRequestFactory(configuration), responseProcessor = new ListRestoreJobErrorCodesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listRestoreJobErrorCodesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listRestoreJobErrorCodes(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreJobErrorCodesWithHttpInfo(responseContext);
    }
    async listRestoreJobErrorCodes(projectId, _options) {
        const responseWithInfo = await this.listRestoreJobErrorCodesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListRestoreJobsTypesApiRequestFactory, ListRestoreJobsTypesApiResponseProcessor } from "../apis/ListRestoreJobsTypesApi.js";
export class PromiseListRestoreJobsTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListRestoreJobsTypesApiRequestFactory(configuration), responseProcessor = new ListRestoreJobsTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listRestoreJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listRestoreJobsTypes(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreJobsTypesWithHttpInfo(responseContext);
    }
    async listRestoreJobsTypes(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listRestoreJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListRestoreRegionsApiRequestFactory, ListRestoreRegionsApiResponseProcessor } from "../apis/ListRestoreRegionsApi.js";
export class PromiseListRestoreRegionsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListRestoreRegionsApiRequestFactory(configuration), responseProcessor = new ListRestoreRegionsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listRestoreRegionsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listRestoreRegions(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreRegionsWithHttpInfo(responseContext);
    }
    async listRestoreRegions(projectId, accountId, _options) {
        const responseWithInfo = await this.listRestoreRegionsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListS3BucketsApiRequestFactory, ListS3BucketsApiResponseProcessor } from "../apis/ListS3BucketsApi.js";
export class PromiseListS3BucketsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListS3BucketsApiRequestFactory(configuration), responseProcessor = new ListS3BucketsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listS3BucketsWithHttpInfo(projectId, accountId, _options) {
        let requestContextValue = await this.requestFactory.listS3Buckets(projectId, accountId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listS3BucketsWithHttpInfo(responseContext);
    }
    async listS3Buckets(projectId, accountId, _options) {
        const responseWithInfo = await this.listS3BucketsWithHttpInfo(projectId, accountId, _options);
        return responseWithInfo.data;
    }
}
import { ListSamlIdentityProviderConfigsApiRequestFactory, ListSamlIdentityProviderConfigsApiResponseProcessor } from "../apis/ListSamlIdentityProviderConfigsApi.js";
export class PromiseListSamlIdentityProviderConfigsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListSamlIdentityProviderConfigsApiRequestFactory(configuration), responseProcessor = new ListSamlIdentityProviderConfigsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listSamlIdentityProviderConfigsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listSamlIdentityProviderConfigs(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSamlIdentityProviderConfigsWithHttpInfo(responseContext);
    }
    async listSamlIdentityProviderConfigs(_options) {
        const responseWithInfo = await this.listSamlIdentityProviderConfigsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListScanJobErrorCodesApiRequestFactory, ListScanJobErrorCodesApiResponseProcessor } from "../apis/ListScanJobErrorCodesApi.js";
export class PromiseListScanJobErrorCodesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListScanJobErrorCodesApiRequestFactory(configuration), responseProcessor = new ListScanJobErrorCodesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listScanJobErrorCodesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listScanJobErrorCodes(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listScanJobErrorCodesWithHttpInfo(responseContext);
    }
    async listScanJobErrorCodes(projectId, _options) {
        const responseWithInfo = await this.listScanJobErrorCodesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { ListScanJobsTypesApiRequestFactory, ListScanJobsTypesApiResponseProcessor } from "../apis/ListScanJobsTypesApi.js";
export class PromiseListScanJobsTypesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListScanJobsTypesApiRequestFactory(configuration), responseProcessor = new ListScanJobsTypesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listScanJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options) {
        let requestContextValue = await this.requestFactory.listScanJobsTypes(projectId, pageIndex, pageSize, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listScanJobsTypesWithHttpInfo(responseContext);
    }
    async listScanJobsTypes(projectId, pageIndex, pageSize, filter, _options) {
        const responseWithInfo = await this.listScanJobsTypesWithHttpInfo(projectId, pageIndex, pageSize, filter, _options);
        return responseWithInfo.data;
    }
}
import { ListScanningAccountsApiRequestFactory, ListScanningAccountsApiResponseProcessor } from "../apis/ListScanningAccountsApi.js";
export class PromiseListScanningAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListScanningAccountsApiRequestFactory(configuration), responseProcessor = new ListScanningAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listScanningAccountsWithHttpInfo(sort, _options) {
        let requestContextValue = await this.requestFactory.listScanningAccounts(sort, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listScanningAccountsWithHttpInfo(responseContext);
    }
    async listScanningAccounts(sort, _options) {
        const responseWithInfo = await this.listScanningAccountsWithHttpInfo(sort, _options);
        return responseWithInfo.data;
    }
}
import { ListSecurityGroupsApiRequestFactory, ListSecurityGroupsApiResponseProcessor } from "../apis/ListSecurityGroupsApi.js";
export class PromiseListSecurityGroupsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListSecurityGroupsApiRequestFactory(configuration), responseProcessor = new ListSecurityGroupsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listSecurityGroupsWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.listSecurityGroups(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSecurityGroupsWithHttpInfo(responseContext);
    }
    async listSecurityGroups(projectId, accountId, region, _options) {
        const responseWithInfo = await this.listSecurityGroupsWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ListSensitivityAnnotationsApiRequestFactory, ListSensitivityAnnotationsApiResponseProcessor } from "../apis/ListSensitivityAnnotationsApi.js";
export class PromiseListSensitivityAnnotationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListSensitivityAnnotationsApiRequestFactory(configuration), responseProcessor = new ListSensitivityAnnotationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listSensitivityAnnotationsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listSensitivityAnnotations(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSensitivityAnnotationsWithHttpInfo(responseContext);
    }
    async listSensitivityAnnotations(projectId, id, _options) {
        const responseWithInfo = await this.listSensitivityAnnotationsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { ListSourceAccountsApiRequestFactory, ListSourceAccountsApiResponseProcessor } from "../apis/ListSourceAccountsApi.js";
export class PromiseListSourceAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListSourceAccountsApiRequestFactory(configuration), responseProcessor = new ListSourceAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listSourceAccountsGlobalWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listSourceAccountsGlobal(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSourceAccountsGlobalWithHttpInfo(responseContext);
    }
    async listSourceAccountsGlobal(_options) {
        const responseWithInfo = await this.listSourceAccountsGlobalWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListUsersApiRequestFactory, ListUsersApiResponseProcessor } from "../apis/ListUsersApi.js";
export class PromiseListUsersApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListUsersApiRequestFactory(configuration), responseProcessor = new ListUsersApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listUsersWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listUsers(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listUsersWithHttpInfo(responseContext);
    }
    async listUsers(_options) {
        const responseWithInfo = await this.listUsersWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { ListVaultAccountsApiRequestFactory, ListVaultAccountsApiResponseProcessor } from "../apis/ListVaultAccountsApi.js";
export class PromiseListVaultAccountsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ListVaultAccountsApiRequestFactory(configuration), responseProcessor = new ListVaultAccountsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listVaultAccountsWithHttpInfo(sort, _options) {
        let requestContextValue = await this.requestFactory.listVaultAccounts(sort, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listVaultAccountsWithHttpInfo(responseContext);
    }
    async listVaultAccounts(sort, _options) {
        const responseWithInfo = await this.listVaultAccountsWithHttpInfo(sort, _options);
        return responseWithInfo.data;
    }
}
import { LlgPoliciesApiRequestFactory, LlgPoliciesApiResponseProcessor } from "../apis/LlgPoliciesApi.js";
export class PromiseLlgPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LlgPoliciesApiRequestFactory(configuration), responseProcessor = new LlgPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async batchUpdateLlgPoliciesWithHttpInfo(projectId, batchUpdateLlgPoliciesRequest, _options) {
        let requestContextValue = await this.requestFactory.batchUpdateLlgPolicies(projectId, batchUpdateLlgPoliciesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.batchUpdateLlgPoliciesWithHttpInfo(responseContext);
    }
    async batchUpdateLlgPolicies(projectId, batchUpdateLlgPoliciesRequest, _options) {
        const responseWithInfo = await this.batchUpdateLlgPoliciesWithHttpInfo(projectId, batchUpdateLlgPoliciesRequest, _options);
        return responseWithInfo.data;
    }
}
import { LogSourcesApiRequestFactory, LogSourcesApiResponseProcessor } from "../apis/LogSourcesApi.js";
export class PromiseLogSourcesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LogSourcesApiRequestFactory(configuration), responseProcessor = new LogSourcesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listLogSourcesWithHttpInfo(projectId, pageIndex, pageSize, locationResourceId, filter, _options) {
        let requestContextValue = await this.requestFactory.listLogSources(projectId, pageIndex, pageSize, locationResourceId, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listLogSourcesWithHttpInfo(responseContext);
    }
    async listLogSources(projectId, pageIndex, pageSize, locationResourceId, filter, _options) {
        const responseWithInfo = await this.listLogSourcesWithHttpInfo(projectId, pageIndex, pageSize, locationResourceId, filter, _options);
        return responseWithInfo.data;
    }
}
import { LogSourcesPoliciesApiRequestFactory, LogSourcesPoliciesApiResponseProcessor } from "../apis/LogSourcesPoliciesApi.js";
export class PromiseLogSourcesPoliciesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LogSourcesPoliciesApiRequestFactory(configuration), responseProcessor = new LogSourcesPoliciesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listLogSourcesPoliciesWithHttpInfo(projectId, pageIndex, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listLogSourcesPolicies(projectId, pageIndex, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listLogSourcesPoliciesWithHttpInfo(responseContext);
    }
    async listLogSourcesPolicies(projectId, pageIndex, pageSize, _options) {
        const responseWithInfo = await this.listLogSourcesPoliciesWithHttpInfo(projectId, pageIndex, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { LoginApiRequestFactory, LoginApiResponseProcessor } from "../apis/LoginApi.js";
export class PromiseLoginApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LoginApiRequestFactory(configuration), responseProcessor = new LoginApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async loginWithHttpInfo(domain, returnUrl, _options) {
        let requestContextValue = await this.requestFactory.login(domain, returnUrl, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.loginWithHttpInfo(responseContext);
    }
    async login(domain, returnUrl, _options) {
        const responseWithInfo = await this.loginWithHttpInfo(domain, returnUrl, _options);
        return responseWithInfo.data;
    }
}
import { LoginDocsApiRequestFactory, LoginDocsApiResponseProcessor } from "../apis/LoginDocsApi.js";
export class PromiseLoginDocsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LoginDocsApiRequestFactory(configuration), responseProcessor = new LoginDocsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async loginDocsWithHttpInfo(returnUrl, _options) {
        let requestContextValue = await this.requestFactory.loginDocs(returnUrl, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.loginDocsWithHttpInfo(responseContext);
    }
    async loginDocs(returnUrl, _options) {
        const responseWithInfo = await this.loginDocsWithHttpInfo(returnUrl, _options);
        return responseWithInfo.data;
    }
}
import { LogoutApiRequestFactory, LogoutApiResponseProcessor } from "../apis/LogoutApi.js";
export class PromiseLogoutApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new LogoutApiRequestFactory(configuration), responseProcessor = new LogoutApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async logoutWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.logout(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.logoutWithHttpInfo(responseContext);
    }
    async logout(_options) {
        const responseWithInfo = await this.logoutWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { MeteringPopulateApiRequestFactory, MeteringPopulateApiResponseProcessor } from "../apis/MeteringPopulateApi.js";
export class PromiseMeteringPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new MeteringPopulateApiRequestFactory(configuration), responseProcessor = new MeteringPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async meteringPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.meteringPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.meteringPopulateWithHttpInfo(responseContext);
    }
    async meteringPopulate(projectId, version, _options) {
        const responseWithInfo = await this.meteringPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { NotificationsApiRequestFactory, NotificationsApiResponseProcessor } from "../apis/NotificationsApi.js";
export class PromiseNotificationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new NotificationsApiRequestFactory(configuration), responseProcessor = new NotificationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createNotificationPolicyWithHttpInfo(projectId, notificationPolicy, _options) {
        let requestContextValue = await this.requestFactory.createNotificationPolicy(projectId, notificationPolicy, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createNotificationPolicyWithHttpInfo(responseContext);
    }
    async createNotificationPolicy(projectId, notificationPolicy, _options) {
        const responseWithInfo = await this.createNotificationPolicyWithHttpInfo(projectId, notificationPolicy, _options);
        return responseWithInfo.data;
    }
    async deleteNotificationPolicyWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.deleteNotificationPolicy(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteNotificationPolicyWithHttpInfo(responseContext);
    }
    async deleteNotificationPolicy(projectId, id, _options) {
        const responseWithInfo = await this.deleteNotificationPolicyWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async listNotificationPoliciesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listNotificationPolicies(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listNotificationPoliciesWithHttpInfo(responseContext);
    }
    async listNotificationPolicies(projectId, _options) {
        const responseWithInfo = await this.listNotificationPoliciesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async listNotificationsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options) {
        let requestContextValue = await this.requestFactory.listNotifications(projectId, pageIndex, pageSize, sort, filter, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listNotificationsWithHttpInfo(responseContext);
    }
    async listNotifications(projectId, pageIndex, pageSize, sort, filter, _options) {
        const responseWithInfo = await this.listNotificationsWithHttpInfo(projectId, pageIndex, pageSize, sort, filter, _options);
        return responseWithInfo.data;
    }
    async testNotificationPolicyWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.testNotificationPolicy(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.testNotificationPolicyWithHttpInfo(responseContext);
    }
    async testNotificationPolicy(projectId, id, _options) {
        const responseWithInfo = await this.testNotificationPolicyWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async updateNotificationPolicyWithHttpInfo(projectId, id, notificationPolicy, _options) {
        let requestContextValue = await this.requestFactory.updateNotificationPolicy(projectId, id, notificationPolicy, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateNotificationPolicyWithHttpInfo(responseContext);
    }
    async updateNotificationPolicy(projectId, id, notificationPolicy, _options) {
        const responseWithInfo = await this.updateNotificationPolicyWithHttpInfo(projectId, id, notificationPolicy, _options);
        return responseWithInfo.data;
    }
}
import { OauthAppsApiRequestFactory, OauthAppsApiResponseProcessor } from "../apis/OauthAppsApi.js";
export class PromiseOauthAppsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OauthAppsApiRequestFactory(configuration), responseProcessor = new OauthAppsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createOAuthAppWithHttpInfo(createOAuthAppRequest, _options) {
        let requestContextValue = await this.requestFactory.createOAuthApp(createOAuthAppRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createOAuthAppWithHttpInfo(responseContext);
    }
    async createOAuthApp(createOAuthAppRequest, _options) {
        const responseWithInfo = await this.createOAuthAppWithHttpInfo(createOAuthAppRequest, _options);
        return responseWithInfo.data;
    }
    async listOAuthAppsWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.listOAuthApps(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listOAuthAppsWithHttpInfo(responseContext);
    }
    async listOAuthApps(_options) {
        const responseWithInfo = await this.listOAuthAppsWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { OnboardAdditionalAzureAppsApiRequestFactory, OnboardAdditionalAzureAppsApiResponseProcessor } from "../apis/OnboardAdditionalAzureAppsApi.js";
export class PromiseOnboardAdditionalAzureAppsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OnboardAdditionalAzureAppsApiRequestFactory(configuration), responseProcessor = new OnboardAdditionalAzureAppsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async onboardAdditionalAzureAppsWithHttpInfo(projectId, onboardAdditionalAzureAppsRequest, _options) {
        let requestContextValue = await this.requestFactory.onboardAdditionalAzureApps(projectId, onboardAdditionalAzureAppsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.onboardAdditionalAzureAppsWithHttpInfo(responseContext);
    }
    async onboardAdditionalAzureApps(projectId, onboardAdditionalAzureAppsRequest, _options) {
        const responseWithInfo = await this.onboardAdditionalAzureAppsWithHttpInfo(projectId, onboardAdditionalAzureAppsRequest, _options);
        return responseWithInfo.data;
    }
}
import { OnboardRdsCdcApiRequestFactory, OnboardRdsCdcApiResponseProcessor } from "../apis/OnboardRdsCdcApi.js";
export class PromiseOnboardRdsCdcApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OnboardRdsCdcApiRequestFactory(configuration), responseProcessor = new OnboardRdsCdcApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async onboardRdsCdcWithHttpInfo(projectId, id, onboardRdsCdcRequest, _options) {
        let requestContextValue = await this.requestFactory.onboardRdsCdc(projectId, id, onboardRdsCdcRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.onboardRdsCdcWithHttpInfo(responseContext);
    }
    async onboardRdsCdc(projectId, id, onboardRdsCdcRequest, _options) {
        const responseWithInfo = await this.onboardRdsCdcWithHttpInfo(projectId, id, onboardRdsCdcRequest, _options);
        return responseWithInfo.data;
    }
}
import { OnboardingApiRequestFactory, OnboardingApiResponseProcessor } from "../apis/OnboardingApi.js";
export class PromiseOnboardingApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OnboardingApiRequestFactory(configuration), responseProcessor = new OnboardingApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getOnboardingAvailableProvidersWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.getOnboardingAvailableProviders(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getOnboardingAvailableProvidersWithHttpInfo(responseContext);
    }
    async getOnboardingAvailableProviders(_options) {
        const responseWithInfo = await this.getOnboardingAvailableProvidersWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { OrganizationsApiRequestFactory, OrganizationsApiResponseProcessor } from "../apis/OrganizationsApi.js";
export class PromiseOrganizationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OrganizationsApiRequestFactory(configuration), responseProcessor = new OrganizationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listAccessibleAccountsWithHttpInfo(pageToken, pageSize, body, _options) {
        let requestContextValue = await this.requestFactory.listAccessibleAccounts(pageToken, pageSize, body, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAccessibleAccountsWithHttpInfo(responseContext);
    }
    async listAccessibleAccounts(pageToken, pageSize, body, _options) {
        const responseWithInfo = await this.listAccessibleAccountsWithHttpInfo(pageToken, pageSize, body, _options);
        return responseWithInfo.data;
    }
    async listOrganizationAccountsWithHttpInfo(pageToken, pageSize, body, _options) {
        let requestContextValue = await this.requestFactory.listOrganizationAccounts(pageToken, pageSize, body, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listOrganizationAccountsWithHttpInfo(responseContext);
    }
    async listOrganizationAccounts(pageToken, pageSize, body, _options) {
        const responseWithInfo = await this.listOrganizationAccountsWithHttpInfo(pageToken, pageSize, body, _options);
        return responseWithInfo.data;
    }
}
import { OverrideApplicationsApiRequestFactory, OverrideApplicationsApiResponseProcessor } from "../apis/OverrideApplicationsApi.js";
export class PromiseOverrideApplicationsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new OverrideApplicationsApiRequestFactory(configuration), responseProcessor = new OverrideApplicationsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async overrideApplicationsWithHttpInfo(projectId, batchApplicationOverrides, _options) {
        let requestContextValue = await this.requestFactory.overrideApplications(projectId, batchApplicationOverrides, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.overrideApplicationsWithHttpInfo(responseContext);
    }
    async overrideApplications(projectId, batchApplicationOverrides, _options) {
        const responseWithInfo = await this.overrideApplicationsWithHttpInfo(projectId, batchApplicationOverrides, _options);
        return responseWithInfo.data;
    }
}
import { ProjectConfigurationKeysApiRequestFactory, ProjectConfigurationKeysApiResponseProcessor } from "../apis/ProjectConfigurationKeysApi.js";
export class PromiseProjectConfigurationKeysApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ProjectConfigurationKeysApiRequestFactory(configuration), responseProcessor = new ProjectConfigurationKeysApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getProjectConfigurationKeysWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.getProjectConfigurationKeys(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getProjectConfigurationKeysWithHttpInfo(responseContext);
    }
    async getProjectConfigurationKeys(projectId, _options) {
        const responseWithInfo = await this.getProjectConfigurationKeysWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async updateProjectConfigurationKeyWithHttpInfo(projectId, updateProjectConfigurationKeyRequest, _options) {
        let requestContextValue = await this.requestFactory.updateProjectConfigurationKey(projectId, updateProjectConfigurationKeyRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectConfigurationKeyWithHttpInfo(responseContext);
    }
    async updateProjectConfigurationKey(projectId, updateProjectConfigurationKeyRequest, _options) {
        const responseWithInfo = await this.updateProjectConfigurationKeyWithHttpInfo(projectId, updateProjectConfigurationKeyRequest, _options);
        return responseWithInfo.data;
    }
}
import { ProjectPopulateApiRequestFactory, ProjectPopulateApiResponseProcessor } from "../apis/ProjectPopulateApi.js";
export class PromiseProjectPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ProjectPopulateApiRequestFactory(configuration), responseProcessor = new ProjectPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async projectPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.projectPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.projectPopulateWithHttpInfo(responseContext);
    }
    async projectPopulate(projectId, version, _options) {
        const responseWithInfo = await this.projectPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { ProjectsApiRequestFactory, ProjectsApiResponseProcessor } from "../apis/ProjectsApi.js";
export class PromiseProjectsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ProjectsApiRequestFactory(configuration), responseProcessor = new ProjectsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async listProjectsWithHttpInfo(pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listProjects(pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listProjectsWithHttpInfo(responseContext);
    }
    async listProjects(pageToken, pageSize, _options) {
        const responseWithInfo = await this.listProjectsWithHttpInfo(pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { PulumiApiRequestFactory, PulumiApiResponseProcessor } from "../apis/PulumiApi.js";
export class PromisePulumiApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new PulumiApiRequestFactory(configuration), responseProcessor = new PulumiApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async upPulumiStackWithHttpInfo(id, _options) {
        let requestContextValue = await this.requestFactory.upPulumiStack(id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.upPulumiStackWithHttpInfo(responseContext);
    }
    async upPulumiStack(id, _options) {
        const responseWithInfo = await this.upPulumiStackWithHttpInfo(id, _options);
        return responseWithInfo.data;
    }
    async upPulumiStacksWithHttpInfo(pulumiConfigurationsRequest, _options) {
        let requestContextValue = await this.requestFactory.upPulumiStacks(pulumiConfigurationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.upPulumiStacksWithHttpInfo(responseContext);
    }
    async upPulumiStacks(pulumiConfigurationsRequest, _options) {
        const responseWithInfo = await this.upPulumiStacksWithHttpInfo(pulumiConfigurationsRequest, _options);
        return responseWithInfo.data;
    }
}
import { ReassessActionRequiredApiRequestFactory, ReassessActionRequiredApiResponseProcessor } from "../apis/ReassessActionRequiredApi.js";
export class PromiseReassessActionRequiredApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ReassessActionRequiredApiRequestFactory(configuration), responseProcessor = new ReassessActionRequiredApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async reassessActionRequiredWithHttpInfo(projectId, reassessActionRequiredRequest, _options) {
        let requestContextValue = await this.requestFactory.reassessActionRequired(projectId, reassessActionRequiredRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.reassessActionRequiredWithHttpInfo(responseContext);
    }
    async reassessActionRequired(projectId, reassessActionRequiredRequest, _options) {
        const responseWithInfo = await this.reassessActionRequiredWithHttpInfo(projectId, reassessActionRequiredRequest, _options);
        return responseWithInfo.data;
    }
}
import { RefreshApiRequestFactory, RefreshApiResponseProcessor } from "../apis/RefreshApi.js";
export class PromiseRefreshApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RefreshApiRequestFactory(configuration), responseProcessor = new RefreshApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async refreshWithHttpInfo(selectedRoleId, _options) {
        let requestContextValue = await this.requestFactory.refresh(selectedRoleId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.refreshWithHttpInfo(responseContext);
    }
    async refresh(selectedRoleId, _options) {
        const responseWithInfo = await this.refreshWithHttpInfo(selectedRoleId, _options);
        return responseWithInfo.data;
    }
}
import { RegisterApiRequestFactory, RegisterApiResponseProcessor } from "../apis/RegisterApi.js";
export class PromiseRegisterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RegisterApiRequestFactory(configuration), responseProcessor = new RegisterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async registerWithHttpInfo(registerInput, _options) {
        let requestContextValue = await this.requestFactory.register(registerInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.registerWithHttpInfo(responseContext);
    }
    async register(registerInput, _options) {
        const responseWithInfo = await this.registerWithHttpInfo(registerInput, _options);
        return responseWithInfo.data;
    }
}
import { RemoveRestoreAtlasPrivateLinkApiRequestFactory, RemoveRestoreAtlasPrivateLinkApiResponseProcessor } from "../apis/RemoveRestoreAtlasPrivateLinkApi.js";
export class PromiseRemoveRestoreAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RemoveRestoreAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new RemoveRestoreAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async removeRestoreAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.removeRestoreAtlasPrivateLink(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeRestoreAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async removeRestoreAtlasPrivateLink(projectId, accountId, region, _options) {
        const responseWithInfo = await this.removeRestoreAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ResourcesApiRequestFactory, ResourcesApiResponseProcessor } from "../apis/ResourcesApi.js";
export class PromiseResourcesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ResourcesApiRequestFactory(configuration), responseProcessor = new ResourcesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async cancelResourceBackupExclusionWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.cancelResourceBackupExclusion(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelResourceBackupExclusionWithHttpInfo(responseContext);
    }
    async cancelResourceBackupExclusion(projectId, id, _options) {
        const responseWithInfo = await this.cancelResourceBackupExclusionWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async cancelVolumeBackupExclusionWithHttpInfo(projectId, id, volumeId, _options) {
        let requestContextValue = await this.requestFactory.cancelVolumeBackupExclusion(projectId, id, volumeId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.cancelVolumeBackupExclusionWithHttpInfo(responseContext);
    }
    async cancelVolumeBackupExclusion(projectId, id, volumeId, _options) {
        const responseWithInfo = await this.cancelVolumeBackupExclusionWithHttpInfo(projectId, id, volumeId, _options);
        return responseWithInfo.data;
    }
    async enableResourceFromLimitExceededWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.enableResourceFromLimitExceeded(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.enableResourceFromLimitExceededWithHttpInfo(responseContext);
    }
    async enableResourceFromLimitExceeded(projectId, id, _options) {
        const responseWithInfo = await this.enableResourceFromLimitExceededWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async excludeResourceFromBackupWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.excludeResourceFromBackup(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.excludeResourceFromBackupWithHttpInfo(responseContext);
    }
    async excludeResourceFromBackup(projectId, id, _options) {
        const responseWithInfo = await this.excludeResourceFromBackupWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async excludeVolumeFromBackupWithHttpInfo(projectId, id, volumeId, _options) {
        let requestContextValue = await this.requestFactory.excludeVolumeFromBackup(projectId, id, volumeId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.excludeVolumeFromBackupWithHttpInfo(responseContext);
    }
    async excludeVolumeFromBackup(projectId, id, volumeId, _options) {
        const responseWithInfo = await this.excludeVolumeFromBackupWithHttpInfo(projectId, id, volumeId, _options);
        return responseWithInfo.data;
    }
    async getObjectStoreScanMethodWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getObjectStoreScanMethod(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getObjectStoreScanMethodWithHttpInfo(responseContext);
    }
    async getObjectStoreScanMethod(projectId, id, _options) {
        const responseWithInfo = await this.getObjectStoreScanMethodWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getResourceWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getResource(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getResourceWithHttpInfo(responseContext);
    }
    async getResource(projectId, id, _options) {
        const responseWithInfo = await this.getResourceWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async listResourceAttachmentEventsWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listResourceAttachmentEvents(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listResourceAttachmentEventsWithHttpInfo(responseContext);
    }
    async listResourceAttachmentEvents(projectId, id, _options) {
        const responseWithInfo = await this.listResourceAttachmentEventsWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async listResourcesWithHttpInfo(projectId, pageToken, pageSize, includeSaas, listInventoryRequest, _options) {
        let requestContextValue = await this.requestFactory.listResources(projectId, pageToken, pageSize, includeSaas, listInventoryRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listResourcesWithHttpInfo(responseContext);
    }
    async listResources(projectId, pageToken, pageSize, includeSaas, listInventoryRequest, _options) {
        const responseWithInfo = await this.listResourcesWithHttpInfo(projectId, pageToken, pageSize, includeSaas, listInventoryRequest, _options);
        return responseWithInfo.data;
    }
    async overrideDataClassesWithHttpInfo(projectId, id, overrideDataClassificationsRequest, _options) {
        let requestContextValue = await this.requestFactory.overrideDataClasses(projectId, id, overrideDataClassificationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.overrideDataClassesWithHttpInfo(responseContext);
    }
    async overrideDataClasses(projectId, id, overrideDataClassificationsRequest, _options) {
        const responseWithInfo = await this.overrideDataClassesWithHttpInfo(projectId, id, overrideDataClassificationsRequest, _options);
        return responseWithInfo.data;
    }
    async overrideEnvironmentWithHttpInfo(projectId, id, overrideEnvironmentRequest, _options) {
        let requestContextValue = await this.requestFactory.overrideEnvironment(projectId, id, overrideEnvironmentRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.overrideEnvironmentWithHttpInfo(responseContext);
    }
    async overrideEnvironment(projectId, id, overrideEnvironmentRequest, _options) {
        const responseWithInfo = await this.overrideEnvironmentWithHttpInfo(projectId, id, overrideEnvironmentRequest, _options);
        return responseWithInfo.data;
    }
    async overrideNetworkClassificationWithHttpInfo(projectId, id, overrideNetworkClassificationRequest, _options) {
        let requestContextValue = await this.requestFactory.overrideNetworkClassification(projectId, id, overrideNetworkClassificationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.overrideNetworkClassificationWithHttpInfo(responseContext);
    }
    async overrideNetworkClassification(projectId, id, overrideNetworkClassificationRequest, _options) {
        const responseWithInfo = await this.overrideNetworkClassificationWithHttpInfo(projectId, id, overrideNetworkClassificationRequest, _options);
        return responseWithInfo.data;
    }
    async overrideSensitivityAnnotationsWithHttpInfo(projectId, id, overrideSensitivityAnnotationsRequest, _options) {
        let requestContextValue = await this.requestFactory.overrideSensitivityAnnotations(projectId, id, overrideSensitivityAnnotationsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.overrideSensitivityAnnotationsWithHttpInfo(responseContext);
    }
    async overrideSensitivityAnnotations(projectId, id, overrideSensitivityAnnotationsRequest, _options) {
        const responseWithInfo = await this.overrideSensitivityAnnotationsWithHttpInfo(projectId, id, overrideSensitivityAnnotationsRequest, _options);
        return responseWithInfo.data;
    }
    async removeDataClassesOverrideWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.removeDataClassesOverride(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeDataClassesOverrideWithHttpInfo(responseContext);
    }
    async removeDataClassesOverride(projectId, id, _options) {
        const responseWithInfo = await this.removeDataClassesOverrideWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async removeEnvironmentOverrideWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.removeEnvironmentOverride(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeEnvironmentOverrideWithHttpInfo(responseContext);
    }
    async removeEnvironmentOverride(projectId, id, _options) {
        const responseWithInfo = await this.removeEnvironmentOverrideWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async removeNetworkClassificationOverrideWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.removeNetworkClassificationOverride(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeNetworkClassificationOverrideWithHttpInfo(responseContext);
    }
    async removeNetworkClassificationOverride(projectId, id, _options) {
        const responseWithInfo = await this.removeNetworkClassificationOverrideWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async removeSensitivityAnnotationsOverrideWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.removeSensitivityAnnotationsOverride(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeSensitivityAnnotationsOverrideWithHttpInfo(responseContext);
    }
    async removeSensitivityAnnotationsOverride(projectId, id, _options) {
        const responseWithInfo = await this.removeSensitivityAnnotationsOverrideWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async updateObjectStoreScanMethodWithHttpInfo(projectId, id, updateObjectStoreScanMethodRequest, _options) {
        let requestContextValue = await this.requestFactory.updateObjectStoreScanMethod(projectId, id, updateObjectStoreScanMethodRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateObjectStoreScanMethodWithHttpInfo(responseContext);
    }
    async updateObjectStoreScanMethod(projectId, id, updateObjectStoreScanMethodRequest, _options) {
        const responseWithInfo = await this.updateObjectStoreScanMethodWithHttpInfo(projectId, id, updateObjectStoreScanMethodRequest, _options);
        return responseWithInfo.data;
    }
}
import { RestoreAtlasClusterApiRequestFactory, RestoreAtlasClusterApiResponseProcessor } from "../apis/RestoreAtlasClusterApi.js";
export class PromiseRestoreAtlasClusterApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RestoreAtlasClusterApiRequestFactory(configuration), responseProcessor = new RestoreAtlasClusterApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async restoreAtlasClusterWithHttpInfo(projectId, id, snapshotId, restoreAtlasClusterInput, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAtlasCluster(projectId, id, snapshotId, restoreAtlasClusterInput, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAtlasClusterWithHttpInfo(responseContext);
    }
    async restoreAtlasCluster(projectId, id, snapshotId, restoreAtlasClusterInput, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAtlasClusterWithHttpInfo(projectId, id, snapshotId, restoreAtlasClusterInput, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { RestoreEksNamespaceApiRequestFactory, RestoreEksNamespaceApiResponseProcessor } from "../apis/RestoreEksNamespaceApi.js";
export class PromiseRestoreEksNamespaceApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RestoreEksNamespaceApiRequestFactory(configuration), responseProcessor = new RestoreEksNamespaceApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async restoreEksNamespaceWithHttpInfo(projectId, id, snapshotId, restoreEksNamespaceInput, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreEksNamespace(projectId, id, snapshotId, restoreEksNamespaceInput, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreEksNamespaceWithHttpInfo(responseContext);
    }
    async restoreEksNamespace(projectId, id, snapshotId, restoreEksNamespaceInput, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreEksNamespaceWithHttpInfo(projectId, id, snapshotId, restoreEksNamespaceInput, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { RestoreImageApiRequestFactory, RestoreImageApiResponseProcessor } from "../apis/RestoreImageApi.js";
export class PromiseRestoreImageApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RestoreImageApiRequestFactory(configuration), responseProcessor = new RestoreImageApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async restoreImageWithHttpInfo(projectId, id, snapshotId, restoreImageInput, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreImage(projectId, id, snapshotId, restoreImageInput, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreImageWithHttpInfo(responseContext);
    }
    async restoreImage(projectId, id, snapshotId, restoreImageInput, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreImageWithHttpInfo(projectId, id, snapshotId, restoreImageInput, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { RestoreTemplatesApiRequestFactory, RestoreTemplatesApiResponseProcessor } from "../apis/RestoreTemplatesApi.js";
export class PromiseRestoreTemplatesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new RestoreTemplatesApiRequestFactory(configuration), responseProcessor = new RestoreTemplatesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async bulkAttachRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, bulkAttachRestoreTemplateRequest, _options) {
        let requestContextValue = await this.requestFactory.bulkAttachRestoreTemplate(projectId, restoreTemplateId, bulkAttachRestoreTemplateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.bulkAttachRestoreTemplateWithHttpInfo(responseContext);
    }
    async bulkAttachRestoreTemplate(projectId, restoreTemplateId, bulkAttachRestoreTemplateRequest, _options) {
        const responseWithInfo = await this.bulkAttachRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, bulkAttachRestoreTemplateRequest, _options);
        return responseWithInfo.data;
    }
    async bulkDetachRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, bulkDetachRestoreTemplateRequest, _options) {
        let requestContextValue = await this.requestFactory.bulkDetachRestoreTemplate(projectId, restoreTemplateId, bulkDetachRestoreTemplateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.bulkDetachRestoreTemplateWithHttpInfo(responseContext);
    }
    async bulkDetachRestoreTemplate(projectId, restoreTemplateId, bulkDetachRestoreTemplateRequest, _options) {
        const responseWithInfo = await this.bulkDetachRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, bulkDetachRestoreTemplateRequest, _options);
        return responseWithInfo.data;
    }
    async createRestoreTemplateWithHttpInfo(projectId, createRestoreTemplateRequest, _options) {
        let requestContextValue = await this.requestFactory.createRestoreTemplate(projectId, createRestoreTemplateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createRestoreTemplateWithHttpInfo(responseContext);
    }
    async createRestoreTemplate(projectId, createRestoreTemplateRequest, _options) {
        const responseWithInfo = await this.createRestoreTemplateWithHttpInfo(projectId, createRestoreTemplateRequest, _options);
        return responseWithInfo.data;
    }
    async deleteRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, _options) {
        let requestContextValue = await this.requestFactory.deleteRestoreTemplate(projectId, restoreTemplateId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteRestoreTemplateWithHttpInfo(responseContext);
    }
    async deleteRestoreTemplate(projectId, restoreTemplateId, _options) {
        const responseWithInfo = await this.deleteRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, _options);
        return responseWithInfo.data;
    }
    async evaluateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, resourceId, snapshotId, _options) {
        let requestContextValue = await this.requestFactory.evaluateRestoreTemplate(projectId, restoreTemplateId, resourceId, snapshotId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.evaluateRestoreTemplateWithHttpInfo(responseContext);
    }
    async evaluateRestoreTemplate(projectId, restoreTemplateId, resourceId, snapshotId, _options) {
        const responseWithInfo = await this.evaluateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, resourceId, snapshotId, _options);
        return responseWithInfo.data;
    }
    async getRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, _options) {
        let requestContextValue = await this.requestFactory.getRestoreTemplate(projectId, restoreTemplateId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRestoreTemplateWithHttpInfo(responseContext);
    }
    async getRestoreTemplate(projectId, restoreTemplateId, _options) {
        const responseWithInfo = await this.getRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, _options);
        return responseWithInfo.data;
    }
    async listRestoreTemplatesWithHttpInfo(projectId, resourceType, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listRestoreTemplates(projectId, resourceType, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreTemplatesWithHttpInfo(responseContext);
    }
    async listRestoreTemplates(projectId, resourceType, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listRestoreTemplatesWithHttpInfo(projectId, resourceType, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async listRestoreTemplatesV2WithHttpInfo(projectId, pageToken, pageSize, listRestoreTemplatesRequest, _options) {
        let requestContextValue = await this.requestFactory.listRestoreTemplatesV2(projectId, pageToken, pageSize, listRestoreTemplatesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listRestoreTemplatesV2WithHttpInfo(responseContext);
    }
    async listRestoreTemplatesV2(projectId, pageToken, pageSize, listRestoreTemplatesRequest, _options) {
        const responseWithInfo = await this.listRestoreTemplatesV2WithHttpInfo(projectId, pageToken, pageSize, listRestoreTemplatesRequest, _options);
        return responseWithInfo.data;
    }
    async revalidateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, revalidateRestoreTemplateRequest, _options) {
        let requestContextValue = await this.requestFactory.revalidateRestoreTemplate(projectId, restoreTemplateId, revalidateRestoreTemplateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.revalidateRestoreTemplateWithHttpInfo(responseContext);
    }
    async revalidateRestoreTemplate(projectId, restoreTemplateId, revalidateRestoreTemplateRequest, _options) {
        const responseWithInfo = await this.revalidateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, revalidateRestoreTemplateRequest, _options);
        return responseWithInfo.data;
    }
    async updateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, updateRestoreTemplateRequest, _options) {
        let requestContextValue = await this.requestFactory.updateRestoreTemplate(projectId, restoreTemplateId, updateRestoreTemplateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRestoreTemplateWithHttpInfo(responseContext);
    }
    async updateRestoreTemplate(projectId, restoreTemplateId, updateRestoreTemplateRequest, _options) {
        const responseWithInfo = await this.updateRestoreTemplateWithHttpInfo(projectId, restoreTemplateId, updateRestoreTemplateRequest, _options);
        return responseWithInfo.data;
    }
}
import { SamlCallbackApiRequestFactory, SamlCallbackApiResponseProcessor } from "../apis/SamlCallbackApi.js";
export class PromiseSamlCallbackApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SamlCallbackApiRequestFactory(configuration), responseProcessor = new SamlCallbackApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async sAMLCallbackWithHttpInfo(sAMLResponse, relayState, _options) {
        let requestContextValue = await this.requestFactory.sAMLCallback(sAMLResponse, relayState, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.sAMLCallbackWithHttpInfo(responseContext);
    }
    async sAMLCallback(sAMLResponse, relayState, _options) {
        const responseWithInfo = await this.sAMLCallbackWithHttpInfo(sAMLResponse, relayState, _options);
        return responseWithInfo.data;
    }
}
import { SamlMetadataApiRequestFactory, SamlMetadataApiResponseProcessor } from "../apis/SamlMetadataApi.js";
export class PromiseSamlMetadataApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SamlMetadataApiRequestFactory(configuration), responseProcessor = new SamlMetadataApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async samlMetadataWithHttpInfo(_options) {
        let requestContextValue = await this.requestFactory.samlMetadata(_options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.samlMetadataWithHttpInfo(responseContext);
    }
    async samlMetadata(_options) {
        const responseWithInfo = await this.samlMetadataWithHttpInfo(_options);
        return responseWithInfo.data;
    }
}
import { SamlSSOApiRequestFactory, SamlSSOApiResponseProcessor } from "../apis/SamlSSOApi.js";
export class PromiseSamlSSOApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SamlSSOApiRequestFactory(configuration), responseProcessor = new SamlSSOApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async samlSsoWithHttpInfo(provider, returnUrl, _options) {
        let requestContextValue = await this.requestFactory.samlSso(provider, returnUrl, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.samlSsoWithHttpInfo(responseContext);
    }
    async samlSso(provider, returnUrl, _options) {
        const responseWithInfo = await this.samlSsoWithHttpInfo(provider, returnUrl, _options);
        return responseWithInfo.data;
    }
}
import { SaveBackupScopeApiRequestFactory, SaveBackupScopeApiResponseProcessor } from "../apis/SaveBackupScopeApi.js";
export class PromiseSaveBackupScopeApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SaveBackupScopeApiRequestFactory(configuration), responseProcessor = new SaveBackupScopeApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async saveBackupScopeWithHttpInfo(projectId, backupScope, triggerDiscovery, _options) {
        let requestContextValue = await this.requestFactory.saveBackupScope(projectId, backupScope, triggerDiscovery, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveBackupScopeWithHttpInfo(responseContext);
    }
    async saveBackupScope(projectId, backupScope, triggerDiscovery, _options) {
        const responseWithInfo = await this.saveBackupScopeWithHttpInfo(projectId, backupScope, triggerDiscovery, _options);
        return responseWithInfo.data;
    }
}
import { SaveDynamoDbBackupConfigApiRequestFactory, SaveDynamoDbBackupConfigApiResponseProcessor } from "../apis/SaveDynamoDbBackupConfigApi.js";
export class PromiseSaveDynamoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SaveDynamoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new SaveDynamoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async saveDynamoDbBackupConfigWithHttpInfo(projectId, dynamoDbBackupConfig, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.saveDynamoDbBackupConfig(projectId, dynamoDbBackupConfig, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveDynamoDbBackupConfigWithHttpInfo(responseContext);
    }
    async saveDynamoDbBackupConfig(projectId, dynamoDbBackupConfig, accountId, resourceId, _options) {
        const responseWithInfo = await this.saveDynamoDbBackupConfigWithHttpInfo(projectId, dynamoDbBackupConfig, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { SaveKeyspacesBackupConfigApiRequestFactory, SaveKeyspacesBackupConfigApiResponseProcessor } from "../apis/SaveKeyspacesBackupConfigApi.js";
export class PromiseSaveKeyspacesBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SaveKeyspacesBackupConfigApiRequestFactory(configuration), responseProcessor = new SaveKeyspacesBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async saveKeyspacesBackupConfigWithHttpInfo(projectId, keyspacesBackupConfig, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.saveKeyspacesBackupConfig(projectId, keyspacesBackupConfig, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveKeyspacesBackupConfigWithHttpInfo(responseContext);
    }
    async saveKeyspacesBackupConfig(projectId, keyspacesBackupConfig, accountId, resourceId, _options) {
        const responseWithInfo = await this.saveKeyspacesBackupConfigWithHttpInfo(projectId, keyspacesBackupConfig, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { SaveMongoDbBackupConfigApiRequestFactory, SaveMongoDbBackupConfigApiResponseProcessor } from "../apis/SaveMongoDbBackupConfigApi.js";
export class PromiseSaveMongoDbBackupConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SaveMongoDbBackupConfigApiRequestFactory(configuration), responseProcessor = new SaveMongoDbBackupConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async saveMongoDbBackupConfigWithHttpInfo(projectId, mongoDbBackupConfig, accountId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.saveMongoDbBackupConfig(projectId, mongoDbBackupConfig, accountId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveMongoDbBackupConfigWithHttpInfo(responseContext);
    }
    async saveMongoDbBackupConfig(projectId, mongoDbBackupConfig, accountId, resourceId, _options) {
        const responseWithInfo = await this.saveMongoDbBackupConfigWithHttpInfo(projectId, mongoDbBackupConfig, accountId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { SaveSecurityPlanApiRequestFactory, SaveSecurityPlanApiResponseProcessor } from "../apis/SaveSecurityPlanApi.js";
export class PromiseSaveSecurityPlanApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SaveSecurityPlanApiRequestFactory(configuration), responseProcessor = new SaveSecurityPlanApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async saveSecurityPlanWithHttpInfo(projectId, securityPlan, _options) {
        let requestContextValue = await this.requestFactory.saveSecurityPlan(projectId, securityPlan, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveSecurityPlanWithHttpInfo(responseContext);
    }
    async saveSecurityPlan(projectId, securityPlan, _options) {
        const responseWithInfo = await this.saveSecurityPlanWithHttpInfo(projectId, securityPlan, _options);
        return responseWithInfo.data;
    }
}
import { SearchApiRequestFactory, SearchApiResponseProcessor } from "../apis/SearchApi.js";
export class PromiseSearchApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SearchApiRequestFactory(configuration), responseProcessor = new SearchApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async getLogsHistogramWithHttpInfo(projectId, searchLogsHistogramInput, _options) {
        let requestContextValue = await this.requestFactory.getLogsHistogram(projectId, searchLogsHistogramInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getLogsHistogramWithHttpInfo(responseContext);
    }
    async getLogsHistogram(projectId, searchLogsHistogramInput, _options) {
        const responseWithInfo = await this.getLogsHistogramWithHttpInfo(projectId, searchLogsHistogramInput, _options);
        return responseWithInfo.data;
    }
    async getLogsSchemasWithHttpInfo(projectId, resourceIds, _options) {
        let requestContextValue = await this.requestFactory.getLogsSchemas(projectId, resourceIds, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getLogsSchemasWithHttpInfo(responseContext);
    }
    async getLogsSchemas(projectId, resourceIds, _options) {
        const responseWithInfo = await this.getLogsSchemasWithHttpInfo(projectId, resourceIds, _options);
        return responseWithInfo.data;
    }
    async searchFilesWithHttpInfo(projectId, searchInput, _options) {
        let requestContextValue = await this.requestFactory.searchFiles(projectId, searchInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchFilesWithHttpInfo(responseContext);
    }
    async searchFiles(projectId, searchInput, _options) {
        const responseWithInfo = await this.searchFilesWithHttpInfo(projectId, searchInput, _options);
        return responseWithInfo.data;
    }
    async searchLogsWithHttpInfo(projectId, searchLogsInput, _options) {
        let requestContextValue = await this.requestFactory.searchLogs(projectId, searchLogsInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchLogsWithHttpInfo(responseContext);
    }
    async searchLogs(projectId, searchLogsInput, _options) {
        const responseWithInfo = await this.searchLogsWithHttpInfo(projectId, searchLogsInput, _options);
        return responseWithInfo.data;
    }
}
import { SearchDirectoryEntitiesApiRequestFactory, SearchDirectoryEntitiesApiResponseProcessor } from "../apis/SearchDirectoryEntitiesApi.js";
export class PromiseSearchDirectoryEntitiesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SearchDirectoryEntitiesApiRequestFactory(configuration), responseProcessor = new SearchDirectoryEntitiesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async searchDirectoryEntitiesWithHttpInfo(projectId, resourceId, searchDirectoryEntitiesRequest, pageIndex, pageSize, _options) {
        let requestContextValue = await this.requestFactory.searchDirectoryEntities(projectId, resourceId, searchDirectoryEntitiesRequest, pageIndex, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchDirectoryEntitiesWithHttpInfo(responseContext);
    }
    async searchDirectoryEntities(projectId, resourceId, searchDirectoryEntitiesRequest, pageIndex, pageSize, _options) {
        const responseWithInfo = await this.searchDirectoryEntitiesWithHttpInfo(projectId, resourceId, searchDirectoryEntitiesRequest, pageIndex, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { SearchPopulateApiRequestFactory, SearchPopulateApiResponseProcessor } from "../apis/SearchPopulateApi.js";
export class PromiseSearchPopulateApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SearchPopulateApiRequestFactory(configuration), responseProcessor = new SearchPopulateApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async searchPopulateWithHttpInfo(projectId, version, _options) {
        let requestContextValue = await this.requestFactory.searchPopulate(projectId, version, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchPopulateWithHttpInfo(responseContext);
    }
    async searchPopulate(projectId, version, _options) {
        const responseWithInfo = await this.searchPopulateWithHttpInfo(projectId, version, _options);
        return responseWithInfo.data;
    }
}
import { SearchTablesApiRequestFactory, SearchTablesApiResponseProcessor } from "../apis/SearchTablesApi.js";
export class PromiseSearchTablesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SearchTablesApiRequestFactory(configuration), responseProcessor = new SearchTablesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async searchTablesWithHttpInfo(projectId, searchInput, pageIndex, pageSize, _options) {
        let requestContextValue = await this.requestFactory.searchTables(projectId, searchInput, pageIndex, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.searchTablesWithHttpInfo(responseContext);
    }
    async searchTables(projectId, searchInput, pageIndex, pageSize, _options) {
        const responseWithInfo = await this.searchTablesWithHttpInfo(projectId, searchInput, pageIndex, pageSize, _options);
        return responseWithInfo.data;
    }
}
import { SecurityScansApiRequestFactory, SecurityScansApiResponseProcessor } from "../apis/SecurityScansApi.js";
export class PromiseSecurityScansApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SecurityScansApiRequestFactory(configuration), responseProcessor = new SecurityScansApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async countInfectedResourcesWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.countInfectedResources(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.countInfectedResourcesWithHttpInfo(responseContext);
    }
    async countInfectedResources(projectId, _options) {
        const responseWithInfo = await this.countInfectedResourcesWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
    async getResourceFindingCountsWithHttpInfo(projectId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.getResourceFindingCounts(projectId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getResourceFindingCountsWithHttpInfo(responseContext);
    }
    async getResourceFindingCounts(projectId, resourceId, _options) {
        const responseWithInfo = await this.getResourceFindingCountsWithHttpInfo(projectId, resourceId, _options);
        return responseWithInfo.data;
    }
    async getSecurityPropertiesWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getSecurityProperties(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSecurityPropertiesWithHttpInfo(responseContext);
    }
    async getSecurityProperties(projectId, id, _options) {
        const responseWithInfo = await this.getSecurityPropertiesWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getSecurityScansSummaryWithHttpInfo(projectId, id, detectorType, _options) {
        let requestContextValue = await this.requestFactory.getSecurityScansSummary(projectId, id, detectorType, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSecurityScansSummaryWithHttpInfo(responseContext);
    }
    async getSecurityScansSummary(projectId, id, detectorType, _options) {
        const responseWithInfo = await this.getSecurityScansSummaryWithHttpInfo(projectId, id, detectorType, _options);
        return responseWithInfo.data;
    }
    async listInfectedSnapshotsWithHttpInfo(projectId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.listInfectedSnapshots(projectId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listInfectedSnapshotsWithHttpInfo(responseContext);
    }
    async listInfectedSnapshots(projectId, resourceId, _options) {
        const responseWithInfo = await this.listInfectedSnapshotsWithHttpInfo(projectId, resourceId, _options);
        return responseWithInfo.data;
    }
    async listSecurityFindingsWithHttpInfo(projectId, pageSize, pageToken, listSecurityFindingsRequest, _options) {
        let requestContextValue = await this.requestFactory.listSecurityFindings(projectId, pageSize, pageToken, listSecurityFindingsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listSecurityFindingsWithHttpInfo(responseContext);
    }
    async listSecurityFindings(projectId, pageSize, pageToken, listSecurityFindingsRequest, _options) {
        const responseWithInfo = await this.listSecurityFindingsWithHttpInfo(projectId, pageSize, pageToken, listSecurityFindingsRequest, _options);
        return responseWithInfo.data;
    }
    async saveSecurityPropertiesWithHttpInfo(projectId, id, saveCloudResourceSecurityPropertiesRequest, _options) {
        let requestContextValue = await this.requestFactory.saveSecurityProperties(projectId, id, saveCloudResourceSecurityPropertiesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.saveSecurityPropertiesWithHttpInfo(responseContext);
    }
    async saveSecurityProperties(projectId, id, saveCloudResourceSecurityPropertiesRequest, _options) {
        const responseWithInfo = await this.saveSecurityPropertiesWithHttpInfo(projectId, id, saveCloudResourceSecurityPropertiesRequest, _options);
        return responseWithInfo.data;
    }
    async updateResourceSecurityScansExclusionWithHttpInfo(projectId, id, updateResourceSecurityScansExclusionRequest, _options) {
        let requestContextValue = await this.requestFactory.updateResourceSecurityScansExclusion(projectId, id, updateResourceSecurityScansExclusionRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateResourceSecurityScansExclusionWithHttpInfo(responseContext);
    }
    async updateResourceSecurityScansExclusion(projectId, id, updateResourceSecurityScansExclusionRequest, _options) {
        const responseWithInfo = await this.updateResourceSecurityScansExclusionWithHttpInfo(projectId, id, updateResourceSecurityScansExclusionRequest, _options);
        return responseWithInfo.data;
    }
}
import { SetupAwsPrivateLinkApiRequestFactory, SetupAwsPrivateLinkApiResponseProcessor } from "../apis/SetupAwsPrivateLinkApi.js";
export class PromiseSetupAwsPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SetupAwsPrivateLinkApiRequestFactory(configuration), responseProcessor = new SetupAwsPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async setupAwsPrivateLinkWithHttpInfo(projectId, id, setupAwsPrivateLinkRequest, _options) {
        let requestContextValue = await this.requestFactory.setupAwsPrivateLink(projectId, id, setupAwsPrivateLinkRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.setupAwsPrivateLinkWithHttpInfo(responseContext);
    }
    async setupAwsPrivateLink(projectId, id, setupAwsPrivateLinkRequest, _options) {
        const responseWithInfo = await this.setupAwsPrivateLinkWithHttpInfo(projectId, id, setupAwsPrivateLinkRequest, _options);
        return responseWithInfo.data;
    }
}
import { SetupMongoAtlasPrivateLinkApiRequestFactory, SetupMongoAtlasPrivateLinkApiResponseProcessor } from "../apis/SetupMongoAtlasPrivateLinkApi.js";
export class PromiseSetupMongoAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SetupMongoAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new SetupMongoAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async setupMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, setupMongoAtlasPrivateLinkRequest, _options) {
        let requestContextValue = await this.requestFactory.setupMongoAtlasPrivateLink(projectId, accountId, setupMongoAtlasPrivateLinkRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.setupMongoAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async setupMongoAtlasPrivateLink(projectId, accountId, setupMongoAtlasPrivateLinkRequest, _options) {
        const responseWithInfo = await this.setupMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, setupMongoAtlasPrivateLinkRequest, _options);
        return responseWithInfo.data;
    }
}
import { SnapshotsApiRequestFactory, SnapshotsApiResponseProcessor } from "../apis/SnapshotsApi.js";
export class PromiseSnapshotsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new SnapshotsApiRequestFactory(configuration), responseProcessor = new SnapshotsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async dynamoDBRestoreMethodAvailabilityWithHttpInfo(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options) {
        let requestContextValue = await this.requestFactory.dynamoDBRestoreMethodAvailability(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.dynamoDBRestoreMethodAvailabilityWithHttpInfo(responseContext);
    }
    async dynamoDBRestoreMethodAvailability(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options) {
        const responseWithInfo = await this.dynamoDBRestoreMethodAvailabilityWithHttpInfo(projectId, id, dynamoDBRestoreMethodAvailabilityRequest, _options);
        return responseWithInfo.data;
    }
    async exportAccessDeniedItemsWithHttpInfo(projectId, id, snapshotId, exportAccessDeniedItemsRequest, _options) {
        let requestContextValue = await this.requestFactory.exportAccessDeniedItems(projectId, id, snapshotId, exportAccessDeniedItemsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.exportAccessDeniedItemsWithHttpInfo(responseContext);
    }
    async exportAccessDeniedItems(projectId, id, snapshotId, exportAccessDeniedItemsRequest, _options) {
        const responseWithInfo = await this.exportAccessDeniedItemsWithHttpInfo(projectId, id, snapshotId, exportAccessDeniedItemsRequest, _options);
        return responseWithInfo.data;
    }
    async getAzureSqlDatabaseNativePitrWindowWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getAzureSqlDatabaseNativePitrWindow(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getAzureSqlDatabaseNativePitrWindowWithHttpInfo(responseContext);
    }
    async getAzureSqlDatabaseNativePitrWindow(projectId, id, _options) {
        const responseWithInfo = await this.getAzureSqlDatabaseNativePitrWindowWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getPitrRestoreWindowsWithHttpInfo(projectId, id, startTime, endTime, _options) {
        let requestContextValue = await this.requestFactory.getPitrRestoreWindows(projectId, id, startTime, endTime, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getPitrRestoreWindowsWithHttpInfo(responseContext);
    }
    async getPitrRestoreWindows(projectId, id, startTime, endTime, _options) {
        const responseWithInfo = await this.getPitrRestoreWindowsWithHttpInfo(projectId, id, startTime, endTime, _options);
        return responseWithInfo.data;
    }
    async getRdsNativePitrWindowWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getRdsNativePitrWindow(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getRdsNativePitrWindowWithHttpInfo(responseContext);
    }
    async getRdsNativePitrWindow(projectId, id, _options) {
        const responseWithInfo = await this.getRdsNativePitrWindowWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async getSnapshotWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.getSnapshot(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getSnapshotWithHttpInfo(responseContext);
    }
    async getSnapshot(projectId, id, _options) {
        const responseWithInfo = await this.getSnapshotWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async holdSnapshotWithHttpInfo(projectId, id, holdSnapshotRequest, _options) {
        let requestContextValue = await this.requestFactory.holdSnapshot(projectId, id, holdSnapshotRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.holdSnapshotWithHttpInfo(responseContext);
    }
    async holdSnapshot(projectId, id, holdSnapshotRequest, _options) {
        const responseWithInfo = await this.holdSnapshotWithHttpInfo(projectId, id, holdSnapshotRequest, _options);
        return responseWithInfo.data;
    }
    async listAzureSqlRestoreTargetServersWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.listAzureSqlRestoreTargetServers(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureSqlRestoreTargetServersWithHttpInfo(responseContext);
    }
    async listAzureSqlRestoreTargetServers(projectId, id, _options) {
        const responseWithInfo = await this.listAzureSqlRestoreTargetServersWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
    async listResourceSnapshotsWithHttpInfo(projectId, id, pageToken, pageSize, listInventorySnapshotsRequest, _options) {
        let requestContextValue = await this.requestFactory.listResourceSnapshots(projectId, id, pageToken, pageSize, listInventorySnapshotsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listResourceSnapshotsWithHttpInfo(responseContext);
    }
    async listResourceSnapshots(projectId, id, pageToken, pageSize, listInventorySnapshotsRequest, _options) {
        const responseWithInfo = await this.listResourceSnapshotsWithHttpInfo(projectId, id, pageToken, pageSize, listInventorySnapshotsRequest, _options);
        return responseWithInfo.data;
    }
    async removeSnapshotHoldWithHttpInfo(projectId, id, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.removeSnapshotHold(projectId, id, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.removeSnapshotHoldWithHttpInfo(responseContext);
    }
    async removeSnapshotHold(projectId, id, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.removeSnapshotHoldWithHttpInfo(projectId, id, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureDiskWithHttpInfo(projectId, id, snapshotId, restoreAzureDiskRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureDisk(projectId, id, snapshotId, restoreAzureDiskRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureDiskWithHttpInfo(responseContext);
    }
    async restoreAzureDisk(projectId, id, snapshotId, restoreAzureDiskRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureDiskWithHttpInfo(projectId, id, snapshotId, restoreAzureDiskRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureFileShareWithHttpInfo(projectId, id, restoreAzureFileShareRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureFileShare(projectId, id, restoreAzureFileShareRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureFileShareWithHttpInfo(responseContext);
    }
    async restoreAzureFileShare(projectId, id, restoreAzureFileShareRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureFileShareWithHttpInfo(projectId, id, restoreAzureFileShareRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzurePostgresDatabaseWithHttpInfo(projectId, id, snapshotId, restoreAzurePostgresDatabaseRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzurePostgresDatabase(projectId, id, snapshotId, restoreAzurePostgresDatabaseRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzurePostgresDatabaseWithHttpInfo(responseContext);
    }
    async restoreAzurePostgresDatabase(projectId, id, snapshotId, restoreAzurePostgresDatabaseRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzurePostgresDatabaseWithHttpInfo(projectId, id, snapshotId, restoreAzurePostgresDatabaseRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureSqlDatabaseWithHttpInfo(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureSqlDatabase(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureSqlDatabaseWithHttpInfo(responseContext);
    }
    async restoreAzureSqlDatabase(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureSqlDatabaseWithHttpInfo(projectId, id, snapshotId, restoreAzureSqlDatabaseRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureSqlDatabaseNativePitrWithHttpInfo(projectId, id, restoreAzureSqlDatabaseNativePitrRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureSqlDatabaseNativePitr(projectId, id, restoreAzureSqlDatabaseNativePitrRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureSqlDatabaseNativePitrWithHttpInfo(responseContext);
    }
    async restoreAzureSqlDatabaseNativePitr(projectId, id, restoreAzureSqlDatabaseNativePitrRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureSqlDatabaseNativePitrWithHttpInfo(projectId, id, restoreAzureSqlDatabaseNativePitrRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureSqlVmPitrWithHttpInfo(projectId, id, restoreAzureSqlVmPitrRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureSqlVmPitr(projectId, id, restoreAzureSqlVmPitrRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureSqlVmPitrWithHttpInfo(responseContext);
    }
    async restoreAzureSqlVmPitr(projectId, id, restoreAzureSqlVmPitrRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureSqlVmPitrWithHttpInfo(projectId, id, restoreAzureSqlVmPitrRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreAzureVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreAzureVmInstanceRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreAzureVmInstance(projectId, id, snapshotId, restoreAzureVmInstanceRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreAzureVmInstanceWithHttpInfo(responseContext);
    }
    async restoreAzureVmInstance(projectId, id, snapshotId, restoreAzureVmInstanceRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreAzureVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreAzureVmInstanceRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreBigQueryDatasetWithHttpInfo(projectId, id, snapshotId, restoreBigQueryDatasetRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreBigQueryDataset(projectId, id, snapshotId, restoreBigQueryDatasetRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreBigQueryDatasetWithHttpInfo(responseContext);
    }
    async restoreBigQueryDataset(projectId, id, snapshotId, restoreBigQueryDatasetRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreBigQueryDatasetWithHttpInfo(projectId, id, snapshotId, restoreBigQueryDatasetRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreBigQueryNativePitrWithHttpInfo(projectId, id, restoreBigQueryNativePitrRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreBigQueryNativePitr(projectId, id, restoreBigQueryNativePitrRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreBigQueryNativePitrWithHttpInfo(responseContext);
    }
    async restoreBigQueryNativePitr(projectId, id, restoreBigQueryNativePitrRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreBigQueryNativePitrWithHttpInfo(projectId, id, restoreBigQueryNativePitrRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreBucketWithHttpInfo(projectId, id, snapshotId, restoreBucketRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreBucket(projectId, id, snapshotId, restoreBucketRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreBucketWithHttpInfo(responseContext);
    }
    async restoreBucket(projectId, id, snapshotId, restoreBucketRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreBucketWithHttpInfo(projectId, id, snapshotId, restoreBucketRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreCassandraClusterWithHttpInfo(projectId, id, snapshotId, restoreAwsCassandraClusterRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreCassandraCluster(projectId, id, snapshotId, restoreAwsCassandraClusterRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreCassandraClusterWithHttpInfo(responseContext);
    }
    async restoreCassandraCluster(projectId, id, snapshotId, restoreAwsCassandraClusterRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreCassandraClusterWithHttpInfo(projectId, id, snapshotId, restoreAwsCassandraClusterRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDatabaseWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDatabase(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDatabaseWithHttpInfo(responseContext);
    }
    async restoreDatabase(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDatabaseWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsInstanceRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDbToRdsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDbToRdsSnapshot(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDbToRdsSnapshotWithHttpInfo(responseContext);
    }
    async restoreDbToRdsSnapshot(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDbToRdsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreDbToRdsSnapshotRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDocumentdbWithHttpInfo(projectId, id, snapshotId, restoreDocumentDBRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDocumentdb(projectId, id, snapshotId, restoreDocumentDBRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDocumentdbWithHttpInfo(responseContext);
    }
    async restoreDocumentdb(projectId, id, snapshotId, restoreDocumentDBRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDocumentdbWithHttpInfo(projectId, id, snapshotId, restoreDocumentDBRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDynamoDBTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBTableRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDynamoDBTable(projectId, id, snapshotId, restoreDynamoDBTableRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDynamoDBTableWithHttpInfo(responseContext);
    }
    async restoreDynamoDBTable(projectId, id, snapshotId, restoreDynamoDBTableRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDynamoDBTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBTableRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDynamoDBToExistingTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDynamoDBToExistingTable(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDynamoDBToExistingTableWithHttpInfo(responseContext);
    }
    async restoreDynamoDBToExistingTable(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDynamoDBToExistingTableWithHttpInfo(projectId, id, snapshotId, restoreDynamoDBToExistingTableRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreDynamoDBToS3WithHttpInfo(projectId, id, snapshotId, restoreDynamoDBToS3Request, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreDynamoDBToS3(projectId, id, snapshotId, restoreDynamoDBToS3Request, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreDynamoDBToS3WithHttpInfo(responseContext);
    }
    async restoreDynamoDBToS3(projectId, id, snapshotId, restoreDynamoDBToS3Request, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreDynamoDBToS3WithHttpInfo(projectId, id, snapshotId, restoreDynamoDBToS3Request, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreEFSWithHttpInfo(projectId, id, snapshotId, restoreEFSRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreEFS(projectId, id, snapshotId, restoreEFSRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreEFSWithHttpInfo(responseContext);
    }
    async restoreEFS(projectId, id, snapshotId, restoreEFSRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreEFSWithHttpInfo(projectId, id, snapshotId, restoreEFSRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreEbsVolumeWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreEbsVolume(projectId, id, snapshotId, restoreVolumeToEbsRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreEbsVolumeWithHttpInfo(responseContext);
    }
    async restoreEbsVolume(projectId, id, snapshotId, restoreVolumeToEbsRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreEbsVolumeWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreEc2InstanceWithHttpInfo(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreEc2Instance(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreEc2InstanceWithHttpInfo(responseContext);
    }
    async restoreEc2Instance(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreEc2InstanceWithHttpInfo(projectId, id, snapshotId, restoreAwsEc2InstanceRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreFSxWithHttpInfo(projectId, id, snapshotId, restoreFSxRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreFSx(projectId, id, snapshotId, restoreFSxRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreFSxWithHttpInfo(responseContext);
    }
    async restoreFSx(projectId, id, snapshotId, restoreFSxRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreFSxWithHttpInfo(projectId, id, snapshotId, restoreFSxRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreFilesWithHttpInfo(projectId, id, snapshotId, restoreFilesRequest, _options) {
        let requestContextValue = await this.requestFactory.restoreFiles(projectId, id, snapshotId, restoreFilesRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreFilesWithHttpInfo(responseContext);
    }
    async restoreFiles(projectId, id, snapshotId, restoreFilesRequest, _options) {
        const responseWithInfo = await this.restoreFilesWithHttpInfo(projectId, id, snapshotId, restoreFilesRequest, _options);
        return responseWithInfo.data;
    }
    async restoreGcpCloudSqlWithHttpInfo(projectId, id, snapshotId, restoreGcpCloudSqlRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreGcpCloudSql(projectId, id, snapshotId, restoreGcpCloudSqlRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreGcpCloudSqlWithHttpInfo(responseContext);
    }
    async restoreGcpCloudSql(projectId, id, snapshotId, restoreGcpCloudSqlRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreGcpCloudSqlWithHttpInfo(projectId, id, snapshotId, restoreGcpCloudSqlRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreGcpDiskWithHttpInfo(projectId, id, snapshotId, restoreGcpDiskRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreGcpDisk(projectId, id, snapshotId, restoreGcpDiskRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreGcpDiskWithHttpInfo(responseContext);
    }
    async restoreGcpDisk(projectId, id, snapshotId, restoreGcpDiskRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreGcpDiskWithHttpInfo(projectId, id, snapshotId, restoreGcpDiskRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreGcpVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreGcpVmInstanceRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreGcpVmInstance(projectId, id, snapshotId, restoreGcpVmInstanceRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreGcpVmInstanceWithHttpInfo(responseContext);
    }
    async restoreGcpVmInstance(projectId, id, snapshotId, restoreGcpVmInstanceRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreGcpVmInstanceWithHttpInfo(projectId, id, snapshotId, restoreGcpVmInstanceRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreGoogleWorkspaceWithHttpInfo(projectId, id, snapshotId, restoreGoogleWorkspaceRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreGoogleWorkspace(projectId, id, snapshotId, restoreGoogleWorkspaceRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreGoogleWorkspaceWithHttpInfo(responseContext);
    }
    async restoreGoogleWorkspace(projectId, id, snapshotId, restoreGoogleWorkspaceRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreGoogleWorkspaceWithHttpInfo(projectId, id, snapshotId, restoreGoogleWorkspaceRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreKeyspacesTableWithHttpInfo(projectId, id, snapshotId, restoreKeyspacesTableRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreKeyspacesTable(projectId, id, snapshotId, restoreKeyspacesTableRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreKeyspacesTableWithHttpInfo(responseContext);
    }
    async restoreKeyspacesTable(projectId, id, snapshotId, restoreKeyspacesTableRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreKeyspacesTableWithHttpInfo(projectId, id, snapshotId, restoreKeyspacesTableRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreMicrosoft365WithHttpInfo(projectId, id, snapshotId, restoreMicrosoft365Request, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreMicrosoft365(projectId, id, snapshotId, restoreMicrosoft365Request, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreMicrosoft365WithHttpInfo(responseContext);
    }
    async restoreMicrosoft365(projectId, id, snapshotId, restoreMicrosoft365Request, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreMicrosoft365WithHttpInfo(projectId, id, snapshotId, restoreMicrosoft365Request, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreNeptuneWithHttpInfo(projectId, id, snapshotId, restoreNeptuneRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreNeptune(projectId, id, snapshotId, restoreNeptuneRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreNeptuneWithHttpInfo(responseContext);
    }
    async restoreNeptune(projectId, id, snapshotId, restoreNeptuneRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreNeptuneWithHttpInfo(projectId, id, snapshotId, restoreNeptuneRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreRdsNativePitrWithHttpInfo(projectId, id, restoreRdsNativePitrRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreRdsNativePitr(projectId, id, restoreRdsNativePitrRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreRdsNativePitrWithHttpInfo(responseContext);
    }
    async restoreRdsNativePitr(projectId, id, restoreRdsNativePitrRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreRdsNativePitrWithHttpInfo(projectId, id, restoreRdsNativePitrRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
    async restoreToEbsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.restoreToEbsSnapshot(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.restoreToEbsSnapshotWithHttpInfo(responseContext);
    }
    async restoreToEbsSnapshot(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.restoreToEbsSnapshotWithHttpInfo(projectId, id, snapshotId, restoreVolumeToEbsSnapshotRequest, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { StorageGatewaysApiRequestFactory, StorageGatewaysApiResponseProcessor } from "../apis/StorageGatewaysApi.js";
export class PromiseStorageGatewaysApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new StorageGatewaysApiRequestFactory(configuration), responseProcessor = new StorageGatewaysApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async importDlsgCertificateWithHttpInfo(projectId, importDlsgCertificateRequest, _options) {
        let requestContextValue = await this.requestFactory.importDlsgCertificate(projectId, importDlsgCertificateRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.importDlsgCertificateWithHttpInfo(responseContext);
    }
    async importDlsgCertificate(projectId, importDlsgCertificateRequest, _options) {
        const responseWithInfo = await this.importDlsgCertificateWithHttpInfo(projectId, importDlsgCertificateRequest, _options);
        return responseWithInfo.data;
    }
    async listStorageGatewaysWithHttpInfo(projectId, _options) {
        let requestContextValue = await this.requestFactory.listStorageGateways(projectId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listStorageGatewaysWithHttpInfo(responseContext);
    }
    async listStorageGateways(projectId, _options) {
        const responseWithInfo = await this.listStorageGatewaysWithHttpInfo(projectId, _options);
        return responseWithInfo.data;
    }
}
import { TeardownAwsPrivateLinkApiRequestFactory, TeardownAwsPrivateLinkApiResponseProcessor } from "../apis/TeardownAwsPrivateLinkApi.js";
export class PromiseTeardownAwsPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new TeardownAwsPrivateLinkApiRequestFactory(configuration), responseProcessor = new TeardownAwsPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async teardownAwsPrivateLinkWithHttpInfo(projectId, id, _options) {
        let requestContextValue = await this.requestFactory.teardownAwsPrivateLink(projectId, id, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.teardownAwsPrivateLinkWithHttpInfo(responseContext);
    }
    async teardownAwsPrivateLink(projectId, id, _options) {
        const responseWithInfo = await this.teardownAwsPrivateLinkWithHttpInfo(projectId, id, _options);
        return responseWithInfo.data;
    }
}
import { TeardownMongoAtlasPrivateLinkApiRequestFactory, TeardownMongoAtlasPrivateLinkApiResponseProcessor } from "../apis/TeardownMongoAtlasPrivateLinkApi.js";
export class PromiseTeardownMongoAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new TeardownMongoAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new TeardownMongoAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async teardownMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.teardownMongoAtlasPrivateLink(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.teardownMongoAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async teardownMongoAtlasPrivateLink(projectId, accountId, region, _options) {
        const responseWithInfo = await this.teardownMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
import { ThreatHuntingApiRequestFactory, ThreatHuntingApiResponseProcessor } from "../apis/ThreatHuntingApi.js";
export class PromiseThreatHuntingApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ThreatHuntingApiRequestFactory(configuration), responseProcessor = new ThreatHuntingApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createThreatHuntingScanRequestWithHttpInfo(projectId, createThreatHuntingScanRequestRequest, _options) {
        let requestContextValue = await this.requestFactory.createThreatHuntingScanRequest(projectId, createThreatHuntingScanRequestRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createThreatHuntingScanRequestWithHttpInfo(responseContext);
    }
    async createThreatHuntingScanRequest(projectId, createThreatHuntingScanRequestRequest, _options) {
        const responseWithInfo = await this.createThreatHuntingScanRequestWithHttpInfo(projectId, createThreatHuntingScanRequestRequest, _options);
        return responseWithInfo.data;
    }
    async deleteThreatHuntingScanRequestWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.deleteThreatHuntingScanRequest(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteThreatHuntingScanRequestWithHttpInfo(responseContext);
    }
    async deleteThreatHuntingScanRequest(projectId, requestId, _options) {
        const responseWithInfo = await this.deleteThreatHuntingScanRequestWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
    async getThreatHuntingScanRequestWithHttpInfo(projectId, requestId, _options) {
        let requestContextValue = await this.requestFactory.getThreatHuntingScanRequest(projectId, requestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getThreatHuntingScanRequestWithHttpInfo(responseContext);
    }
    async getThreatHuntingScanRequest(projectId, requestId, _options) {
        const responseWithInfo = await this.getThreatHuntingScanRequestWithHttpInfo(projectId, requestId, _options);
        return responseWithInfo.data;
    }
    async listThreatHuntingResultsWithHttpInfo(projectId, pageToken, pageSize, listThreatHuntingResultsRequest, _options) {
        let requestContextValue = await this.requestFactory.listThreatHuntingResults(projectId, pageToken, pageSize, listThreatHuntingResultsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listThreatHuntingResultsWithHttpInfo(responseContext);
    }
    async listThreatHuntingResults(projectId, pageToken, pageSize, listThreatHuntingResultsRequest, _options) {
        const responseWithInfo = await this.listThreatHuntingResultsWithHttpInfo(projectId, pageToken, pageSize, listThreatHuntingResultsRequest, _options);
        return responseWithInfo.data;
    }
    async listThreatHuntingScanRequestsWithHttpInfo(projectId, pageToken, pageSize, listThreatHuntingScanRequestsRequest, _options) {
        let requestContextValue = await this.requestFactory.listThreatHuntingScanRequests(projectId, pageToken, pageSize, listThreatHuntingScanRequestsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listThreatHuntingScanRequestsWithHttpInfo(responseContext);
    }
    async listThreatHuntingScanRequests(projectId, pageToken, pageSize, listThreatHuntingScanRequestsRequest, _options) {
        const responseWithInfo = await this.listThreatHuntingScanRequestsWithHttpInfo(projectId, pageToken, pageSize, listThreatHuntingScanRequestsRequest, _options);
        return responseWithInfo.data;
    }
    async updateThreatHuntingScanRequestWithHttpInfo(projectId, requestId, updateThreatHuntingScanRequestRequest, _options) {
        let requestContextValue = await this.requestFactory.updateThreatHuntingScanRequest(projectId, requestId, updateThreatHuntingScanRequestRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateThreatHuntingScanRequestWithHttpInfo(responseContext);
    }
    async updateThreatHuntingScanRequest(projectId, requestId, updateThreatHuntingScanRequestRequest, _options) {
        const responseWithInfo = await this.updateThreatHuntingScanRequestWithHttpInfo(projectId, requestId, updateThreatHuntingScanRequestRequest, _options);
        return responseWithInfo.data;
    }
}
import { TriggerKeyspacesScheduledDumpApiRequestFactory, TriggerKeyspacesScheduledDumpApiResponseProcessor } from "../apis/TriggerKeyspacesScheduledDumpApi.js";
export class PromiseTriggerKeyspacesScheduledDumpApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new TriggerKeyspacesScheduledDumpApiRequestFactory(configuration), responseProcessor = new TriggerKeyspacesScheduledDumpApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async triggerKeyspacesScheduledDumpWithHttpInfo(projectId, resourceId, _options) {
        let requestContextValue = await this.requestFactory.triggerKeyspacesScheduledDump(projectId, resourceId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.triggerKeyspacesScheduledDumpWithHttpInfo(responseContext);
    }
    async triggerKeyspacesScheduledDump(projectId, resourceId, _options) {
        const responseWithInfo = await this.triggerKeyspacesScheduledDumpWithHttpInfo(projectId, resourceId, _options);
        return responseWithInfo.data;
    }
}
import { TriggerScheduledBackupApiRequestFactory, TriggerScheduledBackupApiResponseProcessor } from "../apis/TriggerScheduledBackupApi.js";
export class PromiseTriggerScheduledBackupApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new TriggerScheduledBackupApiRequestFactory(configuration), responseProcessor = new TriggerScheduledBackupApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async triggerScheduledBackupWithHttpInfo(projectId, resourceId, scheduleId, _options) {
        let requestContextValue = await this.requestFactory.triggerScheduledBackup(projectId, resourceId, scheduleId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.triggerScheduledBackupWithHttpInfo(responseContext);
    }
    async triggerScheduledBackup(projectId, resourceId, scheduleId, _options) {
        const responseWithInfo = await this.triggerScheduledBackupWithHttpInfo(projectId, resourceId, scheduleId, _options);
        return responseWithInfo.data;
    }
}
import { UpdateAccountProfileApiRequestFactory, UpdateAccountProfileApiResponseProcessor } from "../apis/UpdateAccountProfileApi.js";
export class PromiseUpdateAccountProfileApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateAccountProfileApiRequestFactory(configuration), responseProcessor = new UpdateAccountProfileApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateAccountCompanyContactsWithHttpInfo(updateAccountCompanyContactsRequest, _options) {
        let requestContextValue = await this.requestFactory.updateAccountCompanyContacts(updateAccountCompanyContactsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateAccountCompanyContactsWithHttpInfo(responseContext);
    }
    async updateAccountCompanyContacts(updateAccountCompanyContactsRequest, _options) {
        const responseWithInfo = await this.updateAccountCompanyContactsWithHttpInfo(updateAccountCompanyContactsRequest, _options);
        return responseWithInfo.data;
    }
    async updateAccountLabelsWithHttpInfo(updateAccountLabelsRequest, _options) {
        let requestContextValue = await this.requestFactory.updateAccountLabels(updateAccountLabelsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateAccountLabelsWithHttpInfo(responseContext);
    }
    async updateAccountLabels(updateAccountLabelsRequest, _options) {
        const responseWithInfo = await this.updateAccountLabelsWithHttpInfo(updateAccountLabelsRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateAccountUIPreferencesApiRequestFactory, UpdateAccountUIPreferencesApiResponseProcessor } from "../apis/UpdateAccountUIPreferencesApi.js";
export class PromiseUpdateAccountUIPreferencesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateAccountUIPreferencesApiRequestFactory(configuration), responseProcessor = new UpdateAccountUIPreferencesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateAccountUIPreferencesWithHttpInfo(key, requestBody, _options) {
        let requestContextValue = await this.requestFactory.updateAccountUIPreferences(key, requestBody, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateAccountUIPreferencesWithHttpInfo(responseContext);
    }
    async updateAccountUIPreferences(key, requestBody, _options) {
        const responseWithInfo = await this.updateAccountUIPreferencesWithHttpInfo(key, requestBody, _options);
        return responseWithInfo.data;
    }
}
import { UpdateApiCredentialsApiRequestFactory, UpdateApiCredentialsApiResponseProcessor } from "../apis/UpdateApiCredentialsApi.js";
export class PromiseUpdateApiCredentialsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateApiCredentialsApiRequestFactory(configuration), responseProcessor = new UpdateApiCredentialsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateApiCredentialsWithHttpInfo(clientId, updateApiCredentialsRequest, _options) {
        let requestContextValue = await this.requestFactory.updateApiCredentials(clientId, updateApiCredentialsRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateApiCredentialsWithHttpInfo(responseContext);
    }
    async updateApiCredentials(clientId, updateApiCredentialsRequest, _options) {
        const responseWithInfo = await this.updateApiCredentialsWithHttpInfo(clientId, updateApiCredentialsRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateBackupPolicyApiRequestFactory, UpdateBackupPolicyApiResponseProcessor } from "../apis/UpdateBackupPolicyApi.js";
export class PromiseUpdateBackupPolicyApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateBackupPolicyApiRequestFactory(configuration), responseProcessor = new UpdateBackupPolicyApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updatePolicyWithHttpInfo(projectId, id, backupPolicyConfig, xActionApprovalRequestId, _options) {
        let requestContextValue = await this.requestFactory.updatePolicy(projectId, id, backupPolicyConfig, xActionApprovalRequestId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updatePolicyWithHttpInfo(responseContext);
    }
    async updatePolicy(projectId, id, backupPolicyConfig, xActionApprovalRequestId, _options) {
        const responseWithInfo = await this.updatePolicyWithHttpInfo(projectId, id, backupPolicyConfig, xActionApprovalRequestId, _options);
        return responseWithInfo.data;
    }
}
import { UpdateControlApiRequestFactory, UpdateControlApiResponseProcessor } from "../apis/UpdateControlApi.js";
export class PromiseUpdateControlApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateControlApiRequestFactory(configuration), responseProcessor = new UpdateControlApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateControlWithHttpInfo(projectId, id, control, _options) {
        let requestContextValue = await this.requestFactory.updateControl(projectId, id, control, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateControlWithHttpInfo(responseContext);
    }
    async updateControl(projectId, id, control, _options) {
        const responseWithInfo = await this.updateControlWithHttpInfo(projectId, id, control, _options);
        return responseWithInfo.data;
    }
}
import { UpdateControlViolationApiRequestFactory, UpdateControlViolationApiResponseProcessor } from "../apis/UpdateControlViolationApi.js";
export class PromiseUpdateControlViolationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateControlViolationApiRequestFactory(configuration), responseProcessor = new UpdateControlViolationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateControlViolationWithHttpInfo(projectId, id, updateControlViolationInput, _options) {
        let requestContextValue = await this.requestFactory.updateControlViolation(projectId, id, updateControlViolationInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateControlViolationWithHttpInfo(responseContext);
    }
    async updateControlViolation(projectId, id, updateControlViolationInput, _options) {
        const responseWithInfo = await this.updateControlViolationWithHttpInfo(projectId, id, updateControlViolationInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateFindingExclusionPatternApiRequestFactory, UpdateFindingExclusionPatternApiResponseProcessor } from "../apis/UpdateFindingExclusionPatternApi.js";
export class PromiseUpdateFindingExclusionPatternApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateFindingExclusionPatternApiRequestFactory(configuration), responseProcessor = new UpdateFindingExclusionPatternApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateFindingExclusionPatternWithHttpInfo(projectId, findingExclusionPattern, _options) {
        let requestContextValue = await this.requestFactory.updateFindingExclusionPattern(projectId, findingExclusionPattern, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateFindingExclusionPatternWithHttpInfo(responseContext);
    }
    async updateFindingExclusionPattern(projectId, findingExclusionPattern, _options) {
        const responseWithInfo = await this.updateFindingExclusionPatternWithHttpInfo(projectId, findingExclusionPattern, _options);
        return responseWithInfo.data;
    }
}
import { UpdateObjectStoreExclusionRuleApiRequestFactory, UpdateObjectStoreExclusionRuleApiResponseProcessor } from "../apis/UpdateObjectStoreExclusionRuleApi.js";
export class PromiseUpdateObjectStoreExclusionRuleApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateObjectStoreExclusionRuleApiRequestFactory(configuration), responseProcessor = new UpdateObjectStoreExclusionRuleApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateObjectStoreExclusionRuleWithHttpInfo(projectId, id, objectStoreExclusionRule, _options) {
        let requestContextValue = await this.requestFactory.updateObjectStoreExclusionRule(projectId, id, objectStoreExclusionRule, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateObjectStoreExclusionRuleWithHttpInfo(responseContext);
    }
    async updateObjectStoreExclusionRule(projectId, id, objectStoreExclusionRule, _options) {
        const responseWithInfo = await this.updateObjectStoreExclusionRuleWithHttpInfo(projectId, id, objectStoreExclusionRule, _options);
        return responseWithInfo.data;
    }
}
import { UpdateProjectRestoreAccountDeprecatedApiRequestFactory, UpdateProjectRestoreAccountDeprecatedApiResponseProcessor } from "../apis/UpdateProjectRestoreAccountDeprecatedApi.js";
export class PromiseUpdateProjectRestoreAccountDeprecatedApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateProjectRestoreAccountDeprecatedApiRequestFactory(configuration), responseProcessor = new UpdateProjectRestoreAccountDeprecatedApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateProjectRestoreAccountDeprecatedWithHttpInfo(projectId, accountId, accountUpdateInput, _options) {
        let requestContextValue = await this.requestFactory.updateProjectRestoreAccountDeprecated(projectId, accountId, accountUpdateInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectRestoreAccountDeprecatedWithHttpInfo(responseContext);
    }
    async updateProjectRestoreAccountDeprecated(projectId, accountId, accountUpdateInput, _options) {
        const responseWithInfo = await this.updateProjectRestoreAccountDeprecatedWithHttpInfo(projectId, accountId, accountUpdateInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateProjectSourceAccountApiRequestFactory, UpdateProjectSourceAccountApiResponseProcessor } from "../apis/UpdateProjectSourceAccountApi.js";
export class PromiseUpdateProjectSourceAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateProjectSourceAccountApiRequestFactory(configuration), responseProcessor = new UpdateProjectSourceAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateProjectSourceAccountWithHttpInfo(projectId, accountId, accountUpdateInput, _options) {
        let requestContextValue = await this.requestFactory.updateProjectSourceAccount(projectId, accountId, accountUpdateInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectSourceAccountWithHttpInfo(responseContext);
    }
    async updateProjectSourceAccount(projectId, accountId, accountUpdateInput, _options) {
        const responseWithInfo = await this.updateProjectSourceAccountWithHttpInfo(projectId, accountId, accountUpdateInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateProjectSourceAccountConfigurationApiRequestFactory, UpdateProjectSourceAccountConfigurationApiResponseProcessor } from "../apis/UpdateProjectSourceAccountConfigurationApi.js";
export class PromiseUpdateProjectSourceAccountConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateProjectSourceAccountConfigurationApiRequestFactory(configuration), responseProcessor = new UpdateProjectSourceAccountConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateProjectSourceAccountConfigurationWithHttpInfo(projectId, accountId, body, _options) {
        let requestContextValue = await this.requestFactory.updateProjectSourceAccountConfiguration(projectId, accountId, body, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectSourceAccountConfigurationWithHttpInfo(responseContext);
    }
    async updateProjectSourceAccountConfiguration(projectId, accountId, body, _options) {
        const responseWithInfo = await this.updateProjectSourceAccountConfigurationWithHttpInfo(projectId, accountId, body, _options);
        return responseWithInfo.data;
    }
}
import { UpdateProjectUserOverrideApiRequestFactory, UpdateProjectUserOverrideApiResponseProcessor } from "../apis/UpdateProjectUserOverrideApi.js";
export class PromiseUpdateProjectUserOverrideApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateProjectUserOverrideApiRequestFactory(configuration), responseProcessor = new UpdateProjectUserOverrideApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateProjectUserOverrideWithHttpInfo(projectId, id, updateUserInput, _options) {
        let requestContextValue = await this.requestFactory.updateProjectUserOverride(projectId, id, updateUserInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectUserOverrideWithHttpInfo(responseContext);
    }
    async updateProjectUserOverride(projectId, id, updateUserInput, _options) {
        const responseWithInfo = await this.updateProjectUserOverrideWithHttpInfo(projectId, id, updateUserInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateProjectsApiRequestFactory, UpdateProjectsApiResponseProcessor } from "../apis/UpdateProjectsApi.js";
export class PromiseUpdateProjectsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateProjectsApiRequestFactory(configuration), responseProcessor = new UpdateProjectsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateProjectWithHttpInfo(projectId, project, _options) {
        let requestContextValue = await this.requestFactory.updateProject(projectId, project, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateProjectWithHttpInfo(responseContext);
    }
    async updateProject(projectId, project, _options) {
        const responseWithInfo = await this.updateProjectWithHttpInfo(projectId, project, _options);
        return responseWithInfo.data;
    }
}
import { UpdatePulumiConfigurationApiRequestFactory, UpdatePulumiConfigurationApiResponseProcessor } from "../apis/UpdatePulumiConfigurationApi.js";
export class PromiseUpdatePulumiConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdatePulumiConfigurationApiRequestFactory(configuration), responseProcessor = new UpdatePulumiConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updatePulumiConfigurationWithHttpInfo(id, updatePulumiConfigurationRequest, _options) {
        let requestContextValue = await this.requestFactory.updatePulumiConfiguration(id, updatePulumiConfigurationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updatePulumiConfigurationWithHttpInfo(responseContext);
    }
    async updatePulumiConfiguration(id, updatePulumiConfigurationRequest, _options) {
        const responseWithInfo = await this.updatePulumiConfigurationWithHttpInfo(id, updatePulumiConfigurationRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateResourceConfigurationApiRequestFactory, UpdateResourceConfigurationApiResponseProcessor } from "../apis/UpdateResourceConfigurationApi.js";
export class PromiseUpdateResourceConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateResourceConfigurationApiRequestFactory(configuration), responseProcessor = new UpdateResourceConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateResourceConfigurationInternalWithHttpInfo(projectId, id, updateCloudResourceConfigurationRequest, _options) {
        let requestContextValue = await this.requestFactory.updateResourceConfigurationInternal(projectId, id, updateCloudResourceConfigurationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateResourceConfigurationInternalWithHttpInfo(responseContext);
    }
    async updateResourceConfigurationInternal(projectId, id, updateCloudResourceConfigurationRequest, _options) {
        const responseWithInfo = await this.updateResourceConfigurationInternalWithHttpInfo(projectId, id, updateCloudResourceConfigurationRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateRestoreAccountNameApiRequestFactory, UpdateRestoreAccountNameApiResponseProcessor } from "../apis/UpdateRestoreAccountNameApi.js";
export class PromiseUpdateRestoreAccountNameApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateRestoreAccountNameApiRequestFactory(configuration), responseProcessor = new UpdateRestoreAccountNameApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateRestoreAccountNameWithHttpInfo(projectId, accountId, name, _options) {
        let requestContextValue = await this.requestFactory.updateRestoreAccountName(projectId, accountId, name, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRestoreAccountNameWithHttpInfo(responseContext);
    }
    async updateRestoreAccountName(projectId, accountId, name, _options) {
        const responseWithInfo = await this.updateRestoreAccountNameWithHttpInfo(projectId, accountId, name, _options);
        return responseWithInfo.data;
    }
}
import { UpdateRestoreCloudAccountTagsConfigurationApiRequestFactory, UpdateRestoreCloudAccountTagsConfigurationApiResponseProcessor } from "../apis/UpdateRestoreCloudAccountTagsConfigurationApi.js";
export class PromiseUpdateRestoreCloudAccountTagsConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateRestoreCloudAccountTagsConfigurationApiRequestFactory(configuration), responseProcessor = new UpdateRestoreCloudAccountTagsConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateRestoreCloudAccountTagsConfigurationWithHttpInfo(projectId, accountId, customTagsInner, _options) {
        let requestContextValue = await this.requestFactory.updateRestoreCloudAccountTagsConfiguration(projectId, accountId, customTagsInner, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateRestoreCloudAccountTagsConfigurationWithHttpInfo(responseContext);
    }
    async updateRestoreCloudAccountTagsConfiguration(projectId, accountId, customTagsInner, _options) {
        const responseWithInfo = await this.updateRestoreCloudAccountTagsConfigurationWithHttpInfo(projectId, accountId, customTagsInner, _options);
        return responseWithInfo.data;
    }
}
import { UpdateSamlIdentityProviderConfigOverrideApiRequestFactory, UpdateSamlIdentityProviderConfigOverrideApiResponseProcessor } from "../apis/UpdateSamlIdentityProviderConfigOverrideApi.js";
export class PromiseUpdateSamlIdentityProviderConfigOverrideApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateSamlIdentityProviderConfigOverrideApiRequestFactory(configuration), responseProcessor = new UpdateSamlIdentityProviderConfigOverrideApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateSamlConfigOverrideWithHttpInfo(id, updateSamlIdentityProviderInput, _options) {
        let requestContextValue = await this.requestFactory.updateSamlConfigOverride(id, updateSamlIdentityProviderInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateSamlConfigOverrideWithHttpInfo(responseContext);
    }
    async updateSamlConfigOverride(id, updateSamlIdentityProviderInput, _options) {
        const responseWithInfo = await this.updateSamlConfigOverrideWithHttpInfo(id, updateSamlIdentityProviderInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateScanningAccountApiRequestFactory, UpdateScanningAccountApiResponseProcessor } from "../apis/UpdateScanningAccountApi.js";
export class PromiseUpdateScanningAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateScanningAccountApiRequestFactory(configuration), responseProcessor = new UpdateScanningAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateScanningAccountWithHttpInfo(accountId, accountUpdateInput, _options) {
        let requestContextValue = await this.requestFactory.updateScanningAccount(accountId, accountUpdateInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateScanningAccountWithHttpInfo(responseContext);
    }
    async updateScanningAccount(accountId, accountUpdateInput, _options) {
        const responseWithInfo = await this.updateScanningAccountWithHttpInfo(accountId, accountUpdateInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateSecurityScanPoliciesConfigApiRequestFactory, UpdateSecurityScanPoliciesConfigApiResponseProcessor } from "../apis/UpdateSecurityScanPoliciesConfigApi.js";
export class PromiseUpdateSecurityScanPoliciesConfigApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateSecurityScanPoliciesConfigApiRequestFactory(configuration), responseProcessor = new UpdateSecurityScanPoliciesConfigApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateSecurityScanPoliciesConfigWithHttpInfo(projectId, backupPoliciesSecurityConfig, _options) {
        let requestContextValue = await this.requestFactory.updateSecurityScanPoliciesConfig(projectId, backupPoliciesSecurityConfig, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateSecurityScanPoliciesConfigWithHttpInfo(responseContext);
    }
    async updateSecurityScanPoliciesConfig(projectId, backupPoliciesSecurityConfig, _options) {
        const responseWithInfo = await this.updateSecurityScanPoliciesConfigWithHttpInfo(projectId, backupPoliciesSecurityConfig, _options);
        return responseWithInfo.data;
    }
}
import { UpdateShouldFullBackupResourceApiRequestFactory, UpdateShouldFullBackupResourceApiResponseProcessor } from "../apis/UpdateShouldFullBackupResourceApi.js";
export class PromiseUpdateShouldFullBackupResourceApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateShouldFullBackupResourceApiRequestFactory(configuration), responseProcessor = new UpdateShouldFullBackupResourceApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateShouldFullBackupResourceWithHttpInfo(projectId, id, updateShouldFullBackupResourceRequest, _options) {
        let requestContextValue = await this.requestFactory.updateShouldFullBackupResource(projectId, id, updateShouldFullBackupResourceRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateShouldFullBackupResourceWithHttpInfo(responseContext);
    }
    async updateShouldFullBackupResource(projectId, id, updateShouldFullBackupResourceRequest, _options) {
        const responseWithInfo = await this.updateShouldFullBackupResourceWithHttpInfo(projectId, id, updateShouldFullBackupResourceRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateUserOverrideApiRequestFactory, UpdateUserOverrideApiResponseProcessor } from "../apis/UpdateUserOverrideApi.js";
export class PromiseUpdateUserOverrideApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateUserOverrideApiRequestFactory(configuration), responseProcessor = new UpdateUserOverrideApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateUserOverrideWithHttpInfo(id, updateUserInput, _options) {
        let requestContextValue = await this.requestFactory.updateUserOverride(id, updateUserInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateUserOverrideWithHttpInfo(responseContext);
    }
    async updateUserOverride(id, updateUserInput, _options) {
        const responseWithInfo = await this.updateUserOverrideWithHttpInfo(id, updateUserInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateVaultAccountApiRequestFactory, UpdateVaultAccountApiResponseProcessor } from "../apis/UpdateVaultAccountApi.js";
export class PromiseUpdateVaultAccountApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateVaultAccountApiRequestFactory(configuration), responseProcessor = new UpdateVaultAccountApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateVaultAccountWithHttpInfo(accountId, accountUpdateInput, _options) {
        let requestContextValue = await this.requestFactory.updateVaultAccount(accountId, accountUpdateInput, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateVaultAccountWithHttpInfo(responseContext);
    }
    async updateVaultAccount(accountId, accountUpdateInput, _options) {
        const responseWithInfo = await this.updateVaultAccountWithHttpInfo(accountId, accountUpdateInput, _options);
        return responseWithInfo.data;
    }
}
import { UpdateViewerRoleApiRequestFactory, UpdateViewerRoleApiResponseProcessor } from "../apis/UpdateViewerRoleApi.js";
export class PromiseUpdateViewerRoleApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateViewerRoleApiRequestFactory(configuration), responseProcessor = new UpdateViewerRoleApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateViewerRoleWithHttpInfo(updateViewerRoleRequest, _options) {
        let requestContextValue = await this.requestFactory.updateViewerRole(updateViewerRoleRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateViewerRoleWithHttpInfo(responseContext);
    }
    async updateViewerRole(updateViewerRoleRequest, _options) {
        const responseWithInfo = await this.updateViewerRoleWithHttpInfo(updateViewerRoleRequest, _options);
        return responseWithInfo.data;
    }
}
import { UpdateViewerUIPreferencesApiRequestFactory, UpdateViewerUIPreferencesApiResponseProcessor } from "../apis/UpdateViewerUIPreferencesApi.js";
export class PromiseUpdateViewerUIPreferencesApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new UpdateViewerUIPreferencesApiRequestFactory(configuration), responseProcessor = new UpdateViewerUIPreferencesApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async updateViewerUIPreferencesWithHttpInfo(key, requestBody, _options) {
        let requestContextValue = await this.requestFactory.updateViewerUIPreferences(key, requestBody, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateViewerUIPreferencesWithHttpInfo(responseContext);
    }
    async updateViewerUIPreferences(key, requestBody, _options) {
        const responseWithInfo = await this.updateViewerUIPreferencesWithHttpInfo(key, requestBody, _options);
        return responseWithInfo.data;
    }
}
import { ValidateResourceConfigurationApiRequestFactory, ValidateResourceConfigurationApiResponseProcessor } from "../apis/ValidateResourceConfigurationApi.js";
export class PromiseValidateResourceConfigurationApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new ValidateResourceConfigurationApiRequestFactory(configuration), responseProcessor = new ValidateResourceConfigurationApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async validateResourceConfigurationWithHttpInfo(projectId, validateResourceConfigurationRequest, _options) {
        let requestContextValue = await this.requestFactory.validateResourceConfiguration(projectId, validateResourceConfigurationRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.validateResourceConfigurationWithHttpInfo(responseContext);
    }
    async validateResourceConfiguration(projectId, validateResourceConfigurationRequest, _options) {
        const responseWithInfo = await this.validateResourceConfigurationWithHttpInfo(projectId, validateResourceConfigurationRequest, _options);
        return responseWithInfo.data;
    }
}
import { VaultsApiRequestFactory, VaultsApiResponseProcessor } from "../apis/VaultsApi.js";
export class PromiseVaultsApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new VaultsApiRequestFactory(configuration), responseProcessor = new VaultsApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async createVaultWithHttpInfo(projectId, createVaultRequest, _options) {
        let requestContextValue = await this.requestFactory.createVault(projectId, createVaultRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.createVaultWithHttpInfo(responseContext);
    }
    async createVault(projectId, createVaultRequest, _options) {
        const responseWithInfo = await this.createVaultWithHttpInfo(projectId, createVaultRequest, _options);
        return responseWithInfo.data;
    }
    async deleteVaultWithHttpInfo(projectId, vaultId, _options) {
        let requestContextValue = await this.requestFactory.deleteVault(projectId, vaultId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.deleteVaultWithHttpInfo(responseContext);
    }
    async deleteVault(projectId, vaultId, _options) {
        const responseWithInfo = await this.deleteVaultWithHttpInfo(projectId, vaultId, _options);
        return responseWithInfo.data;
    }
    async getVaultWithHttpInfo(projectId, vaultId, _options) {
        let requestContextValue = await this.requestFactory.getVault(projectId, vaultId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.getVaultWithHttpInfo(responseContext);
    }
    async getVault(projectId, vaultId, _options) {
        const responseWithInfo = await this.getVaultWithHttpInfo(projectId, vaultId, _options);
        return responseWithInfo.data;
    }
    async listAzureCmkEligibleAppsWithHttpInfo(projectId, vaultAccountId, customerTenantId, _options) {
        let requestContextValue = await this.requestFactory.listAzureCmkEligibleApps(projectId, vaultAccountId, customerTenantId, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listAzureCmkEligibleAppsWithHttpInfo(responseContext);
    }
    async listAzureCmkEligibleApps(projectId, vaultAccountId, customerTenantId, _options) {
        const responseWithInfo = await this.listAzureCmkEligibleAppsWithHttpInfo(projectId, vaultAccountId, customerTenantId, _options);
        return responseWithInfo.data;
    }
    async listVaultsWithHttpInfo(projectId, pageToken, pageSize, _options) {
        let requestContextValue = await this.requestFactory.listVaults(projectId, pageToken, pageSize, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.listVaultsWithHttpInfo(responseContext);
    }
    async listVaults(projectId, pageToken, pageSize, _options) {
        const responseWithInfo = await this.listVaultsWithHttpInfo(projectId, pageToken, pageSize, _options);
        return responseWithInfo.data;
    }
    async updateVaultWithHttpInfo(projectId, vaultId, updateVaultRequest, _options) {
        let requestContextValue = await this.requestFactory.updateVault(projectId, vaultId, updateVaultRequest, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.updateVaultWithHttpInfo(responseContext);
    }
    async updateVault(projectId, vaultId, updateVaultRequest, _options) {
        const responseWithInfo = await this.updateVaultWithHttpInfo(projectId, vaultId, updateVaultRequest, _options);
        return responseWithInfo.data;
    }
}
import { VerifyMongoAtlasPrivateLinkApiRequestFactory, VerifyMongoAtlasPrivateLinkApiResponseProcessor } from "../apis/VerifyMongoAtlasPrivateLinkApi.js";
export class PromiseVerifyMongoAtlasPrivateLinkApi {
    configuration;
    requestFactory;
    responseProcessor;
    constructor(configuration, requestFactory = new VerifyMongoAtlasPrivateLinkApiRequestFactory(configuration), responseProcessor = new VerifyMongoAtlasPrivateLinkApiResponseProcessor()) {
        this.configuration = configuration;
        this.requestFactory = requestFactory;
        this.responseProcessor = responseProcessor;
    }
    async verifyMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options) {
        let requestContextValue = await this.requestFactory.verifyMongoAtlasPrivateLink(projectId, accountId, region, _options);
        const responseContext = await requestWithRetries(requestContextValue, this.configuration);
        return await this.responseProcessor.verifyMongoAtlasPrivateLinkWithHttpInfo(responseContext);
    }
    async verifyMongoAtlasPrivateLink(projectId, accountId, region, _options) {
        const responseWithInfo = await this.verifyMongoAtlasPrivateLinkWithHttpInfo(projectId, accountId, region, _options);
        return responseWithInfo.data;
    }
}
